export type DrillCategory = 
  | "patterns" 
  | "brilliant-basics" 
  | "positioning-games" 
  | "game-training";

export type DrillTopic = 
  | "building" 
  | "mid-play" 
  | "creating-finishing" 
  | "bpo-1" 
  | "bpo-3+1"
  | "transitions";

export interface Drill {
  id: string;
  title: string;
  description: string;
  category: DrillCategory;
  topics: DrillTopic[];
  duration: string;
  players: string;
  areaSize: string;
  rules: string[];
  coachingPoints: string[];
  diagram: DiagramData;
}

export interface DiagramData {
  type: "full-pitch" | "full-pitch-no-bottom-box" | "full-pitch-top-only" | "full-pitch-bottom-only" | "full-pitch-wide-goals" | "full-pitch-empty" | "half-pitch" | "half-pitch-minimal" | "goal-square" | "small-grid" | "custom";
  attackers: PlayerPosition[];
  defenders: PlayerPosition[];
  neutrals?: PlayerPosition[];
  mannequins?: PlayerPosition[];
  cones?: ConePosition[];
  zones?: ZoneData[];
  arrows?: ArrowData[];
  goals?: GoalPosition[];
  ball?: { x: number; y: number };
  balls?: { x: number; y: number }[];
  lines?: LineData[];
}

export interface LineData {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  style: "solid" | "dotted" | "dashed";
  color?: string;
}

export interface PlayerPosition {
  x: number;
  y: number;
  label?: string;
  color?: string;
}

export interface ConePosition {
  x: number;
  y: number;
  color?: string;
}

export interface ZoneData {
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string;
}

export interface ArrowData {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  type: "pass" | "movement" | "dribble";
  curved?: boolean;
  curveDirection?: number;
}

export interface GoalPosition {
  x: number;
  y: number;
  width: number;
  rotation?: number;
}

export const drillCategories: { id: DrillCategory; label: string; color: string }[] = [
  { id: "patterns", label: "Patterns", color: "bg-primary" },
  { id: "brilliant-basics", label: "Brilliant Basics", color: "bg-player-defend" },
  { id: "positioning-games", label: "Positioning Games", color: "bg-emerald-500" },
  { id: "game-training", label: "Game Training", color: "bg-amber-600" },
];

export const drillTopics: { id: DrillTopic; label: string }[] = [
  { id: "building", label: "Building" },
  { id: "mid-play", label: "Mid Play" },
  { id: "creating-finishing", label: "Creating & Finishing" },
  { id: "bpo-1", label: "BPO 1" },
  { id: "bpo-3+1", label: "BPO 3+1" },
  { id: "transitions", label: "Transitions" },
];

export const drills: Drill[] = [
  {
    id: "mid-play-boxes",
    title: "Mid Play Boxes",
    description: "2v2+1 in first half, 2nd half is 1v1. If player drops into attacking half to receive, other midfielder must stay high. Player receiving in attacking half is one touch. Must be one-touch finish to score in attacking half.",
    category: "positioning-games",
    topics: ["mid-play"],
    duration: "4x2mins",
    players: "8",
    areaSize: "14x22m",
    rules: [
      "2v2+1 in first half, 2nd half is 2v1",
      "If player drops into attacking half to receive, other midfielder must stay high",
      "Player receiving in the attacking half is one touch",
      "Must be a one-touch finish to score in attacking half",
      "If BPO team win the ball, they must score in the goals either side of the bouncer in green",
      "If they score, they are now the BP team"
    ],
    coachingPoints: [
      "Double Movements to Lose Defender",
      "Identifying Space/Scan",
      "Deception/Disguise - Body Feint, Reverse Pass",
      "Receive level or beyond your marker",
      "Touch - Front foot / Back foot",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Left zone rectangle (2v2+1)
        { x: 18, y: 18, width: 28, height: 64, color: "rgba(255,255,255,0.05)" },
        // Right zone rectangle (2v1)
        { x: 54, y: 18, width: 28, height: 64, color: "rgba(255,255,255,0.05)" },
      ],
      // Blue players - 2 in each zone
      attackers: [
        // Left zone - top blue
        { x: 28, y: 28 },
        // Left zone - bottom blue
        { x: 28, y: 72 },
        // Right zone - top blue
        { x: 62, y: 32 },
        // Right zone - bottom blue
        { x: 62, y: 68 },
      ],
      // Red players - 2 in left zone, 1 in right zone
      defenders: [
        // Left zone - top red
        { x: 38, y: 32 },
        // Left zone - bottom red
        { x: 38, y: 68 },
        // Right zone - center red
        { x: 75, y: 50 },
      ],
      // Pink bouncer between mini goals on far left
      neutrals: [
        { x: 8, y: 50 },
      ],
      ball: { x: 12, y: 50 },
      arrows: [],
      // Mini goals
      goals: [
        // Left side - two mini goals flanking the bouncer
        { x: 3, y: 32, width: 8, rotation: 90 },
        { x: 3, y: 68, width: 8, rotation: 90 },
        // Right side - two mini goals
        { x: 97, y: 32, width: 8, rotation: -90 },
        { x: 97, y: 68, width: 8, rotation: -90 },
      ],
      cones: [],
    }
  },
  {
    id: "1v1-defending",
    title: "1v1 Defending",
    description: "1v1 Game with rotation based on achievement. Red begins with the ball and passes into blue.",
    category: "brilliant-basics",
    topics: ["bpo-1", "bpo-3+1"],
    duration: "3x3mins",
    players: "8",
    areaSize: "12x15m",
    rules: [
      "1v1 Game - Red begins with the ball and passes into blue",
      "Blues aim is to win 1v1 attacking duel and score in the opposite goal",
      "Reds aim is to pass and then press to win the 1v1 defending duel and score if they win possession",
      "Players rotate depending on achievement",
      "Two highest scoring players move up the court, two lowest move down",
      "Players who finish on top court are winners, lowest two courts have punishment",
      "Goal = 1 Point"
    ],
    coachingPoints: [
      "1v1: Angle of Approach",
      "1v1: Side On - Force Wide",
      "1v1: Timing of Tackle",
      "2v2: Press and Cover"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // 4 vertical grids side by side
        { x: 8, y: 15, width: 18, height: 70, color: "rgba(255,255,255,0.05)" },
        { x: 30, y: 15, width: 18, height: 70, color: "rgba(255,255,255,0.05)" },
        { x: 52, y: 15, width: 18, height: 70, color: "rgba(255,255,255,0.05)" },
        { x: 74, y: 15, width: 18, height: 70, color: "rgba(255,255,255,0.05)" },
      ],
      // Blue players (attackers) - one per grid at top
      attackers: [
        { x: 17, y: 35 },
        { x: 39, y: 35 },
        { x: 61, y: 35 },
        { x: 83, y: 35 },
      ],
      // Red players (defenders) - one per grid at bottom
      defenders: [
        { x: 17, y: 65 },
        { x: 39, y: 65 },
        { x: 61, y: 65 },
        { x: 83, y: 65 },
      ],
      neutrals: [],
      // Balls in front of each blue player
      balls: [
        { x: 17, y: 40 },
        { x: 39, y: 40 },
        { x: 61, y: 40 },
        { x: 83, y: 40 },
      ],
      arrows: [],
      // Mini goals at top and bottom of each grid
      goals: [
        // Grid 1 - top and bottom
        { x: 17, y: 10, width: 10, rotation: 0 },
        { x: 17, y: 90, width: 10, rotation: 180 },
        // Grid 2
        { x: 39, y: 10, width: 10, rotation: 0 },
        { x: 39, y: 90, width: 10, rotation: 180 },
        // Grid 3
        { x: 61, y: 10, width: 10, rotation: 0 },
        { x: 61, y: 90, width: 10, rotation: 180 },
        // Grid 4
        { x: 83, y: 10, width: 10, rotation: 0 },
        { x: 83, y: 90, width: 10, rotation: 180 },
      ],
      cones: [],
    }
  },
  {
    id: "pattern-play-6v5",
    title: "Pattern Play 6v5",
    description: "Semi-opposed passing pattern in a 6v5. Any patterns that get forwards on the ball quickly for more defending reps.",
    category: "patterns",
    topics: ["bpo-3+1"],
    duration: "4x3mins",
    players: "13",
    areaSize: "50x50m",
    rules: [
      "Semi-opposed passing pattern in a 6v5",
      "Any patterns that get forwards on the ball quickly for more defending reps",
      "As soon as red break the line the game becomes opposed",
      "Red score in the big goal",
      "Blue x5 passes or FBO to outside player",
      "Players rotate with 5 every 2 reps"
    ],
    coachingPoints: [
      "Force the opposition one way",
      "Pressing cues",
      "If line is broken, positioning of furthest defender to be 2nd defender",
      "3+1 defending in the box"
    ],
    diagram: {
      type: "full-pitch-top-only",
      zones: [
        // Main playing area
        { x: 15, y: 10, width: 70, height: 80, color: "rgba(255,255,255,0.05)" },
      ],
      // Blue players (attackers)
      attackers: [
        { x: 50, y: 8, label: "1" },      // GK at top
        { x: 28, y: 45, label: "2" },      // Left back
        { x: 45, y: 45, label: "3" },     // Left center
        { x: 68, y: 45, label: "4" },     // Right center
        { x: 92, y: 55, label: "5" },     // Wide right
        { x: 30, y: 70, label: "6" },     // Left defender (was red)
        { x: 60, y: 70, label: "8" },     // Right defender (was red)
      ],
      // Red players (defenders)
      defenders: [
        { x: 18, y: 45, label: "11" },    // Left wing
        { x: 50, y: 45, label: "9" },     // Center
        { x: 82, y: 45, label: "7" },     // Right wing
        { x: 45, y: 70, label: "10" },    // Center defender
        { x: 25, y: 90, label: "6" },     // Bottom left
        { x: 75, y: 85, label: "8" },     // Bottom right
      ],
      neutrals: [],
      ball: { x: 25, y: 85 },
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 20, rotation: 0 },
      ],
      cones: [],
      lines: [],
    }
  },
  {
    id: "bpo-3-1-game-training",
    title: "BPO 3+1 Game Training",
    description: "Attack vs Defence. 6v5+1. Start play with direct ball into CBs to clear and play live. In Possession: 2-1-3, Out of Possession: 3-2-1.",
    category: "game-training",
    topics: ["bpo-3+1"],
    duration: "2x10mins",
    players: "12",
    areaSize: "50x50m",
    rules: [
      "Attack vs Defence - 6v5+1",
      "Start play with direct ball into CBs to clear and play live",
      "In Possession: 2-1-3",
      "Out of Possession: 3-2-1",
      "Red score in big goal",
      "Blue score in minis"
    ],
    coachingPoints: [
      "Force the opposition one way",
      "Pressing cues",
      "If line is broken, positioning of furthest defender to be 2nd defender",
      "3+1 defending in the box"
    ],
    diagram: {
      type: "full-pitch-top-only",
      zones: [],
      // Blue players (attackers)
      attackers: [
        { x: 50, y: 8, label: "1" },      // GK at top
        { x: 30, y: 38, label: "3" },     // Left center - more left
        { x: 50, y: 33, label: "4" },     // In front of the 9, touching
        { x: 75, y: 38, label: "5" },     // Right
        { x: 35, y: 58, label: "6" },     // Left mid (now blue)
        { x: 65, y: 58, label: "8" },     // Right mid (now blue)
      ],
      // Red players (defenders)
      defenders: [
        { x: 18, y: 38, label: "11" },    // Left wing
        { x: 50, y: 38, label: "9" },     // Center
        { x: 92, y: 38, label: "7" },     // Wide right (now red)
        { x: 50, y: 58, label: "10" },    // Center defender
        { x: 28, y: 82, label: "6" },     // Bottom left
        { x: 72, y: 82, label: "8" },     // Bottom right
      ],
      neutrals: [],
      ball: { x: 28, y: 77 },
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 20, rotation: 0 },
        { x: 8, y: 95, width: 10, rotation: 180 },
        { x: 92, y: 95, width: 10, rotation: 180 },
      ],
      cones: [],
    }
  },
  {
    id: "brilliant-basics-2v2-turn",
    title: "2v2 Turn & Play",
    description: "Set up with 2 players as Targets at each end and 4 players in the middle creating a 2v2. Target players pass to their teammate in the middle who must turn and play to the opposite side.",
    category: "brilliant-basics",
    topics: ["bpo-1", "bpo-3+1"],
    duration: "4x2mins",
    players: "8",
    areaSize: "20x15m",
    rules: [
      "Set up with 2 players as Targets at each end and 4 players in the middle creating a 2v2",
      "The Target players pass to their teammate in the middle who must turn and look to play to the opposite side",
      "Defenders in the middle looking to prevent them from turning",
      "If successful, the Attackers then become the Defenders, and vice versa for the other players"
    ],
    coachingPoints: [
      "Connection + Press + Cover",
      "Block and Press on Triggers",
      "Defend your lane",
      "Force one way"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 10, y: 15, width: 80, height: 70, color: "rgba(255,255,255,0.05)" },
      ],
      // Blue target players on left and right sides
      attackers: [
        { x: 12, y: 30 },     // Left top target
        { x: 12, y: 70 },     // Left bottom target
        { x: 88, y: 30 },     // Right top target
        { x: 88, y: 70 },     // Right bottom target
      ],
      // Red defenders in the middle
      defenders: [
        { x: 55, y: 45 },     // Center left defender
        { x: 65, y: 60 },     // Center right defender
      ],
      // Yellow/neutral players in the middle (attackers)
      neutrals: [
        { x: 45, y: 55, color: "#F59E0B" },     // Yellow attacker left
        { x: 75, y: 45, color: "#F59E0B" },     // Yellow attacker right
      ],
      balls: [
        { x: 82, y: 30 },     // Left of top right blue
        { x: 39, y: 55 },     // Left of left orange
      ],
      arrows: [],
      goals: [],
      cones: [],
    }
  },
  {
    id: "bpo-3-1-positioning-game",
    title: "BPO 3+1 Positioning Game",
    description: "4v1 in end zones with 3 screening + 1 atk. Play KP through central zone below knee height.",
    category: "positioning-games",
    topics: ["bpo-3+1"],
    duration: "3x4mins",
    players: "12-13",
    areaSize: "40x25m",
    rules: [
      "4v1 in end zones with 3 screening + 1 atk",
      "Play KP through central zone below knee height",
      "If BPO team win the ball, either score in goal or complete 3 passes (Progress or Posses)",
      "BPO team stay for duration of set",
      "Touch Restriction/2 pressers",
      "All can recover if ball is played through to win the ball"
    ],
    coachingPoints: [
      "Connection + Press + Cover",
      "Block and Press on Triggers",
      "Line Broken = Positioning of furthest defender to be 2nd Defender",
      "Pressing cues",
      "3+1 emergency defending",
      "Defend your lane"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Central yellow zone
        { x: 10, y: 40, width: 80, height: 30, color: "rgba(245, 208, 140, 0.5)" },
      ],
      // Blue players (top zone) and green players (bottom zone)
      attackers: [
        // Top zone - 4 blue players
        { x: 25, y: 22 },
        { x: 55, y: 28 },
        { x: 75, y: 22 },
        { x: 45, y: 35 },
        // Bottom zone - 4 green players
        { x: 25, y: 78, color: "#22C55E" },
        { x: 55, y: 72, color: "#22C55E" },
        { x: 75, y: 78, color: "#22C55E" },
        { x: 45, y: 85, color: "#22C55E" },
      ],
      // Red players in central zone
      defenders: [
        { x: 35, y: 50 },
        { x: 55, y: 48 },
        { x: 70, y: 50 },
        { x: 50, y: 60 },
      ],
      // One orange player in central zone
      neutrals: [
        { x: 45, y: 52, color: "#F59E0B" },
      ],
      balls: [
        { x: 45, y: 80 },
      ],
      arrows: [],
      goals: [
        // Top - more central
        { x: 35, y: 8, width: 8, rotation: 0 },
        { x: 65, y: 8, width: 8, rotation: 0 },
        // Bottom - more central
        { x: 35, y: 92, width: 8, rotation: 180 },
        { x: 65, y: 92, width: 8, rotation: 180 },
      ],
      cones: [],
    }
  },
  {
    id: "jump-on-square-pass",
    title: "Jump on Square Pass",
    description: "Defending wall pass pattern. Must jump on square pass and block def run after they release.",
    category: "brilliant-basics",
    topics: ["bpo-1", "bpo-3+1"],
    duration: "3x3mins",
    players: "4",
    areaSize: "10x20m",
    rules: [
      "Defending wall pass pattern",
      "Must jump on square pass and block def run after they release",
      "Reds aim to pass and drive past the bottom mannequins"
    ],
    coachingPoints: [
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "Def your lane",
      "Set up to reduce time + space",
      "Reduce distance whilst ball is moving"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 20, y: 10, width: 60, height: 80, color: "rgba(255,255,255,0.05)" },
      ],
      attackers: [
        { x: 32, y: 65, label: "4" },
        { x: 68, y: 65, label: "5" },
      ],
      defenders: [
        { x: 32, y: 45, label: "9" },
        { x: 68, y: 45, label: "10" },
      ],
      neutrals: [],
      mannequins: [
        { x: 40, y: 45 },
        { x: 40, y: 65 },
        { x: 60, y: 45 },
        { x: 60, y: 65 },
      ],
      ball: { x: 73, y: 45 },
      arrows: [],
      goals: [],
      cones: [],
    }
  },
  {
    id: "force-outside",
    title: "Force Outside",
    description: "6v6 in zones. Can score from any zone. Progression: no zones.",
    category: "positioning-games",
    topics: ["bpo-1"],
    duration: "2x10mins",
    players: "14",
    areaSize: "40x40m",
    rules: [
      "6v6 in zone",
      "Blues look to win the ball and progress into space behind"
    ],
    coachingPoints: [
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "Def your lane",
      "Set up to reduce time + space",
      "Reduce distance whilst ball is moving"
    ],
    diagram: {
      type: "full-pitch",
      zones: [
        { x: 25, y: 25, width: 50, height: 50, color: "rgba(0,0,0,0.3)" },
      ],
      attackers: [
        { x: 50, y: 22, label: "1" },
        { x: 40, y: 30, label: "3" },
        { x: 65, y: 30, label: "4" },
        { x: 40, y: 45, label: "6" },
        { x: 60, y: 45, label: "8" },
        { x: 55, y: 52, label: "10" },
        { x: 50, y: 70, label: "9" },
      ],
      defenders: [
        { x: 58, y: 30, label: "9" },
        { x: 45, y: 38, label: "10" },
        { x: 68, y: 45, label: "8" },
        { x: 50, y: 60, label: "6" },
        { x: 35, y: 70, label: "4" },
        { x: 65, y: 70, label: "3" },
        { x: 50, y: 92, label: "1" },
      ],
      neutrals: [],
      ball: { x: 50, y: 87 },
      arrows: [],
      goals: [
        { x: 50, y: 18, width: 20, rotation: 0 },
      ],
      cones: [],
      lines: [
        { startX: 20, startY: 75, endX: 20, endY: 100, style: "dashed", color: "#E91E9E" },
        { startX: 80, startY: 75, endX: 80, endY: 100, style: "dashed", color: "#E91E9E" },
        { startX: 20, startY: 75, endX: 35, endY: 75, style: "dashed", color: "#E91E9E" },
        { startX: 65, startY: 75, endX: 80, endY: 75, style: "dashed", color: "#E91E9E" },
        { startX: 20, startY: 0, endX: 20, endY: 18, style: "dashed", color: "#E91E9E" },
        { startX: 80, startY: 0, endX: 80, endY: 18, style: "dashed", color: "#E91E9E" },
        { startX: 20, startY: 18, endX: 35, endY: 18, style: "dashed", color: "#E91E9E" },
        { startX: 65, startY: 18, endX: 80, endY: 18, style: "dashed", color: "#E91E9E" },
      ],
    }
  },
  {
    id: "individual-press",
    title: "Individual Press",
    description: "Individual press drill. Player in the middle blocks + intercepts. When ball is won, drive past a player on the line.",
    category: "brilliant-basics",
    topics: ["bpo-1", "bpo-3+1"],
    duration: "12x1min",
    players: "6",
    areaSize: "10x20m",
    rules: [
      "Individual Press",
      "Player in the middle blocks + intercepts",
      "When ball is won, drive past a player on the line",
      "Promotion/relegation every 3 rounds"
    ],
    coachingPoints: [
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "Def your lane",
      "Set up to reduce time + space",
      "Reduce distance whilst ball is moving"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 10, y: 10, width: 35, height: 40, color: "rgba(255,255,255,0.05)" },
        { x: 55, y: 10, width: 35, height: 40, color: "rgba(255,255,255,0.05)" },
        { x: 10, y: 50, width: 35, height: 40, color: "rgba(255,255,255,0.05)" },
        { x: 55, y: 50, width: 35, height: 40, color: "rgba(255,255,255,0.05)" },
      ],
      attackers: [
        { x: 35, y: 40, label: "10" },
        { x: 70, y: 60, label: "9" },
      ],
      defenders: [
        { x: 20, y: 20, label: "7" },
        { x: 85, y: 20, label: "6" },
        { x: 25, y: 75, label: "3" },
        { x: 85, y: 75, label: "11" },
      ],
      neutrals: [],
      balls: [
        { x: 20, y: 27 },
        { x: 85, y: 27 },
      ],
      arrows: [],
      goals: [],
      cones: [],
    }
  },
  {
    id: "block-forward-pass",
    title: "Block Forward Pass",
    description: "Ball starts with the GK and the blues need to get the ball to the 9. Game is then reversed and played back through the other side to get the ball to the 1.",
    category: "positioning-games",
    topics: ["bpo-1"],
    duration: "3x4mins",
    players: "14",
    areaSize: "40x30m",
    rules: [
      "Ball starts with the GK and the blues need to get the ball to the 9",
      "Game is then reversed and played back through the other side to get the ball to the 1",
      "3v3 in each half of the pitch",
      "If the defending team wins the ball, they try to score a goal as quickly as possible"
    ],
    coachingPoints: [
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "Def your lane",
      "Set up to reduce time + space",
      "Reduce distance whilst ball is moving"
    ],
    diagram: {
      type: "full-pitch",
      zones: [
        { x: 15, y: 50, width: 35, height: 35, color: "rgba(255,0,0,0.15)" },
        { x: 50, y: 50, width: 35, height: 35, color: "rgba(255,0,0,0.15)" },
      ],
      attackers: [
        { x: 25, y: 58, label: "" },
        { x: 42, y: 68, label: "" },
        { x: 25, y: 78, label: "" },
        { x: 58, y: 58, label: "" },
        { x: 75, y: 68, label: "" },
        { x: 65, y: 78, label: "" },
      ],
      defenders: [
        { x: 35, y: 52, label: "" },
        { x: 24, y: 70, label: "" },
        { x: 35, y: 80, label: "" },
        { x: 70, y: 55, label: "" },
        { x: 68, y: 70, label: "" },
        { x: 80, y: 82, label: "" },
      ],
      neutrals: [
        { x: 50, y: 45, label: "9", color: "#F59E0B" },
        { x: 50, y: 92, label: "1", color: "#F59E0B" },
      ],
      ball: { x: 50, y: 88 },
      arrows: [
        { startX: 50, startY: 90, endX: 60, endY: 70, type: "pass" },
      ],
      goals: [
        { x: 50, y: 40, width: 16, rotation: 0 },
        { x: 50, y: 98, width: 16, rotation: 180 },
      ],
      cones: [],
      lines: [
        { startX: 15, startY: 50, endX: 50, endY: 50, style: "dashed", color: "#DC2626" },
        { startX: 15, startY: 85, endX: 50, endY: 85, style: "dashed", color: "#DC2626" },
        { startX: 15, startY: 50, endX: 15, endY: 85, style: "dashed", color: "#DC2626" },
        { startX: 50, startY: 50, endX: 50, endY: 85, style: "dashed", color: "#DC2626" },
        { startX: 50, startY: 50, endX: 85, endY: 50, style: "dashed", color: "#DC2626" },
        { startX: 50, startY: 85, endX: 85, endY: 85, style: "dashed", color: "#DC2626" },
        { startX: 85, startY: 50, endX: 85, endY: 85, style: "dashed", color: "#DC2626" },
      ],
    }
  },
  {
    id: "press-to-win",
    title: "Press to Win",
    description: "Ball starts with the GK and the blues need to get the ball to the 9. Game is then reversed and played back through the other side to get the ball to the 1.",
    category: "game-training",
    topics: ["bpo-1"],
    duration: "2x10mins",
    players: "14",
    areaSize: "40x30m",
    rules: [
      "6v6+GKs",
      "Game is multi-directional",
      "Resets at GKs, reward scoring side",
      "If the defending team wins the ball, they try to score a goal as quickly as possible"
    ],
    coachingPoints: [
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "Def your lane",
      "Set up to reduce time + space",
      "Reduce distance whilst ball is moving"
    ],
    diagram: {
      type: "full-pitch",
      zones: [
        { x: 15, y: 50, width: 70, height: 48, color: "rgba(255,0,0,0.15)" },
      ],
      attackers: [
        { x: 25, y: 58, label: "" },
        { x: 42, y: 68, label: "" },
        { x: 40, y: 78, label: "" },
        { x: 58, y: 58, label: "" },
        { x: 75, y: 68, label: "" },
        { x: 65, y: 78, label: "" },
      ],
      defenders: [
        { x: 35, y: 52, label: "" },
        { x: 38, y: 70, label: "" },
        { x: 28, y: 90, label: "" },
        { x: 70, y: 55, label: "" },
        { x: 62, y: 70, label: "" },
        { x: 72, y: 90, label: "" },
      ],
      neutrals: [
        { x: 50, y: 45, label: "1", color: "#F59E0B" },
        { x: 50, y: 92, label: "1", color: "#F59E0B" },
      ],
      ball: { x: 50, y: 88 },
      arrows: [],
      goals: [
        { x: 50, y: 40, width: 16, rotation: 0 },
        { x: 50, y: 98, width: 16, rotation: 180 },
      ],
      cones: [],
      lines: [
        { startX: 15, startY: 50, endX: 85, endY: 50, style: "dashed", color: "#DC2626" },
        { startX: 15, startY: 50, endX: 15, endY: 98, style: "dashed", color: "#DC2626" },
        { startX: 85, startY: 50, endX: 85, endY: 98, style: "dashed", color: "#DC2626" },
      ],
    }
  },
  {
    id: "jump-on-inside-link",
    title: "Jump on Inside Link",
    description: "7v4 with 2 defenders in 'jump zone' and one player waiting in opposite grid. Objective is for the BP team to dribble through the 'jump zone' and play a pass into the end player or score.",
    category: "positioning-games",
    topics: ["bpo-1"],
    duration: "4x3mins",
    players: "14",
    areaSize: "24x52m",
    rules: [
      "7v4 with 2 defenders in 'jump zone' and one player waiting in opposite grid",
      "Objective is for the BP team to dribble through the 'jump zone' and play a pass into the end player or score",
      "FB's slide up and down each wing",
      "FB's can drive through the jump zone",
      "This should force the BPO team to keep at least one or even two players in this zone"
    ],
    coachingPoints: [
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "Def your lane",
      "Set up to reduce time + space",
      "Reduce distance whilst ball is moving"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 10, y: 15, width: 32, height: 70, color: "rgba(0,0,100,0.15)" },
        { x: 42, y: 15, width: 16, height: 70, color: "rgba(255,0,255,0.25)" },
        { x: 58, y: 15, width: 32, height: 70, color: "rgba(0,0,100,0.15)" },
      ],
      attackers: [
        { x: 36, y: 30, label: "" },
        { x: 26, y: 45, label: "" },
        { x: 26, y: 55, label: "" },
        { x: 36, y: 70, label: "" },
        { x: 50, y: 42, label: "" },
        { x: 50, y: 58, label: "" },
        { x: 94, y: 50, label: "GK" },
      ],
      defenders: [],
      neutrals: [
        { x: 6, y: 50, label: "GK", color: "#F59E0B" },
        { x: 5, y: 30, label: "", color: "#F59E0B" },
        { x: 5, y: 70, label: "", color: "#F59E0B" },
        { x: 26, y: 15, label: "", color: "#F59E0B" },
        { x: 26, y: 85, label: "", color: "#F59E0B" },
        { x: 32, y: 50, label: "", color: "#F59E0B" },
        { x: 50, y: 50, label: "", color: "#F59E0B" },
      ],
      ball: { x: 12, y: 50 },
      arrows: [],
      goals: [
        { x: 3, y: 50, width: 14, rotation: 90 },
        { x: 97, y: 50, width: 14, rotation: -90 },
      ],
      cones: [],
      lines: [
        { startX: 10, startY: 15, endX: 42, endY: 15, style: "dashed", color: "#1E3A8A" },
        { startX: 10, startY: 85, endX: 42, endY: 85, style: "dashed", color: "#1E3A8A" },
        { startX: 10, startY: 15, endX: 10, endY: 85, style: "dashed", color: "#1E3A8A" },
        { startX: 42, startY: 15, endX: 42, endY: 85, style: "dashed", color: "#E91E9E" },
        { startX: 58, startY: 15, endX: 58, endY: 85, style: "dashed", color: "#E91E9E" },
        { startX: 58, startY: 15, endX: 90, endY: 15, style: "dashed", color: "#1E3A8A" },
        { startX: 58, startY: 85, endX: 90, endY: 85, style: "dashed", color: "#1E3A8A" },
        { startX: 90, startY: 15, endX: 90, endY: 85, style: "dashed", color: "#1E3A8A" },
      ],
    }
  },
  {
    id: "jump-on-inside-link-gt",
    title: "Jump on Inside Link GT",
    description: "GT session. Cut the wide areas out so teams have to play out centrally. BP team try to score in goals on halfway. BPO team win the ball and either score within 5 passes or keep the ball for 10 passes.",
    category: "game-training",
    topics: ["bpo-1"],
    duration: "2x10mins",
    players: "14",
    areaSize: "Half pitch tapered",
    rules: [
      "GT session. Cut the wide areas out so teams have to play out centrally",
      "BP team try to score in goals on halfway",
      "BPO team win the ball and either score within 5 passes or keep the ball for 10 passes (Possess or Progress)"
    ],
    coachingPoints: [
      "1v1 defending",
      "Def your lane",
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "Adjust as ball is moving",
      "Ball side Goal side"
    ],
    diagram: {
      type: "full-pitch",
      zones: [],
      attackers: [
        { x: 50, y: 4, label: "GK" },
        { x: 30, y: 7, label: "" },
        { x: 70, y: 7, label: "" },
        { x: 10, y: 25, label: "" },
        { x: 50, y: 20, label: "" },
        { x: 90, y: 25, label: "" },
        { x: 35, y: 32, label: "" },
        { x: 65, y: 32, label: "" },
      ],
      defenders: [
        { x: 35, y: 17, label: "" },
        { x: 65, y: 17, label: "" },
        { x: 25, y: 25, label: "" },
        { x: 45, y: 32, label: "" },
        { x: 55, y: 32, label: "" },
        { x: 75, y: 25, label: "" },
      ],
      neutrals: [],
      ball: { x: 50, y: 6 },
      arrows: [],
      goals: [
        { x: 50, y: 1, width: 16, rotation: 0 },
        { x: 35, y: 49, width: 8, rotation: 180 },
        { x: 65, y: 49, width: 8, rotation: 180 },
      ],
      cones: [],
      lines: [
        { startX: 5, startY: 15, endX: 25, endY: 50, style: "dashed", color: "#1E3A8A" },
        { startX: 95, startY: 15, endX: 75, endY: 50, style: "dashed", color: "#1E3A8A" },
      ],
    }
  },
  {
    id: "bpo-3-1-game-training-7v8",
    title: "BPO 3+1 Game Training 6v6+1",
    description: "Attack vs Defence. 6v6+1. In Possession: 2-4, Out of Possession: 1-3-2-1.",
    category: "game-training",
    topics: ["bpo-3+1"],
    duration: "3x8min",
    players: "13",
    areaSize: "Atk half - 4m in each side",
    rules: [
      "Attack vs Defence. 6v6+1",
      "In Possession: 2-4",
      "Out of Possession: 1-3-2-1",
      "Restarts at BP 6/3",
      "BP big goal",
      "BPO hitting minis (number 10's)"
    ],
    coachingPoints: [
      "Connection + Press + Cover",
      "Block and Press on Triggers",
      "Line Broken = Positioning of furthest defender to be 2nd Defender",
      "Pressing cues (open ball)",
      "3+1 emergency defending",
      "Force one way"
    ],
    diagram: {
      type: "full-pitch",
      zones: [],
      // Green players (attackers including GK)
      attackers: [
        { x: 50, y: 12, label: "GK" },     // GK
        // 4 blue players closer to big goal than reds
        { x: 28, y: 38 },                   // Left blue
        { x: 45, y: 35 },                   // Left center blue
        { x: 55, y: 35 },                   // Right center blue
        { x: 72, y: 38 },                   // Right blue
        // 2 blue players between central reds and mini goals
        { x: 42, y: 55 },                   // Left mid blue
        { x: 58, y: 55 },                   // Right mid blue
      ],
      // Red players (defenders)
      defenders: [
        { x: 10, y: 45 },                   // Olivia - closer to sideline
        { x: 40, y: 42 },                   // Abigail
        { x: 55, y: 42 },                   // Cece
        { x: 92, y: 45 },                   // Irena
        { x: 35, y: 85 },                   // Emma - on halfway line
        { x: 65, y: 85 },                   // Mia - on halfway line
      ],
      neutrals: [],
      ball: { x: 35, y: 80 },
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 20, rotation: 0 },
        // Mini goals closer to center circle
        { x: 45, y: 70, width: 8, rotation: 180 },
        { x: 55, y: 70, width: 8, rotation: 180 },
      ],
      cones: [],
    }
  },
  {
    id: "positioning-boxes",
    title: "Positioning Boxes",
    description: "Grid split into 4 zones with players set up in 1-2 shape. Objective is to get the ball to 2 players in middle zone to create 2v1.",
    category: "positioning-games",
    topics: ["bpo-1", "bpo-3+1"],
    duration: "4x4mins",
    players: "8",
    areaSize: "25x15m",
    rules: [
      "Grid split into 4 zones (as shown)",
      "Players set up in 1-2 shape in respective zones",
      "1 attacker and defender from each zone is allowed to step one grid in front to support attack or apply pressure to opponent",
      "+1 with a joker can also be used to play the ball into the attackers within the attackers half",
      "Objective for attacking team is to get the ball to their 2 players in middle zone to create 2v1 and score against 1 defender",
      "Objective for defending team is to prevent attackers from turning and facing forward",
      "Offsides apply"
    ],
    coachingPoints: [
      "Pass quality to play the ball quickly and accurately to the attacker",
      "Attacker to receive facing forward if possible",
      "Anticipation ready to step towards the attacker as the ball is played",
      "Close down space and prevent attacker from facing forward",
      "Once attacker is facing backward, keep your arm outstretched to prevent them from turning",
      "As you get close, find a moment to win the ball with your front foot"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // 4 zones
        { x: 10, y: 25, width: 18, height: 50, color: "rgba(245, 208, 140, 0.5)" },
        { x: 30, y: 25, width: 18, height: 50, color: "rgba(245, 208, 140, 0.5)" },
        { x: 50, y: 25, width: 18, height: 50, color: "rgba(245, 208, 140, 0.5)" },
        { x: 70, y: 25, width: 18, height: 50, color: "rgba(245, 208, 140, 0.5)" },
      ],
      // Blue players (attackers)
      attackers: [
        { x: 18, y: 50 },                   // Abigail - left zone
        { x: 58, y: 35 },                   // Emmy - middle-right zone top
        { x: 58, y: 62 },                   // Erika - middle-right zone bottom
      ],
      // Red players (defenders)
      defenders: [
        { x: 38, y: 35 },                   // Ella - middle-left zone top
        { x: 38, y: 62 },                   // Leyla - middle-left zone bottom
        { x: 78, y: 50 },                   // Cece - right zone
      ],
      // Neutral/joker player
      neutrals: [
        { x: 38, y: 50 },                   // Christina - middle-left zone center
        { x: 92, y: 50, color: "#22C55E" }, // Anastasia - green joker on right
      ],
      balls: [
        { x: 24, y: 50 },                   // Ball on other side of Abigail
      ],
      arrows: [],
      goals: [
        { x: 5, y: 50, width: 8, rotation: 90 },
        { x: 95, y: 50, width: 16, rotation: -90 },
      ],
      cones: [],
    }
  },
  {
    id: "building-boxes",
    title: "Building Boxes",
    description: "Boxes 6v3 with maximum of four blue players in one box. Reds win possession and straight swap with blue who lost the ball.",
    category: "brilliant-basics",
    topics: ["building"],
    duration: "4x3mins",
    players: "9",
    areaSize: "14x20",
    rules: [
      "Boxes 6v3 with maximum of four blue players in one box",
      "Reds win possession and straight swap with blue who lost the ball",
      "If a red wins the ball its a straight swap with that blue player",
      "If error occurs the closest red player gets to swap",
      "When ball transfers one red and 2 blues transfer",
      "Block 1: No Rotation",
      "Block 2: Fullback Inverts + 6 Progresses + 10 Provides Width",
      "Block 3: Fullback Inverts or 10 Drops In",
      "Block 4: Fullback Inverts or 10 Drops In or CB Drives",
      "Progression must go through central zones"
    ],
    coachingPoints: [
      "Technical: In Possession Principles",
      "Rotations/Movements to Create Free Player",
      "Fullback Inverting",
      "10 Dropping In",
      "CB Driving",
      "Players identifying rotation and not doubling up"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Top box
        { x: 20, y: 8, width: 60, height: 38, color: "rgba(255,255,255,0.05)" },
        // Bottom box
        { x: 20, y: 54, width: 60, height: 38, color: "rgba(255,255,255,0.05)" },
      ],
      // Blue players - 4 in diamond in top box, 2 in bottom
      attackers: [
        { x: 50, y: 10 },                   // Top of diamond (very top)
        { x: 22, y: 27 },                   // Left of diamond
        { x: 78, y: 27 },                   // Right of diamond
        { x: 50, y: 44 },                   // Bottom of diamond
        { x: 45, y: 65 },                   // Bottom grid left
        { x: 50, y: 88 },                   // Bottom grid center
      ],
      // Red players - 2 in top grid, 1 in bottom
      defenders: [
        { x: 55, y: 25 },                   // Top box center
        { x: 65, y: 30 },                   // Top box right (moved from bottom)
        { x: 50, y: 72 },                   // Bottom box center
      ],
      neutrals: [],
      balls: [
        { x: 50, y: 15 },                    // Ball below top blue player
      ],
      arrows: [],
      goals: [],
      cones: [],
      lines: [
        { startX: 20, startY: 46, endX: 80, endY: 46, style: "dashed", color: "#F59E0B" },
      ],
    }
  },
  {
    id: "building-pattern",
    title: "Building Pattern",
    description: "Pattern play to focus on inverting fullback. Players follow the patterns and movements once the ball hits the opposite side winger who plays the ball in behind the red defenders become active and the team attempt to score on goal.",
    category: "patterns",
    topics: ["building"],
    duration: "3x4mins",
    players: "15",
    areaSize: "Half Pitch",
    rules: [
      "Pattern play to focus on inverting fullback",
      "Players follow the patterns and movements once the ball hits the opposite side winger who plays the ball in behind the red defenders become active and the team attempt to score on goal",
      "After the finishing phase ball resets from CB who build from the alternate side",
      "Blue Goal Scored = 1 Point",
      "Red No Goal Conceded = 1 Point",
      "Progression 1: CB > FB > 6 > FB > Winger",
      "Progression 2: CB > FB > Skip to CB > 6",
      "Progression 3: CB > FB > CB > Skip to Winger"
    ],
    coachingPoints: [
      "Technical: In Possession Principles",
      "Passing Speed",
      "Timing of Movements",
      "Tactical: Rotations/Movements to Create Free Player",
      "Fullback Inverting",
      "Timing of Movement",
      "Blind Side of Opponent when they are watching the ball",
      "Players identifying rotation and not doubling up"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 30, y: 92, label: "3" },
        { x: 70, y: 92, label: "4" },
        { x: 5, y: 70, label: "5" },
        { x: 40, y: 46, label: "6" },
        { x: 60, y: 46, label: "10" },
        { x: 38, y: 68, label: "6" },
        { x: 62, y: 68, label: "2" },
        { x: 5, y: 35, label: "11" },
        { x: 95, y: 35, label: "7" },
        { x: 50, y: 30, label: "9" },
      ],
      neutrals: [
        { x: 50, y: 8, label: "1", color: "#FBBF24" },
      ],
      defenders: [
        { x: 25, y: 26, label: "2" },
        { x: 40, y: 26, label: "3" },
        { x: 60, y: 26, label: "4" },
        { x: 75, y: 26, label: "5" },
      ],
      mannequins: [
        // 4 defenders line (closest to goal)
        { x: 25, y: 35 },
        { x: 40, y: 35 },
        { x: 60, y: 35 },
        { x: 75, y: 35 },
        // 4 midfielders line
        { x: 25, y: 57 },
        { x: 40, y: 57 },
        { x: 60, y: 57 },
        { x: 75, y: 57 },
        // 2 forwards (closest to halfway)
        { x: 40, y: 79 },
        { x: 60, y: 79 },
      ],
      ball: { x: 30, y: 88 },
      arrows: [
        { startX: 30, startY: 92, endX: 5, endY: 70, type: "pass" },
        { startX: 5, startY: 70, endX: 38, endY: 68, type: "pass" },
        { startX: 38, startY: 68, endX: 62, endY: 68, type: "pass" },
        { startX: 62, startY: 68, endX: 95, endY: 35, type: "pass" },
      ],
      goals: [
        { x: 50, y: 2, width: 16, rotation: 0 },
      ],
      cones: [],
      zones: [
        { x: 35, y: 57, width: 30, height: 25, color: "rgba(59, 130, 246, 0.2)" },
      ],
    }
  },
  {
    id: "building-game-training",
    title: "Building Game Training",
    description: "9v8+GK game training focused on building from the back with emphasis on getting four players in the central zone during build up.",
    category: "game-training",
    topics: ["building"],
    duration: "3x8mins",
    players: "18",
    areaSize: "3/4 Pitch Narrowed",
    rules: [
      "BP - 3 - 2 - 4",
      "BPO - 4 - 2 - 2",
      "9 v 8 + GK",
      "Blues build up from CB who plays to FB which initiates press",
      "Blues then attempt to score on goal with a focus on getting four players in the central blue box on build up",
      "If they then score from this action the goal is awarded two",
      "Reds win possession they attempt to score in the three mini goals in 15 seconds",
      "Dots set out for starting positions",
      "Four Blues in Blue Box = 1 Point",
      "Goal = 1 Point",
      "Progression: Final Block Open Game"
    ],
    coachingPoints: [
      "Technical: In Possession Principles",
      "Passing Speed",
      "Timing of Movements",
      "Tactical: Rotations/Movements to Create Free Player",
      "Fullback Inverting",
      "Players identifying rotation and not doubling up",
      "Other Rotations: CB Drive",
      "10 Drops Deep",
      "Winger Inside"
    ],
    diagram: {
      type: "full-pitch-no-bottom-box",
      attackers: [
        { x: 50, y: 85, label: "4" },
        { x: 15, y: 65, label: "5" },
        { x: 85, y: 65, label: "2" },
        { x: 35, y: 52, label: "6" },
        { x: 65, y: 52, label: "8" },
        { x: 15, y: 35, label: "11" },
        { x: 85, y: 35, label: "7" },
        { x: 40, y: 30, label: "9" },
        { x: 60, y: 30, label: "10" },
      ],
      defenders: [
        { x: 25, y: 25, label: "2" },
        { x: 40, y: 25, label: "3" },
        { x: 60, y: 25, label: "4" },
        { x: 75, y: 25, label: "5" },
        { x: 35, y: 40, label: "6" },
        { x: 65, y: 40, label: "8" },
        { x: 25, y: 55, label: "10" },
        { x: 75, y: 55, label: "11" },
      ],
      neutrals: [
        { x: 50, y: 8, label: "1", color: "#FBBF24" },
      ],
      mannequins: [],
      ball: { x: 50, y: 80 },
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 16, rotation: 0 },
        { x: 35, y: 97, width: 6, rotation: 180 },
        { x: 50, y: 97, width: 6, rotation: 180 },
        { x: 65, y: 97, width: 6, rotation: 180 },
      ],
      cones: [],
      zones: [
        { x: 32, y: 35, width: 36, height: 30, color: "rgba(59, 130, 246, 0.2)" },
      ],
    }
  },
  {
    id: "play-forward-boxes",
    title: "Play Forward Boxes",
    description: "A practice focused on receiving in the middle zone between opponents' pressing line to face forwards and play forwards.",
    category: "brilliant-basics",
    topics: ["building"],
    duration: "3x3min",
    players: "6",
    areaSize: "4x8m",
    rules: [
      "Player must receive in the middle zone between opponents pressing line to face forwards and play forwards",
      "Player in central area must receive the ball in a position where they can play beyond using support of wide teammate",
      "Team cannot progress into the next half until they have combined for minimum of 5 passes",
      "To score a player must receive the ball in the next boxed area",
      "If red team wins the ball they can immediately score in the mini-goals",
      "If red team scores the ball restarts with the opposition team (practice is flipped)"
    ],
    coachingPoints: [
      "Can you play forward and position yourself between the oppositions midfield line before doing so?",
      "After you play the ball forwards can you make a forward's movement to get in behind (2nd man run)?",
      "Can you make a diagonal run in behind to receive the ball beyond the opponent's final defensive line?",
      "Can you time receiving a pass in order to face forwards and progress play forwards?",
      "How can you combine to play forwards and progress from one side to the other?",
      "When do you need to scan and be aware of space to plan your next action?"
    ],
    diagram: {
      type: "custom",
      attackers: [
        { x: 30, y: 75, label: "" },
        { x: 50, y: 75, label: "" },
        { x: 70, y: 75, label: "" },
        { x: 50, y: 55, label: "" },
        { x: 50, y: 93, label: "" },
      ],
      defenders: [
        { x: 50, y: 40, label: "" },
        { x: 50, y: 75, label: "" },
      ],
      neutrals: [],
      mannequins: [],
      ball: { x: 50, y: 88 },
      arrows: [],
      goals: [
        { x: 35, y: 3, width: 6, rotation: 0 },
        { x: 65, y: 3, width: 6, rotation: 0 },
        { x: 35, y: 97, width: 6, rotation: 180 },
        { x: 65, y: 97, width: 6, rotation: 180 },
      ],
      cones: [],
      zones: [
        { x: 20, y: 0, width: 60, height: 40, color: "rgba(0, 0, 0, 0.1)" },
        { x: 20, y: 40, width: 60, height: 20, color: "rgba(0, 0, 0, 0.05)" },
        { x: 20, y: 60, width: 60, height: 40, color: "rgba(0, 0, 0, 0.1)" },
      ],
      lines: [
        { startX: 20, startY: 40, endX: 80, endY: 40, style: "solid", color: "#000" },
        { startX: 20, startY: 60, endX: 80, endY: 60, style: "solid", color: "#000" },
      ],
    }
  },
  {
    id: "play-forward-positioning",
    title: "Play Forward",
    description: "A positioning game where players must receive in the middle zone between opponents' pressing line to face forwards and play forwards, progressing through zones.",
    category: "positioning-games",
    topics: ["building", "mid-play"],
    duration: "3x3min",
    players: "12",
    areaSize: "20x30m",
    rules: [
      "Player must receive in the middle zone (between opponents pressing line) to face forwards and play forwards",
      "Player in central area must receive the ball in a position where they can play beyond using support of wide teammate",
      "Team cannot progress into the next half until they have combined for minimum of 5 passes",
      "To score a player must receive the ball in the next boxed area",
      "If red team wins the ball they can immediately score in the mini-goals",
      "If red team scores the ball restarts with the opposition team (practice is flipped)"
    ],
    coachingPoints: [
      "Can you play forward and position yourself between the oppositions midfield line before doing so?",
      "After you play the ball forwards can you make a forward's movement to get in behind (2nd man run)?",
      "Can you make a diagonal run in behind to receive the ball beyond the opponent's final defensive line?",
      "Can you time receiving a pass in order to face forwards and progress play forwards?",
      "How can you combine to play forwards and progress from one side to the other?",
      "When do you need to scan and be aware of space to plan your next action?"
    ],
    diagram: {
      type: "custom",
      attackers: [
        // Bottom zone - 2 blue players (more central)
        { x: 40, y: 92, label: "" },
        { x: 60, y: 92, label: "" },
        // Middle zone - 3 blue players
        { x: 30, y: 47, label: "" },
        { x: 50, y: 47, label: "" },
        { x: 70, y: 47, label: "" },
        // Side line blues in line with lower red pair (at y: 75)
        { x: 20, y: 75, label: "" },
        { x: 80, y: 75, label: "" },
      ],
      defenders: [
        // Bottom zone - 2 middle reds (converted from blues)
        { x: 40, y: 75, label: "" },
        { x: 60, y: 75, label: "" },
        // On the middle-to-top line - 2 new reds
        { x: 35, y: 40, label: "" },
        { x: 65, y: 40, label: "" },
      ],
      neutrals: [
        // Goalkeeper - yellow
        { x: 50, y: 8, label: "" },
      ],
      mannequins: [],
      ball: { x: 40, y: 88 },
      arrows: [
        { startX: 40, startY: 88, endX: 20, endY: 75, type: "pass" },
        { startX: 20, startY: 75, endX: 50, endY: 47, type: "pass" },
        { startX: 30, startY: 47, endX: 45, endY: 25, type: "movement", curved: true },
      ],
      goals: [
        { x: 35, y: 97, width: 6, rotation: 180 },
        { x: 65, y: 97, width: 6, rotation: 180 },
        { x: 50, y: 3, width: 12, rotation: 0 },
      ],
      cones: [],
      zones: [
        { x: 20, y: 0, width: 60, height: 40, color: "rgba(0, 0, 0, 0.1)" },
        { x: 20, y: 40, width: 60, height: 15, color: "rgba(0, 0, 0, 0.05)" },
        { x: 20, y: 55, width: 60, height: 45, color: "rgba(0, 0, 0, 0.1)" },
      ],
      lines: [
        { startX: 20, startY: 40, endX: 80, endY: 40, style: "solid", color: "#000" },
        { startX: 20, startY: 55, endX: 80, endY: 55, style: "solid", color: "#000" },
      ],
    }
  },
  {
    id: "through-around-over",
    title: "Through Around Over",
    description: "Practice designed for players to progress into space away from opponent by making a forward run into space and using end player as a set pass.",
    category: "positioning-games",
    topics: ["building"],
    duration: "3x3mins",
    players: "11",
    areaSize: "20x40m",
    rules: [
      "Ball is always served from the side from a coach to the players on the base line that are set in a back 4 shape",
      "After 5 passes a player in possession can progress over the half-way line and play in the opposite half by finding the 'outlet' pass to the player on opposite side",
      "If a blue player loses the ball to a pink player then they will swap teams and that player now becomes a defender"
    ],
    coachingPoints: [
      "Players will be encouraged to exploit numerical advantage to progress over to the next area of free space",
      "Attacking team will be encouraged to create through passes and 2v1 on wide player in space",
      "Forwards passing, timing of movement forwards",
      "Mobility to link the play forwards in a direction"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Top zone (equal)
        { x: 20, y: 10, width: 60, height: 40, color: "rgba(255,255,255,0.05)" },
        // Bottom zone (equal)
        { x: 20, y: 50, width: 60, height: 40, color: "rgba(255,255,255,0.1)" },
      ],
      attackers: [
        // Blue player in top zone
        { x: 50, y: 30 },
        // Blue players in back 4 shape
        { x: 30, y: 80 },
        { x: 45, y: 85 },
        { x: 55, y: 85 },
        { x: 70, y: 80 },
        // Blues in middle zone
        { x: 35, y: 60 },
        { x: 50, y: 65 },
        { x: 65, y: 60 },
      ],
      defenders: [
        // Pink defenders
        { x: 40, y: 55, color: "#EC4899" },
        { x: 60, y: 55, color: "#EC4899" },
        { x: 50, y: 70, color: "#EC4899" },
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 95, width: 14, rotation: 180 },
      ],
      lines: [
        // Half-way line between zones
        { startX: 20, startY: 50, endX: 80, endY: 50, style: "solid", color: "white" },
        // Outer boundary
        { startX: 20, startY: 10, endX: 80, endY: 10, style: "solid", color: "white" },
        { startX: 20, startY: 90, endX: 80, endY: 90, style: "solid", color: "white" },
        { startX: 20, startY: 10, endX: 20, endY: 90, style: "solid", color: "white" },
        { startX: 80, startY: 10, endX: 80, endY: 90, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "through-thirds",
    title: "Through Thirds",
    description: "7v5 positioning game. 4v2 in box one, to 2v2, to 1v1. Greens to play through the thirds to advance. Greens can go +1 moving forward. Greens one touch finish in final zone.",
    category: "positioning-games",
    topics: ["building"],
    duration: "4x4mins",
    players: "12",
    areaSize: "20x32m",
    rules: [
      "7v5",
      "4v2 in box one, to 2v2, to 1v1",
      "Greens to play through the thirds to advance",
      "Greens can go +1 moving forward",
      "Greens one touch finish in final zone",
      "Reds to start on disc",
      "Reds can only press in their zone",
      "Can press on first pass and score in any goal when they win possession",
      "Restarts at the central CB"
    ],
    coachingPoints: [
      "Double Movements to Lose Defender",
      "Identifying Space/Scan",
      "Deception/Disguise - Body Feint, Reverse Pass",
      "Receive level or beyond your marker",
      "Touch - Front foot / Back foot",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Top zone (attacking - white)
        { x: 15, y: 10, width: 70, height: 25, color: "rgba(255,255,255,0.05)" },
        // Middle zone (yellow/defending)
        { x: 15, y: 35, width: 70, height: 30, color: "rgba(255,220,150,0.3)" },
        // Bottom zone (building - white)
        { x: 15, y: 65, width: 70, height: 25, color: "rgba(255,255,255,0.05)" }
      ],
      attackers: [
        // Green players - top zone (1v1)
        { x: 50, y: 18, color: "#10B981" },
        // Green players - middle zone (2v2)
        { x: 30, y: 45, color: "#10B981" },
        { x: 70, y: 45, color: "#10B981" },
        // Green players - bottom zone (4v2)
        { x: 25, y: 78, color: "#10B981" },
        { x: 50, y: 75, color: "#10B981" },
        { x: 75, y: 78, color: "#10B981" },
        { x: 50, y: 88, color: "#10B981" }
      ],
      defenders: [
        // Red player - top zone
        { x: 55, y: 15 },
        // Red players - middle zone
        { x: 35, y: 48 },
        { x: 65, y: 48 },
        // Red players - bottom zone
        { x: 40, y: 75 },
        { x: 60, y: 75 }
      ],
      neutrals: [],
      balls: [
        { x: 50, y: 84 }
      ],
      cones: [],
      arrows: [],
      goals: [
        // Top mini goals (3)
        { x: 30, y: 5, width: 6, rotation: 0 },
        { x: 50, y: 5, width: 6, rotation: 0 },
        { x: 70, y: 5, width: 6, rotation: 0 },
        // Bottom mini goals (3)
        { x: 30, y: 95, width: 6, rotation: 180 },
        { x: 50, y: 95, width: 6, rotation: 180 },
        { x: 70, y: 95, width: 6, rotation: 180 }
      ],
      lines: [
        // Zone dividers
        { startX: 15, startY: 35, endX: 85, endY: 35, style: "dashed", color: "rgba(255,255,255,0.5)" },
        { startX: 15, startY: 65, endX: 85, endY: 65, style: "dashed", color: "rgba(255,255,255,0.5)" }
      ]
    }
  },
  {
    id: "attract-to-penetrate",
    title: "Attract to Penetrate",
    description: "4v3 in build-up area with 2 defenders screening. 3 jokers who are two touch and cannot transfer the ball. Players centrally try to play the ball below knee height into the next zone.",
    category: "positioning-games",
    topics: ["building"],
    duration: "4x3mins",
    players: "14",
    areaSize: "40x24m",
    rules: [
      "4v3 in build-up area with 2 defenders screening",
      "3 jokers are two touch and cannot transfer the ball",
      "Players centrally try to play the ball below knee height into the next zone",
      "Can also dribble with the ball to break the line",
      "If defending team win the ball, they keep the ball",
      "In transition, attacking team try to win the ball back and play first ball out to a green player out wide"
    ],
    coachingPoints: [
      "Double movements to lose defender",
      "Identifying space/scan",
      "Deception/Disguise - Reverse pass",
      "Receive level or beyond your marker",
      "Touch - Front foot / Back foot",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line",
      "2/5 forward runs for through",
      "Invert into space when available",
      "6 Drop and attract",
      "Support - Underneath and beyond"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Build-up zone (left)
        { x: 10, y: 20, width: 32, height: 60, color: "rgba(255,255,255,0.1)" },
        // Middle zone (thinner)
        { x: 42, y: 20, width: 16, height: 60, color: "rgba(255,255,255,0.05)" },
        // Final zone (right) - equal to left
        { x: 58, y: 20, width: 32, height: 60, color: "rgba(255,255,255,0.1)" },
      ],
      attackers: [
        // Build-up zone - 4 blues
        { x: 15, y: 35 },
        { x: 15, y: 65 },
        { x: 30, y: 45 },
        { x: 30, y: 55 },
        // Final zone - 2 blues
        { x: 80, y: 35 },
        { x: 80, y: 65 },
      ],
      defenders: [
        // Screening defenders in middle zone - 2 reds (closer together)
        { x: 50, y: 42 },
        { x: 50, y: 58 },
        // Defenders in build-up zone - 3 reds
        { x: 28, y: 35 },
        { x: 23, y: 50 },
        { x: 28, y: 65 },
      ],
      neutrals: [
        // Green between the two reds in middle zone
        { x: 50, y: 50, color: "#22c55e" },
        // Yellow jokers on top and bottom lines of left zone
        { x: 26, y: 20 },
        { x: 26, y: 80 },
      ],
      cones: [],
      arrows: [],
      ball: { x: 18, y: 68 },
      lines: [
        // Zone dividers
        { startX: 42, startY: 20, endX: 42, endY: 80, style: "solid", color: "white" },
        { startX: 58, startY: 20, endX: 58, endY: 80, style: "solid", color: "white" },
        // Outer boundary
        { startX: 10, startY: 20, endX: 90, endY: 20, style: "solid", color: "white" },
        { startX: 10, startY: 80, endX: 90, endY: 80, style: "solid", color: "white" },
        { startX: 10, startY: 20, endX: 10, endY: 80, style: "solid", color: "white" },
        { startX: 90, startY: 20, endX: 90, endY: 80, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "play-forward-game-training",
    title: "Play Forward Game Training",
    description: "A 6v6 game training exercise focused on playing forward and positioning between the opposition's midfield line.",
    category: "game-training",
    topics: ["building"],
    duration: "2x10min",
    players: "13",
    areaSize: "20x40m",
    rules: [
      "6v6 game format",
      "Reds score in mini goals on the sides",
      "Blues score in the big goal",
      "Focus on playing forward and breaking lines"
    ],
    coachingPoints: [
      "Can you play forward and position yourself between the oppositions midfield line before doing so?",
      "After you play the ball forwards can you make a forward's movement to get in behind (2nd man run)?",
      "Can you make a diagonal run in behind to receive the ball beyond the opponent's final defensive line?",
      "Can you time receiving a pass in order to face forwards and progress play forwards?",
      "How can you combine to play forwards and progress from one side to the other?",
      "When do you need to scan and be aware of space to plan your next action?"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        // White/Blue team - below central reds (more central and lower)
        { x: 42, y: 65, label: "" },
        { x: 58, y: 65, label: "" },
        // Blue players below wide reds
        { x: 5, y: 52, label: "" },
        { x: 95, y: 52, label: "" },
        // Blue players on other side of central reds (above them)
        { x: 40, y: 45, label: "" },
        { x: 60, y: 45, label: "" },
      ],
      defenders: [
        // Red team - back line
        { x: 25, y: 25, label: "" },
        { x: 75, y: 25, label: "" },
        // Red team - midfield line (wide reds back to sidelines)
        { x: 5, y: 45, label: "" },
        { x: 40, y: 50, label: "" },
        { x: 60, y: 50, label: "" },
        { x: 95, y: 45, label: "" },
      ],
      neutrals: [
        // Pink in front of big goal
        { x: 50, y: 25, label: "" },
      ],
      mannequins: [],
      ball: { x: 50, y: 30 },
      arrows: [],
      goals: [
        // Big goal past the goal square
        { x: 50, y: 20, width: 14, rotation: 0 },
        // Mini goals at the line near the blue players
        { x: 35, y: 75, width: 6, rotation: 0 },
        { x: 65, y: 75, width: 6, rotation: 0 },
      ],
      cones: [],
      zones: [],
      lines: [
        // Halfway line
        { startX: 5, startY: 75, endX: 95, endY: 75, style: "solid", color: "rgba(255,255,255,0.5)" },
      ],
    }
  },
  {
    id: "transition-box",
    title: "Transition Box",
    description: "Possession game with transitions. Complete 8 passes to score. Upon winning the ball, counter-attacking team must play through central neutrals to other half.",
    category: "brilliant-basics",
    topics: ["transitions"],
    duration: "3x4mins",
    players: "9",
    areaSize: "30x15m",
    rules: [
      "To score a goal, the attacking team must complete 8 passes between their own team",
      "If they pass to the outside players, that pass does not count in their attempt to score",
      "Upon winning the ball, the counter-attacking team must pass to one of the central neutral players who then passes back to play to the other side of the field",
      "Replicate playing forward, then maintain possession in the other half and attempt to make 8 passes",
      "Upon winning and passing to the neutral players in the middle, they can either pass back if behind the halfway line or pass straight through if in the same half"
    ],
    coachingPoints: [
      "Progression vs Possession",
      "Hit highest point of pass as quickly as possible (1st out and forward)",
      "Secure FBO",
      "Attackers need to either support passes underneath to receive a pass to play through",
      "Or support ahead of the ball as the ball is played through quickly"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 10, y: 20, width: 80, height: 60, color: "rgba(255,255,255,0.05)" },
      ],
      attackers: [
        { x: 25, y: 35 },
        { x: 35, y: 50 },
        { x: 25, y: 65 },
      ],
      defenders: [
        { x: 35, y: 35 },
        { x: 45, y: 55 },
        { x: 30, y: 65 },
      ],
      neutrals: [
        { x: 45, y: 40 },
        { x: 5, y: 50 },
        { x: 95, y: 50 },
      ],
      ball: { x: 30, y: 50 },
      arrows: [],
      goals: [],
      cones: [],
      lines: [
        { startX: 50, startY: 20, endX: 50, endY: 80, style: "dotted", color: "rgba(255,255,255,0.6)" },
      ],
    }
  },
  {
    id: "cb-drive",
    title: "CB Drive",
    description: "Players must move ball from one end line to the other. Can only RWB over middle line. Defenders are locked in their zones. Attackers can move into either half when in possession.",
    category: "brilliant-basics",
    topics: ["building"],
    duration: "6x2mins",
    players: "6",
    areaSize: "15x20m",
    rules: [
      "Players must move ball from one end line to the other",
      "Can only RWB over middle line",
      "Defenders are locked in their zones",
      "Attackers can move into either half when in possession",
      "Score by RWB over end line",
      "Players swap roles every 2mins",
      "Ball to restart with team that has scored goal"
    ],
    coachingPoints: [
      "Circulate ball in back half until a CB can inject into the next third",
      "When RWB, ball carrier to keep the ball on furthest foot from opponent",
      "As CB is injecting into next half, can the CM move away from the ball to create space",
      "If tightly marked can supporting player move in line with the ball",
      "CB to use disguise when attracting opponent and passing to midfielder",
      "CM to stay away from CB during buildup (level / outside)"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Top zone
        { x: 15, y: 15, width: 70, height: 35, color: "rgba(255,255,255,0.05)" },
        // Bottom zone
        { x: 15, y: 50, width: 70, height: 35, color: "rgba(255,255,255,0.05)" }
      ],
      attackers: [
        // Blue player - top zone
        { x: 30, y: 25 },
        // Blue players - bottom zone
        { x: 25, y: 75 },
        { x: 70, y: 70 }
      ],
      defenders: [
        // Red players - top zone
        { x: 55, y: 30 },
        { x: 38, y: 25 },
        // Red player - just below halfway line
        { x: 50, y: 55 }
      ],
      neutrals: [],
      balls: [
        { x: 25, y: 71 }
      ],
      cones: [],
      arrows: [],
      goals: [],
      lines: [
        // Middle dividing line
        { startX: 15, startY: 50, endX: 85, endY: 50, style: "dashed", color: "rgba(0,0,0,0.5)" },
        // Outer border
        { startX: 15, startY: 15, endX: 85, endY: 15, style: "solid", color: "rgba(0,0,0,0.5)" },
        { startX: 15, startY: 85, endX: 85, endY: 85, style: "solid", color: "rgba(0,0,0,0.5)" },
        { startX: 15, startY: 15, endX: 15, endY: 85, style: "solid", color: "rgba(0,0,0,0.5)" },
        { startX: 85, startY: 15, endX: 85, endY: 85, style: "solid", color: "rgba(0,0,0,0.5)" }
      ]
    }
  },
  {
    id: "transitional-transfer-rondo",
    title: "Transitional Transfer Rondo",
    description: "3v2 with a target. BPO - 2 can press, if win get across the line for a point. BP - Play forward pass from def half to target to get a point. FUN - Promotion - Relegation.",
    category: "brilliant-basics",
    topics: ["transitions"],
    duration: "4x3mins",
    players: "6",
    areaSize: "20x10m",
    rules: [
      "3v2 with a target",
      "BPO - 2 can press, if win get across the line for a point",
      "BP - Play forward pass from def half to target to get a point",
      "FUN - Promotion - Relegation"
    ],
    coachingPoints: [
      "Inject to trigger movement to lose marker",
      "Receive level or beyond your marker",
      "Receive with an open body shape",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line",
      "Supporting positions (time - no time)"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Left zone (white/attacking)
        { x: 10, y: 20, width: 40, height: 60, color: "rgba(255,255,255,0.05)" },
        // Right zone (yellow/defending)
        { x: 50, y: 20, width: 40, height: 60, color: "rgba(255,220,150,0.3)" }
      ],
      attackers: [
        // Blue players (3)
        { x: 55, y: 25 },
        { x: 60, y: 75 },
        { x: 90, y: 50 }
      ],
      defenders: [
        // Red players (3)
        { x: 10, y: 50 },
        { x: 70, y: 40 },
        { x: 65, y: 60 }
      ],
      neutrals: [],
      balls: [
        { x: 86, y: 50 }
      ],
      cones: [],
      arrows: [],
      goals: [],
      lines: [
        // Dividing line between zones
        { startX: 50, startY: 20, endX: 50, endY: 80, style: "dashed", color: "rgba(255,255,255,0.5)" }
      ]
    }
  },
  {
    id: "rwtb-to-score",
    title: "RWTB to Score",
    description: "Follow passing pattern to allow Run With The Ball and cut across the defender to score in 1v1 vs GK.",
    category: "brilliant-basics",
    topics: ["creating-finishing"],
    duration: "4x3mins",
    players: "5",
    areaSize: "14x36m",
    rules: [
      "Follow passing pattern to allow RWTB and cut across the defender to score in 1v1 vs GK",
      "If defenders win the ball, they need to play into mini goals",
      "Red player chases on the final blues first touch",
      "PROG - Change distance of the chasing defender",
      "Change sides to favor both L+R"
    ],
    coachingPoints: [
      "Body positioning open to break a line",
      "Explosive touch to go beyond Body-Body-Ball",
      "Top pointed down, head up, large touches"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 10, y: 10, width: 80, height: 35, color: "rgba(255,255,255,0.05)" },
        { x: 10, y: 55, width: 80, height: 35, color: "rgba(255,255,255,0.05)" },
      ],
      attackers: [
        { x: 45, y: 92 },
        { x: 15, y: 72 },
        { x: 84, y: 72 },
      ],
      defenders: [
        { x: 95, y: 72 },
      ],
      neutrals: [
        { x: 50, y: 8 },
      ],
      mannequins: [
        { x: 50, y: 95 },
        { x: 10, y: 72 },
        { x: 90, y: 72 },
      ],
      ball: { x: 50, y: 88 },
      arrows: [
        { startX: 45, startY: 90, endX: 82, endY: 74, type: "pass" },
        { startX: 82, startY: 70, endX: 47, endY: 90, type: "pass" },
        { startX: 43, startY: 90, endX: 17, endY: 74, type: "pass" },
        { startX: 15, startY: 70, endX: 38, endY: 30, type: "dribble" },
        { startX: 95, startY: 70, endX: 58, endY: 48, type: "dribble" },
      ],
      goals: [
        { x: 50, y: 2, width: 14, rotation: 0 },
        { x: 30, y: 98, width: 8, rotation: 180 },
        { x: 70, y: 98, width: 8, rotation: 180 },
      ],
      cones: [],
      lines: [
        { startX: 10, startY: 50, endX: 90, endY: 50, style: "dashed", color: "rgba(255,255,255,0.5)" },
      ],
    }
  },
  {
    id: "wide-areas",
    title: "Wide Areas",
    description: "3v3 plus 1 joker positioning game in a rondo zone. Work the ball into wide zones to create finishing opportunities.",
    category: "positioning-games",
    topics: ["creating-finishing"],
    duration: "3x8mins",
    players: "12",
    areaSize: "45x24m",
    rules: [
      "Play will begin as a 3v3 plus 1 joker in a rondo zone",
      "The objective for the attacking team is to work the ball into one of two team mates positioned in a wide zone",
      "There will be one defender positioned in the wide zone attempting to win possession",
      "A second defender can enter that zone as a recovery run as soon as a pass is made into that area",
      "A minimum of three passes must be complete in the rondo zone before attempting to switch play"
    ],
    coachingPoints: [
      "6: If a ball is played beyond you, can you get in a line of pass underneath so we can play the next pass forward?",
      "As the ball is played from one side of the pitch, try to receive it on your furthest foot to expose the opposition",
      "If we play a pass to team mate in a tight area, can you have quick 2nd movement to provide another line of pass",
      "7/11: As your team mates combine centrally, can you create width and threaten their last line",
      "10: If there is space in the pocket, can you receive and drive, or play quickly out of the pocket",
      "If your team mate has the ball open, can you make a run beyond to threaten through the channel"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 5, y: 35, width: 28, height: 60, color: "rgba(255,200,0,0.15)" },
        { x: 36, y: 35, width: 28, height: 60, color: "rgba(255,200,0,0.05)" },
        { x: 67, y: 35, width: 28, height: 60, color: "rgba(255,200,0,0.15)" },
      ],
      attackers: [
        { x: 42, y: 38 },
        { x: 50, y: 50 },
        { x: 58, y: 38 },
        { x: 75, y: 42 },
        { x: 90, y: 65 },
      ],
      defenders: [
        { x: 40, y: 48 },
        { x: 64, y: 62 },
        { x: 60, y: 48 },
        { x: 93, y: 50 },
        { x: 75, y: 52 },
      ],
      neutrals: [
        { x: 50, y: 8 },
        { x: 35, y: 62 },
      ],
      ball: { x: 64, y: 58 },
      arrows: [],
      goals: [
        { x: 50, y: 5, width: 14, rotation: 0 },
        { x: 50, y: 65, width: 8, rotation: 180 },
        { x: 80, y: 65, width: 8, rotation: 180 },
      ],
      cones: [],
      lines: [
        { startX: 33, startY: 35, endX: 33, endY: 65, style: "dashed", color: "rgba(255,200,0,0.6)" },
        { startX: 67, startY: 35, endX: 67, endY: 65, style: "dashed", color: "rgba(255,200,0,0.6)" },
        { startX: 5, startY: 65, endX: 95, endY: 65, style: "dashed", color: "rgba(255,200,0,0.6)" },
        { startX: 35, startY: 5, endX: 35, endY: 20, style: "solid", color: "rgba(255,255,255,0.7)" },
        { startX: 65, startY: 5, endX: 65, endY: 20, style: "solid", color: "rgba(255,255,255,0.7)" },
        { startX: 35, startY: 20, endX: 65, endY: 20, style: "solid", color: "rgba(255,255,255,0.7)" },
      ],
    }
  },
  {
    id: "wide-area-2v1",
    title: "Wide Area 2v1",
    description: "6v5+1 open game focusing on exposing wide area numerical superiority",
    category: "positioning-games",
    topics: ["creating-finishing"],
    duration: "2x10mins",
    players: "12",
    areaSize: "40x30m",
    rules: [
      "6v5+1 open game",
      "BP look to expose wide area numerical superiority",
      "BPO team win and score in the mini goals"
    ],
    coachingPoints: [
      "6: If a ball is played beyond you, can you get in a line of pass underneath so we can play the next pass forward?",
      "6: As the ball is played from one side of the pitch, try to receive it on your furthest foot to expose the opposition",
      "If we play a pass to team mate in a tight area, can you have quick 2nd movement to provide another line of pass",
      "7/11: As your team mates combine centrally, can you create width and threaten their last line",
      "10: If there is space in the pocket, can you receive and drive, or play quickly out of the pocket",
      "10: If your team mate has the ball open, can you make a run beyond to threaten through the channel"
    ],
    diagram: {
      type: "custom",
      zones: [
        { x: 5, y: 5, width: 28, height: 90, color: "rgba(255,200,0,0.08)" },
        { x: 67, y: 5, width: 28, height: 90, color: "rgba(255,200,0,0.08)" },
      ],
      attackers: [
        { x: 10, y: 50 },
        { x: 33, y: 55 },
        { x: 50, y: 40 },
        { x: 67, y: 55 },
        { x: 90, y: 50 },
        { x: 50, y: 85 },
      ],
      defenders: [
        { x: 50, y: 12, label: "GK", color: "yellow" },
        { x: 25, y: 44 },
        { x: 40, y: 40 },
        { x: 60, y: 40 },
        { x: 75, y: 44 },
        { x: 50, y: 60 },
      ],
      neutrals: [],
      ball: { x: 50, y: 80 },
      arrows: [],
      goals: [
        { x: 50, y: 5, width: 14, rotation: 0 },
        { x: 10, y: 95, width: 6, rotation: 180 },
        { x: 90, y: 95, width: 6, rotation: 180 },
      ],
      cones: [],
      lines: [
        { startX: 33, startY: 5, endX: 33, endY: 95, style: "dashed", color: "rgba(255,200,0,0.6)" },
        { startX: 67, startY: 5, endX: 67, endY: 95, style: "dashed", color: "rgba(255,200,0,0.6)" },
      ],
    }
  },
  {
    id: "press-cover-box",
    title: "Press Cover Box",
    description: "4v2 drill focusing on pressing and covering principles with end zone target player",
    category: "brilliant-basics",
    topics: ["bpo-1"],
    duration: "6x1.30mins",
    players: "7",
    areaSize: "8x12m",
    rules: [
      "4v2 to find the player in the end zone",
      "End zone player is one touch, combine to score",
      "Two defenders try to win ball and keep the ball"
    ],
    coachingPoints: [
      "1v1: Angle of Approach",
      "1v1: Side On - Force Wide",
      "1v1: Timing of Tackle",
      "2v2: Press and Cover"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 50, y: 18, label: "1" },
        { x: 20, y: 45, label: "2" },
        { x: 80, y: 45, label: "3" },
        { x: 50, y: 72, label: "4" },
        { x: 50, y: 85, label: "5" },
      ],
      defenders: [
        { x: 45, y: 35, label: "1" },
        { x: 55, y: 45, label: "2" },
      ],
      neutrals: [],
      goals: [
        { x: 20, y: 92, width: 6, rotation: 0 },
        { x: 40, y: 8, width: 6, rotation: 180 },
        { x: 60, y: 8, width: 6, rotation: 180 },
        { x: 80, y: 92, width: 6, rotation: 0 },
      ],
      zones: [
        { x: 15, y: 15, width: 70, height: 60, color: "rgba(0,0,0,0.1)" },
        { x: 15, y: 75, width: 70, height: 15, color: "rgba(255,200,0,0.2)" },
      ],
      arrows: [],
      ball: { x: 50, y: 24 },
      cones: [],
      lines: [],
    }
  },
  {
    id: "emergency-defending",
    title: "Emergency Defending",
    description: "GK + 4v3 practice with team in possession aiming to build up and score in the mini goal. Team in possession need to either run or pass over the offside line to score.",
    category: "positioning-games",
    topics: ["bpo-3+1"],
    duration: "4x4mins",
    players: "GK + 7",
    areaSize: "25x15m",
    rules: [
      "GK + 4v3 Practice with the team in possession aiming to build up and score in the mini goal",
      "Team in possession need to either run, or pass over the offside line to score",
      "Red in opposite grid can come off the dot to defend with closest 2nd defender providing a recovery run",
      "One extra blue can join the attack to create a 2v2",
      "All blues players become active defenders",
      "If reds win possession they score a point by scoring in either of the opposite two mini goals",
      "BP - 1 Goal = 1 Point",
      "BPO - Pass to Bouncer = 1 Point"
    ],
    coachingPoints: [
      "Angle of Approach",
      "Side On - Force Wide",
      "Timing of Tackle",
      "Connection + Press + Cover",
      "Block and Press on Triggers",
      "Line Broken = Positioning of furthest defender to be 2nd Defender"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 30, y: 58, label: "1" },
        { x: 65, y: 58, label: "2" },
        { x: 22, y: 72, label: "3" },
        { x: 92, y: 72, label: "4" },
      ],
      defenders: [
        { x: 50, y: 25, label: "1" },
        { x: 50, y: 62, label: "2" },
        { x: 60, y: 68, label: "3" },
      ],
      neutrals: [
        { x: 50, y: 90, label: "GK", color: "#22c55e" },
      ],
      goals: [
        { x: 50, y: 5, width: 8, rotation: 180 },
        { x: 25, y: 95, width: 6, rotation: 0 },
        { x: 75, y: 95, width: 6, rotation: 0 },
      ],
      zones: [],
      arrows: [
        { startX: 50, startY: 90, endX: 92, endY: 72, type: "pass" },
        { startX: 92, startY: 72, endX: 65, endY: 58, type: "pass" },
        { startX: 65, startY: 58, endX: 65, endY: 30, type: "movement" },
        { startX: 50, startY: 25, endX: 65, endY: 30, type: "movement" },
        { startX: 50, startY: 62, endX: 45, endY: 28, type: "movement", curved: true, curveDirection: -1 },
      ],
      ball: { x: 50, y: 85 },
      cones: [
        { x: 50, y: 21, color: "#C13C3C" },
      ],
      lines: [
        { startX: 10, startY: 45, endX: 90, endY: 45, style: "dashed", color: "#eab308" }
      ],
    }
  },
  {
    id: "press-after-pass",
    title: "Press After Pass",
    description: "A building pattern focusing on pressing reactions after passing, with players rotating through positions.",
    category: "patterns",
    topics: ["mid-play"],
    duration: "4x1.5mins",
    players: "10-12",
    areaSize: "8-10m b/w poles",
    rules: [
      "Player 1 with the ball passes to player 2.",
      "Player 1 presses position 3 after passing the ball to player 2.",
      "Player 3, receives the ball and turns to shoot.",
      "Player 4, passes the ball to position 6, and then runs to press position 1.",
      "The rotation continues for 90 sec, and then players rotating in positions 2 and 6 will rotate with players rotating vertically (positions 1, 3 and 4)."
    ],
    coachingPoints: [
      "Reaction is for front on positive press i.e. Player 1 presses player 3",
      "PROG is for side on positive reaction, i.e. player 1 pressure player 2"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 50, y: 75, label: "1", color: "#3b82f6" },
        { x: 55, y: 78, label: "X", color: "#3b82f6" },
        { x: 75, y: 50, label: "2", color: "#3b82f6" },
        { x: 78, y: 55, label: "X", color: "#3b82f6" },
        { x: 50, y: 18, label: "3", color: "#3b82f6" },
        { x: 55, y: 15, label: "X", color: "#3b82f6" },
        { x: 25, y: 50, label: "6", color: "#3b82f6" },
        { x: 22, y: 55, label: "X", color: "#3b82f6" },
        { x: 50, y: 35, label: "4", color: "#3b82f6" },
        { x: 55, y: 38, label: "X", color: "#3b82f6" },
      ],
      defenders: [],
      arrows: [
        { startX: 50, startY: 75, endX: 75, endY: 50, type: "pass" },
        { startX: 75, startY: 50, endX: 50, endY: 18, type: "pass" },
        { startX: 50, startY: 18, endX: 50, endY: 5, type: "movement" },
        { startX: 50, startY: 75, endX: 50, endY: 18, type: "movement", curved: true, curveDirection: -1 },
      ],
      lines: [],
      ball: { x: 55, y: 68 },
      cones: [],
      mannequins: [
        { x: 50, y: 70 },
        { x: 70, y: 50 },
        { x: 50, y: 30 },
        { x: 30, y: 50 },
      ],
      goals: [
        { x: 50, y: 0, width: 14 }
      ],
    }
  },
  {
    id: "midfield-rotations",
    title: "Midfield Rotations",
    description: "CB to CB switch outside area. Ball is played into area for the three to combine to find the outlet ball. Players inside the grid cannot be on the same pole twice in a row. One-in, one-out.",
    category: "patterns",
    topics: ["mid-play"],
    duration: "4x3mins",
    players: "7",
    areaSize: "24x18m",
    rules: [
      "CB to CB switch outside area",
      "Ball is played into area for the three to combine to find the outlet ball",
      "Players inside the grid cannot be on the same pole twice in a row",
      "One-in, one-out"
    ],
    coachingPoints: [
      "Double movements to lose defender",
      "Identifying space/scan",
      "Deception/Disguise - Reverse pass",
      "Receive level or beyond your marker",
      "Touch - Front foot / Back foot",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Main grid area
        { x: 35, y: 25, width: 40, height: 50, color: "rgba(255,255,255,0.1)" },
      ],
      attackers: [
        // Two CBs on left (outside grid)
        { x: 15, y: 35 },
        { x: 15, y: 65 },
        // Three players inside the grid
        { x: 45, y: 40 },
        { x: 70, y: 35 },
        { x: 50, y: 70 },
        // Two outlet players on right
        { x: 85, y: 30 },
        { x: 85, y: 70 },
      ],
      defenders: [],
      cones: [
        // Red poles/markers inside grid
        { x: 40, y: 30, color: "#ef4444" },
        { x: 70, y: 30, color: "#ef4444" },
        { x: 40, y: 70, color: "#ef4444" },
        { x: 70, y: 70, color: "#ef4444" },
      ],
      arrows: [],
      ball: { x: 18, y: 68 },
      lines: [
        // Grid outline
        { startX: 35, startY: 25, endX: 75, endY: 25, style: "solid", color: "white" },
        { startX: 75, startY: 25, endX: 75, endY: 75, style: "solid", color: "white" },
        { startX: 75, startY: 75, endX: 35, endY: 75, style: "solid", color: "white" },
        { startX: 35, startY: 75, endX: 35, endY: 25, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "transfer-rondo",
    title: "Transfer Rondo",
    description: "Practice designed for players to progress into space away from opponent by making a forward run into space and using end player as a set pass.",
    category: "brilliant-basics",
    topics: ["mid-play"],
    duration: "3x3mins",
    players: "10-12",
    areaSize: "40x20m",
    rules: [
      "This practice is designed for players to progress into space away from opponent by making a forward run into space and using end player as a set pass",
      "Ball is always served from the side from a coach to the players on the base line that are set in a back 3-4 shape",
      "After 5 passes a player in possession can progress over the half-way line and play in the opposite half by finding the 'outlet' pass to the player on opposite side",
      "If a blue player loses the ball to a pink player then they will swap teams and that play now becomes a defender"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Top half (20m)
        { x: 10, y: 10, width: 80, height: 35, color: "rgba(255,255,255,0.05)" },
        // Bottom half (40m)
        { x: 10, y: 45, width: 80, height: 45, color: "rgba(255,255,255,0.05)" },
      ],
      // Blue players - 7 in bottom box: pentagon shape with 1 central + 1 in top box
      attackers: [
        // Pentagon outer points (5 players)
        { x: 50, y: 52 },   // Top center
        { x: 25, y: 62 },   // Top left
        { x: 75, y: 62 },   // Top right
        { x: 30, y: 85 },   // Bottom left
        { x: 70, y: 85 },   // Bottom right
        // Central player
        { x: 50, y: 70 },
        // Extra player
        { x: 50, y: 88 },
        // Player in top box
        { x: 50, y: 25 },
      ],
      // Pink defenders - 3 in bottom box
      defenders: [
        { x: 40, y: 65, color: "#EC4899" },
        { x: 60, y: 75, color: "#EC4899" },
        { x: 55, y: 58, color: "#EC4899" },
      ],
      neutrals: [],
      balls: [
        { x: 25, y: 68 },
      ],
      arrows: [],
      goals: [],
      cones: [],
      lines: [
        // Half-way line
        { startX: 10, startY: 45, endX: 90, endY: 45, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "forward-play",
    title: "Forward Play",
    description: "A positioning game focusing on playing through the opponent from the GK or defenders, creating numerical advantages in midfield.",
    category: "positioning-games",
    topics: ["mid-play"],
    duration: "5x3mins",
    players: "12",
    areaSize: "20x40m",
    rules: [
      "The team with the ball will begin the play from the GK or from #3/#4. At the time the team will be set up in shape in order to play through the opponent. Once they are set to play the game begins. If the ball is lost to the opponent, it is open play until the opponent scores or the ball has left the playing area",
      "Remove pressing zones from two sets of players pressing from opponent",
      "Can we score in fewer passes than before (challenge the team)"
    ],
    coachingPoints: [
      "When the ball begins with us from the GK can we immediately engage our #6 to create a numerical advantage in the middle to play through",
      "When we start with the ball and #3 or #4 is used first can the #6 or #8 create a line of pass for them to play through the opponents first line of press",
      "If you cannot make a direct vertical pass, attempt to still make a forward's pass (not backwards) - treat to block the middle area and prevent any forward's pass",
      "When the ball is played out of the back can we play a wider pass in order to drag the first line of press and then play a through pass to our #6, #8 or #10"
    ],
    diagram: {
      type: "full-pitch",
      zones: [
        // Three horizontal zones
        { x: 10, y: 10, width: 80, height: 26, color: "rgba(220, 38, 38, 0.2)" },
        { x: 10, y: 37, width: 80, height: 26, color: "rgba(220, 38, 38, 0.2)" },
        { x: 10, y: 64, width: 80, height: 26, color: "rgba(220, 38, 38, 0.2)" },
      ],
      // Blue attacking players - 2 in bottom zone, 2 in middle zone
      attackers: [
        // Bottom zone
        { x: 35, y: 77 },
        { x: 65, y: 77 },
        // Middle zone
        { x: 35, y: 50 },
        { x: 65, y: 50 },
      ],
      // Red defenders - 3 in top zone, 2 in middle zone, 3 in bottom zone
      defenders: [
        // Top zone
        { x: 25, y: 23 },
        { x: 50, y: 23 },
        { x: 75, y: 23 },
        // Middle zone
        { x: 25, y: 45 },
        { x: 75, y: 55 },
        // Bottom zone - wide players on sideline, central closer to goal
        { x: 10, y: 77 },
        { x: 50, y: 88 },
        { x: 90, y: 77 },
      ],
      neutrals: [],
      ball: { x: 50, y: 82 },
      arrows: [],
      goals: [
        { x: 50, y: 0, width: 14, rotation: 0 },
        { x: 50, y: 100, width: 14, rotation: 180 },
      ],
      cones: [],
      lines: [],
    }
  },
  {
    id: "forward-play-game-training",
    title: "Forward Play Game Training",
    description: "Attack vs Defence game focusing on playing from one side of the back 4 to the other to penetrate through and into a deep pocket.",
    category: "game-training",
    topics: ["mid-play"],
    duration: "2x10 mins",
    players: "18",
    areaSize: "24x40m",
    rules: [
      "Attack vs Defence game",
      "Key here is if we can play from one side of our back 4 to the other, ultimately to penetrate through and into a deep pocket",
      "Immediate attacking transition, by switching the point of attack to penetrate the defensive line of the opponent",
      "When play has progressed over the first zonal line the blues cannot go back over it, this is to prompt play forwards once the ball has been switched (i.e. concept of switch of play to penetrate up and forwards is key here)"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "full-pitch",
      zones: [
        // Three horizontal zones
        { x: 5, y: 10, width: 90, height: 25, color: "rgba(255,255,255,0.05)" },
        { x: 5, y: 35, width: 90, height: 30, color: "rgba(255,255,255,0.05)" },
        { x: 5, y: 65, width: 90, height: 25, color: "rgba(255,255,255,0.05)" },
      ],
      // Blue players
      attackers: [
        // Mid line
        { x: 75, y: 35, label: "9" },
        // Central
        { x: 50, y: 50, label: "8" },
        { x: 30, y: 55, label: "6" },
        { x: 70, y: 55, label: "10" },
        // Forward line
        { x: 10, y: 70, label: "5" },
        { x: 90, y: 70, label: "2" },
        // Attack
        { x: 35, y: 80, label: "3" },
        { x: 65, y: 80, label: "4" },
        // GK at bottom
        { x: 50, y: 95, label: "1" },
      ],
      // Red defenders
      defenders: [
        { x: 50, y: 15, label: "1" },
        // Top zone - moved closer together
        { x: 40, y: 30, label: "3" },
        { x: 60, y: 30, label: "4" },
        { x: 15, y: 35, label: "5" },
        { x: 85, y: 35, label: "2" },
        { x: 55, y: 40, label: "8" },
        { x: 45, y: 55, label: "6" },
        { x: 60, y: 60, label: "10" },
        // Bottom grid - close to white line
        { x: 50, y: 68, label: "9" },
      ],
      neutrals: [],
      ball: { x: 55, y: 52 },
      arrows: [],
      goals: [
        { x: 50, y: 0, width: 14, rotation: 0 },
        { x: 50, y: 100, width: 14, rotation: 180 },
      ],
      cones: [],
      lines: [
        // Zone dividing lines
        { startX: 5, startY: 35, endX: 95, endY: 35, style: "solid", color: "white" },
        { startX: 5, startY: 65, endX: 95, endY: 65, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "first-ball-out-fbo",
    title: "First Ball Out (FBO)",
    description: "4v4+3 transition drill focusing on anticipating loss of possession and defending proactively to stop the opposition's first ball out.",
    category: "brilliant-basics",
    topics: ["transitions"],
    duration: "3x4 mins",
    players: "11",
    areaSize: "15x25m",
    rules: [
      "4v4 with two bouncers and a neutral",
      "Blue in possession try to get ball to the 9",
      "Bouncers are one touch",
      "If reds win the ball, they must quickly dribble or pass across the weak side",
      "If reds do this successfully three times, they swap roles with the blues"
    ],
    coachingPoints: [
      "Can you anticipate when we might lose the ball and defend pro-actively?",
      "How can you stop the opposition from playing their first ball out?",
      "Can you anticipate a loss of possession and block passing lanes for the FBO forward first and then to retain possession?",
      "Can you prepare to protect the space behind?",
      "Can you make the pitch as small as possible quickly?"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 25, y: 75, label: "8" },
        { x: 75, y: 75, label: "7" },
        { x: 30, y: 50, label: "6" },
        { x: 25, y: 25, label: "10" },
      ],
      defenders: [
        { x: 40, y: 55, label: "6" },
        { x: 40, y: 35, label: "10" },
        { x: 60, y: 35, label: "10" },
        { x: 60, y: 55, label: "9" },
      ],
      neutrals: [
        { x: 5, y: 50, label: "9" },
        { x: 95, y: 50, label: "9" },
        { x: 78, y: 25, label: "B", color: "yellow" },
      ],
      ball: { x: 10, y: 50 },
      goals: [
        { x: 50, y: 0, width: 8, rotation: 0 },
        { x: 50, y: 100, width: 8, rotation: 180 },
      ],
      cones: [],
      arrows: [],
      zones: [],
      lines: [],
    }
  },
  {
    id: "first-ball-out-ssg",
    title: "First Ball Out SSG",
    description: "4v3+1 positioning game focusing on transition moments and stopping the opposition from playing their first ball out.",
    category: "positioning-games",
    topics: ["transitions"],
    duration: "4x3 mins",
    players: "12",
    areaSize: "30x60m",
    rules: [
      "4v3+1 in attacking box",
      "If BPO team win the ball they must try to get the ball into their attacking grid",
      "They cannot use bouncers to achieve this",
      "If they score they retain",
      "Always leave one BPO player in the attacking box",
      "A team can only score once they play the bouncer in their attacking grid"
    ],
    coachingPoints: [
      "Can you anticipate when we might lose the ball and defend pro-actively?",
      "How can you stop the opposition from playing their first ball out?",
      "Can you anticipate a loss of possession and block passing lanes for the FBO forward first and then to retain possession?",
      "Can you prepare to protect the space behind?",
      "Can you make the pitch as small as possible quickly?"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 70, y: 25, label: "6" },
        { x: 80, y: 40, label: "10" },
        { x: 70, y: 55, label: "8" },
        { x: 80, y: 70, label: "9" },
      ],
      defenders: [
        { x: 25, y: 50, label: "9" },
        { x: 75, y: 30, label: "10" },
        { x: 75, y: 50, label: "6" },
        { x: 75, y: 70, label: "8" },
      ],
      neutrals: [
        { x: 5, y: 50, label: "B", color: "yellow" },
        { x: 95, y: 50, label: "B", color: "yellow" },
        { x: 42, y: 47, label: "GK", color: "green" },
        { x: 58, y: 53, label: "GK", color: "green" },
      ],
      ball: { x: 70, y: 50 },
      goals: [
        { x: 50, y: 47, width: 12, rotation: 270 },
        { x: 50, y: 53, width: 12, rotation: 90 },
      ],
      cones: [],
      arrows: [],
      zones: [
        { x: 0, y: 0, width: 50, height: 100, color: "rgba(100, 100, 255, 0.1)" },
        { x: 50, y: 0, width: 50, height: 100, color: "rgba(255, 100, 100, 0.1)" },
      ],
      lines: [
        { startX: 50, startY: 0, endX: 50, endY: 100, style: "dashed", color: "white" },
      ],
    }
  },
  {
    id: "first-ball-out-gt",
    title: "First Ball Out PG",
    description: "Positioning game focusing on first ball out transitions, progressing to goal after winning possession.",
    category: "positioning-games",
    topics: ["transitions"],
    duration: "4x4 mins",
    players: "11",
    areaSize: "Box 25x15m",
    rules: [
      "Bouncer to play Red 3/4 who aims to get the ball into the mini goals",
      "Blue win ball look for FBO and progress to big goal",
      "Red look to action reaction and Progress or Possess"
    ],
    coachingPoints: [
      "Can you anticipate when we might lose the ball and defend pro-actively?",
      "How can you stop the opposition from playing their first ball out?",
      "Can you anticipate a loss of possession and block passing lanes for the FBO forward first and then to retain possession?",
      "Can you prepare to protect the space behind?",
      "Can you make the pitch as small as possible quickly?"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 5, y: 45, label: "" },
        { x: 95, y: 45, label: "" },
        { x: 30, y: 60, label: "" },
        { x: 70, y: 65, label: "" },
        { x: 50, y: 75, label: "" },
      ],
      defenders: [
        { x: 35, y: 55, label: "" },
        { x: 65, y: 55, label: "" },
        { x: 45, y: 65, label: "" },
        { x: 55, y: 65, label: "" },
      ],
      neutrals: [
        { x: 50, y: 15, label: "GK", color: "green" },
        { x: 50, y: 90, label: "", color: "yellow" },
      ],
      mannequins: [],
      ball: { x: 50, y: 85 },
      goals: [
        { x: 50, y: 0, width: 14, rotation: 0 },
        { x: 35, y: 90, width: 6, rotation: 180 },
        { x: 65, y: 90, width: 6, rotation: 180 },
      ],
      cones: [
        { x: 5, y: 50 },
        { x: 95, y: 50 },
      ],
      arrows: [
        { startX: 50, startY: 90, endX: 35, endY: 55, type: "pass" },
      ],
      zones: [
        { x: 25, y: 45, width: 50, height: 40, color: "rgba(255, 100, 100, 0.2)" },
      ],
      lines: [
        { startX: 25, startY: 45, endX: 75, endY: 45, style: "dashed", color: "red" },
        { startX: 25, startY: 85, endX: 75, endY: 85, style: "dashed", color: "red" },
        { startX: 25, startY: 45, endX: 25, endY: 85, style: "dashed", color: "red" },
        { startX: 75, startY: 45, endX: 75, endY: 85, style: "dashed", color: "red" },
      ],
    }
  },
  {
    id: "wave-game",
    title: "Wave Game",
    description: "Positioning game focusing on crossing and finishing patterns with 4v4 + GK. Attack transitions through side bouncers into scoring zones.",
    category: "positioning-games",
    topics: ["creating-finishing"],
    duration: "4x3 mins",
    players: "13",
    areaSize: "45x24m",
    rules: [
      "Wave game 3+2 vs 4+GK",
      "Blues play the ball to either side bouncers",
      "Bouncer either plays ball into cut back red zone or yellow crossing zone",
      "Other players attack back post, penalty spot or front post",
      "Defenders attempt to win the ball and score by passing the ball into mini goals",
      "Game then resets and the greens attack"
    ],
    coachingPoints: [
      "Winger 'touch and deliver'",
      "Sacrificial runs",
      "Getting into scoring zones"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 30, y: 35, label: "ST" },
        { x: 50, y: 40, label: "AM" },
        { x: 70, y: 35, label: "ST" },
      ],
      defenders: [
        { x: 35, y: 19, label: "" },
        { x: 45, y: 19, label: "" },
        { x: 55, y: 19, label: "" },
        { x: 65, y: 19, label: "" },
      ],
      neutrals: [
        { x: 50, y: 10, label: "GK", color: "yellow" },
        { x: 18, y: 22, label: "N", color: "yellow" },
        { x: 82, y: 22, label: "N", color: "yellow" },
        { x: 35, y: 48, label: "ST", color: "green" },
        { x: 50, y: 52, label: "AM", color: "green" },
        { x: 65, y: 48, label: "ST", color: "green" },
      ],
      mannequins: [],
      ball: { x: 30, y: 30 },
      goals: [
        { x: 50, y: 0, width: 14, rotation: 0 },
        { x: 25, y: 48, width: 6, rotation: 180 },
        { x: 75, y: 48, width: 6, rotation: 180 },
      ],
      cones: [],
      arrows: [
        { startX: 30, startY: 35, endX: 18, endY: 22, type: "pass" },
        { startX: 18, startY: 22, endX: 35, endY: 10, type: "pass" },
        { startX: 50, startY: 40, endX: 40, endY: 15, type: "movement" },
        { startX: 30, startY: 35, endX: 50, endY: 12, type: "movement" },
        { startX: 70, startY: 35, endX: 60, endY: 12, type: "movement" },
      ],
      zones: [
        { x: 8, y: 12, width: 20, height: 20, color: "rgba(255, 200, 100, 0.4)" },
        { x: 72, y: 12, width: 20, height: 20, color: "rgba(255, 200, 100, 0.4)" },
        { x: 28, y: 5, width: 15, height: 8, color: "rgba(255, 100, 100, 0.3)" },
        { x: 57, y: 5, width: 15, height: 8, color: "rgba(255, 100, 100, 0.3)" },
      ],
      lines: [
        { startX: 5, startY: 50, endX: 95, endY: 50, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "cage-game",
    title: "Cage Game",
    description: "4v4+3 game with unopposed wide areas for wingers/full-backs to create overloads and penetrate.",
    category: "positioning-games",
    topics: ["creating-finishing"],
    duration: "3x6mins",
    players: "13",
    areaSize: "40x30m",
    rules: [
      "4v4+3 Unopposed wide area for wingers/FB's",
      "Two touch in those areas, drive inside to get in-behind",
      "Can then use more than two-touch once driven inside",
      "In Possession: 2-2 / Out of Possession: 2-2"
    ],
    coachingPoints: [
      "Creativity in 1v1 Situations",
      "Drive to Open Space",
      "Draw to Release",
      "Timing of Overlap/Underlap/Takeover",
      "Winger 'touch and deliver'",
      "Sacrificial runs",
      "Getting into scoring zones"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 40, y: 55, label: "" },
        { x: 60, y: 55, label: "" },
        { x: 40, y: 70, label: "" },
        { x: 60, y: 70, label: "" },
      ],
      defenders: [
        { x: 45, y: 45, label: "" },
        { x: 55, y: 45, label: "" },
        { x: 45, y: 60, label: "" },
        { x: 55, y: 60, label: "" },
      ],
      neutrals: [
        { x: 50, y: 12, label: "GK", color: "white" },
        { x: 50, y: 88, label: "GK", color: "white" },
        { x: 12, y: 50, label: "", color: "green" },
        { x: 88, y: 35, label: "", color: "green" },
        { x: 50, y: 50, label: "", color: "green" },
      ],
      mannequins: [],
      ball: { x: 65, y: 52 },
      goals: [
        { x: 50, y: 5, width: 14, rotation: 0 },
        { x: 50, y: 95, width: 14, rotation: 180 },
      ],
      cones: [],
      arrows: [
        { startX: 60, startY: 55, endX: 88, endY: 35, type: "pass" },
        { startX: 88, startY: 35, endX: 60, endY: 30, type: "pass" },
        { startX: 40, startY: 55, endX: 50, endY: 25, type: "movement" },
      ],
      zones: [
        { x: 0, y: 25, width: 18, height: 50, color: "rgba(255, 200, 100, 0.4)" },
        { x: 82, y: 25, width: 18, height: 50, color: "rgba(255, 200, 100, 0.4)" },
      ],
      lines: [],
    }
  },
  {
    id: "pass-and-move",
    title: "Pass and Move",
    description: "3 team rondo across two boxes focusing on movement to support and connect play between zones.",
    category: "brilliant-basics",
    topics: ["mid-play"],
    duration: "4x2mins",
    players: "6",
    areaSize: "10x10m",
    rules: [
      "Play always begins in one half with this 3 team rondo",
      "Players are locked in their own half",
      "Last team to lose possession presses the ball carrier",
      "To progress, remove the halfway line to increase resistance"
    ],
    coachingPoints: [
      "Provide movement to give your teammate a line of pass",
      "Connect play and combine around opposition",
      "Support around the ball or away from the ball",
      "Movement away from defender to exploit numerical advantage"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 75, y: 25, label: "" },
        { x: 75, y: 75, label: "" },
      ],
      defenders: [
        { x: 45, y: 20, label: "" },
        { x: 45, y: 60, label: "" },
      ],
      neutrals: [
        { x: 25, y: 30, label: "", color: "yellow" },
        { x: 25, y: 70, label: "", color: "yellow" },
      ],
      mannequins: [],
      ball: { x: 75, y: 70 },
      goals: [],
      cones: [],
      arrows: [
        { startX: 75, startY: 75, endX: 75, endY: 25, type: "pass" },
      ],
      zones: [],
      lines: [
        { startX: 10, startY: 50, endX: 90, endY: 50, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "atk-transition",
    title: "ATK Transition",
    description: "4v4+2 positioning game focusing on immediate attacking transition when the ball is won.",
    category: "positioning-games",
    topics: ["transitions"],
    duration: "4x4mins",
    players: "12",
    areaSize: "20x40m",
    rules: [
      "4v4+2 positioning game with goals",
      "When ball is won (attacking transition) score in the direction you won the ball",
      "First option must be to pass forwards",
      "Look for a vertical pass first",
      "If no direct forward pass, pass across but still forwards",
      "If cannot go forwards, retain possession with a pass backwards"
    ],
    coachingPoints: [
      "When we win the ball get into a passing lane quickly",
      "If you cannot provide a direct passing line, make a forwards run",
      "Move away from your direct opponent to make another line behind",
      "Switch the point of attack to penetrate defensive line"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [],
      defenders: [
        { x: 50, y: 28, label: "" },
        { x: 25, y: 50, label: "" },
        { x: 75, y: 50, label: "" },
        { x: 50, y: 72, label: "" },
        { x: 50, y: 15, label: "" },
        { x: 50, y: 85, label: "" },
      ],
      neutrals: [
        { x: 40, y: 40, label: "", color: "yellow" },
        { x: 60, y: 40, label: "", color: "yellow" },
        { x: 40, y: 60, label: "", color: "yellow" },
        { x: 60, y: 60, label: "", color: "yellow" },
        { x: 30, y: 15, label: "", color: "yellow" },
        { x: 70, y: 85, label: "", color: "yellow" },
      ],
      mannequins: [],
      ball: { x: 70, y: 50 },
      goals: [],
      cones: [],
      arrows: [],
      zones: [
        { x: 5, y: 5, width: 90, height: 20, color: "rgba(255, 255, 255, 0.15)" },
        { x: 5, y: 25, width: 90, height: 50, color: "rgba(255, 255, 255, 0.08)" },
        { x: 5, y: 75, width: 90, height: 20, color: "rgba(255, 255, 255, 0.15)" },
      ],
      lines: [
        { startX: 5, startY: 25, endX: 95, endY: 25, style: "solid", color: "white" },
        { startX: 5, startY: 75, endX: 95, endY: 75, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "action-reaction-box",
    title: "Action Reaction Box",
    description: "6v2 rondo with FBO transition to 3v0 grid, focusing on anticipating loss of possession.",
    category: "brilliant-basics",
    topics: ["transitions"],
    duration: "5x2mins",
    players: "11",
    areaSize: "18x24m",
    rules: [
      "6v2 in half grid, 3v0 in the other grid",
      "8 passes is a goal",
      "Defending team win the ball and FBO to their 3v0",
      "REP 1+2 as above, REP 3+4+5 progress to 6v3"
    ],
    coachingPoints: [
      "Anticipate when we might lose the ball and defend pro-actively",
      "Stop the opposition from playing their first ball out",
      "Block passing lanes for the FBO forward first",
      "Prepare to protect the space behind",
      "Make the pitch as small as possible quickly"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 20, y: 20, label: "6" },
        { x: 12, y: 45, label: "3" },
        { x: 35, y: 35, label: "9" },
        { x: 28, y: 72, label: "10" },
        { x: 12, y: 88, label: "8" },
      ],
      defenders: [
        // Left grid reds (2)
        { x: 28, y: 55, label: "9" },
        { x: 40, y: 68, label: "10" },
        // Right grid reds (3)
        { x: 75, y: 25, label: "11" },
        { x: 65, y: 55, label: "7" },
        { x: 85, y: 70, label: "8" },
      ],
      neutrals: [
        // Single yellow in left grid
        { x: 42, y: 45, label: "N", color: "yellow" },
      ],
      mannequins: [],
      ball: { x: 18, y: 50 },
      goals: [],
      cones: [],
      arrows: [],
      zones: [
        { x: 5, y: 5, width: 45, height: 90, color: "rgba(255, 255, 255, 0.08)" },
        { x: 50, y: 5, width: 45, height: 90, color: "rgba(255, 255, 255, 0.08)" },
      ],
      lines: [
        { startX: 50, startY: 5, endX: 50, endY: 95, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "action-reaction-wave",
    title: "Action Reaction Wave",
    description: "3v2 into 1v1 transition drill focusing on recovery and delaying the ball carrier.",
    category: "positioning-games",
    topics: ["transitions"],
    duration: "3x4mins",
    players: "14",
    areaSize: "15x25m (5m 1v1 zones)",
    rules: [
      "3v2 into 1v1 transition drill",
      "3v2 attacking wave",
      "Once there is a shot, 2 players dribble into 1v1 zones",
      "Any two of the 3 attackers need to recover and delay/force ball carrier wide"
    ],
    coachingPoints: [
      "Closest player presses the ball carrier",
      "Force the attacker wide or backwards",
      "Wedge yourself between player and ball",
      "Find FBO and go forward",
      "Prepare to protect the space behind",
      "Make the pitch as small as possible quickly"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 35, y: 50, label: "" },
        { x: 50, y: 50, label: "" },
        { x: 65, y: 50, label: "" },
        { x: 10, y: 92, label: "" },
        { x: 75, y: 95, label: "" },
        { x: 90, y: 92, label: "" },
      ],
      defenders: [
        { x: 40, y: 19, label: "" },
        { x: 60, y: 19, label: "" },
        { x: 10, y: 8, label: "" },
        { x: 35, y: 1, label: "" },
        { x: 65, y: 1, label: "" },
        { x: 90, y: 8, label: "" },
      ],
      neutrals: [
        { x: 50, y: 5, label: "GK", color: "yellow" },
        { x: 50, y: 95, label: "GK", color: "yellow" },
      ],
      mannequins: [],
      balls: [
        { x: 52, y: 45 },
        { x: 10, y: 15 },
        { x: 90, y: 15 },
      ],
      goals: [
        { x: 50, y: 2, width: 14, rotation: 0 },
        { x: 50, y: 98, width: 14, rotation: 180 },
        { x: 10, y: 2, width: 6, rotation: 0 },
        { x: 90, y: 2, width: 6, rotation: 0 },
        { x: 10, y: 50, width: 6, rotation: 180 },
        { x: 90, y: 50, width: 6, rotation: 180 },
      ],
      cones: [],
      arrows: [],
      zones: [
        { x: 0, y: 5, width: 20, height: 90, color: "rgba(255, 255, 255, 0.15)" },
        { x: 80, y: 5, width: 20, height: 90, color: "rgba(255, 255, 255, 0.15)" },
      ],
      lines: [
        { startX: 20, startY: 5, endX: 20, endY: 95, style: "solid", color: "white" },
        { startX: 80, startY: 5, endX: 80, endY: 95, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "out-and-forward",
    title: "Out and Forward",
    description: "Transition positioning game with 3v3+1 in one half and 2v2 in the other, focusing on anticipating possession loss and defending proactively.",
    category: "positioning-games",
    topics: ["transitions"],
    duration: "3x4mins",
    players: "13",
    areaSize: "40x30m",
    rules: [
      "3v3+1 in one half from half way",
      "2v2 in the other box",
      "BP team can look to score after x3 passes and action reaction immediately if they lose possession",
      "BPO team must win the ball and score in the opposing goal"
    ],
    coachingPoints: [
      "Anticipate when we might lose the ball and defend pro-actively",
      "Stop the opposition from playing their first ball out",
      "Anticipate loss of possession and block passing lanes for FBO forward first",
      "Prepare to protect the space behind",
      "Make the pitch as small as possible quickly"
    ],
    diagram: {
      type: "full-pitch-wide-goals",
      attackers: [
        // Left side 2v2 attackers
        { x: 25, y: 55, label: "9" },
        { x: 22, y: 45, label: "10" },
        // Right side attackers
        { x: 70, y: 55, label: "6" },
        { x: 85, y: 45, label: "3" },
        { x: 82, y: 60, label: "2" },
      ],
      defenders: [
        // Left side GK and defenders
        { x: 5, y: 50, label: "1", color: "yellow" },
        { x: 20, y: 65, label: "4" },
        { x: 18, y: 40, label: "3" },
        // Right side defenders
        { x: 75, y: 60, label: "10" },
        { x: 80, y: 70, label: "7" },
        { x: 78, y: 40, label: "9" },
        { x: 95, y: 50, label: "1", color: "yellow" },
      ],
      neutrals: [
        { x: 55, y: 50, label: "N", color: "yellow" },
      ],
      mannequins: [],
      ball: { x: 60, y: 50 },
      goals: [
        { x: 5, y: 50, width: 20, rotation: 90 },
        { x: 95, y: 50, width: 20, rotation: 270 },
      ],
      cones: [],
      arrows: [],
      zones: [],
      lines: [
        { startX: 50, startY: 5, endX: 50, endY: 95, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "action-reaction-ssg",
    title: "Action Reaction SSG",
    description: "3v3+1 possession game in central area with transition to goal scoring opportunities.",
    category: "positioning-games",
    topics: ["transitions"],
    duration: "3x4mins",
    players: "13",
    areaSize: "Box to box",
    rules: [
      "3v3+1 in central area",
      "If team completes x5 passes they can break out and score",
      "If BPO team win the ball they can FBO and look to score",
      "Play resets at opposite team after each transition"
    ],
    coachingPoints: [
      "Anticipate when we might lose the ball and defend pro-actively",
      "Stop the opposition from playing their first ball out",
      "Anticipate loss of possession and block passing lanes for FBO forward first",
      "Prepare to protect the space behind",
      "Make the pitch as small as possible quickly"
    ],
    diagram: {
      type: "full-pitch-wide-goals",
      attackers: [
        { x: 25, y: 60, label: "4" },
        { x: 45, y: 38, label: "9" },
        { x: 48, y: 55, label: "3" },
        { x: 48, y: 68, label: "6" },
        { x: 80, y: 30, label: "11" },
      ],
      defenders: [
        { x: 25, y: 30, label: "7" },
        { x: 58, y: 38, label: "9" },
        { x: 62, y: 55, label: "10" },
        { x: 62, y: 68, label: "8" },
        { x: 80, y: 50, label: "4" },
      ],
      neutrals: [
        { x: 8, y: 50, label: "1", color: "yellow" },
        { x: 92, y: 50, label: "1", color: "yellow" },
        { x: 55, y: 50, label: "N", color: "yellow" },
      ],
      mannequins: [],
      ball: { x: 50, y: 62 },
      goals: [
        { x: 5, y: 50, width: 20, rotation: 90 },
        { x: 95, y: 50, width: 20, rotation: 270 },
      ],
      cones: [],
      arrows: [],
      zones: [
        { x: 40, y: 28, width: 30, height: 50, color: "rgba(0, 100, 255, 0.15)" },
      ],
      lines: [
        { startX: 50, startY: 5, endX: 50, endY: 95, style: "solid", color: "white" },
      ],
    }
  },
  {
    id: "action-reaction-game",
    title: "Action Reaction Game",
    description: "5v5+1 regular game focusing on anticipating possession loss and proactive defending.",
    category: "game-training",
    topics: ["transitions"],
    duration: "2x10mins",
    players: "12",
    areaSize: "40x30m",
    rules: [
      "5v5+1",
      "Regular game",
      "Resets at keepers"
    ],
    coachingPoints: [
      "Anticipate when we might lose the ball and defend pro-actively",
      "Stop the opposition from playing their first ball out",
      "Anticipate loss of possession and block passing lanes for FBO forward first",
      "Prepare to protect the space behind",
      "Make the pitch as small as possible quickly"
    ],
    diagram: {
      type: "full-pitch-wide-goals",
      attackers: [
        { x: 12, y: 50, label: "3" },
        { x: 25, y: 25, label: "4" },
        { x: 55, y: 40, label: "9" },
        { x: 68, y: 50, label: "10" },
        { x: 35, y: 75, label: "2" },
      ],
      defenders: [
        { x: 60, y: 28, label: "8" },
        { x: 52, y: 45, label: "9" },
        { x: 80, y: 40, label: "4" },
        { x: 55, y: 58, label: "10" },
        { x: 80, y: 55, label: "3" },
        { x: 95, y: 50, label: "1", color: "yellow" },
      ],
      neutrals: [
        { x: 5, y: 50, label: "1", color: "yellow" },
        { x: 75, y: 75, label: "N", color: "yellow" },
      ],
      mannequins: [],
      ball: { x: 15, y: 50 },
      goals: [
        { x: 5, y: 50, width: 20, rotation: 90 },
        { x: 95, y: 50, width: 20, rotation: 270 },
      ],
      cones: [],
      arrows: [],
      zones: [],
      lines: [
        { startX: 50, startY: 5, endX: 50, endY: 95, style: "solid", color: "white" },
        { startX: 30, startY: 5, endX: 30, endY: 95, style: "solid", color: "rgba(0, 100, 255, 0.6)" },
        { startX: 80, startY: 5, endX: 80, endY: 95, style: "solid", color: "rgba(0, 100, 255, 0.6)" },
      ],
    }
  },
  {
    id: "10-specific-box",
    title: "#10 Specific Box",
    description: "4v2+1 drill focused on the #10 role. Team in BP scores by transferring the ball through the central #10 player to the opposite grid. The #10 must receive on back foot and play forward.",
    category: "brilliant-basics",
    topics: ["building", "mid-play"],
    duration: "4x2mins",
    players: "7",
    areaSize: "30x15m",
    rules: [
      "Team in BP scores a goal by transferring the ball from one end of the grid, into the opposite grid",
      "Defenders score if they are able to win the ball and dribble it to the outside of the grid",
      "The ball must be played to the opposite grid through the N who operates in the central zone and cannot be played directly to the opposite side",
      "Defenders and attackers swap over every two minutes",
      "Player in central area acts as a #10 and must receive ball on back foot and play the ball forward to opposite side of grid",
      "#10 may pass to either of the two options at the end of the grid"
    ],
    coachingPoints: [
      "Movement into Pocket",
      "Timing",
      "Type",
      "Sacrificial Movements to Drag Defenders",
      "Support",
      "Runs Beyond/Support Underneath"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 20, y: 30 },
        { x: 20, y: 70 },
        { x: 80, y: 30 },
        { x: 80, y: 70 },
      ],
      defenders: [
        { x: 33, y: 40 },
        { x: 67, y: 60 },
      ],
      neutrals: [
        { x: 50, y: 50 },
      ],
      ball: { x: 25, y: 35 },
      zones: [
        { x: 5, y: 10, width: 28, height: 80, color: "rgba(255,255,255,0.05)" },
        { x: 36, y: 10, width: 28, height: 80, color: "rgba(255,255,255,0.08)" },
        { x: 67, y: 10, width: 28, height: 80, color: "rgba(255,255,255,0.05)" },
      ],
      lines: [
        { startX: 33, startY: 10, endX: 33, endY: 90, style: "dashed", color: "rgba(255,255,255,0.5)" },
        { startX: 67, startY: 10, endX: 67, endY: 90, style: "dashed", color: "rgba(255,255,255,0.5)" },
      ],
    }
  },
  {
    id: "between-lines",
    title: "Between Lines",
    description: "Blues aim to play through the defensive block of 2-2-2 to score in either of the two far goals. Red defenders are locked to their lines but can shuffle left and right.",
    category: "positioning-games",
    topics: ["building", "mid-play"],
    duration: "4x4mins",
    players: "13",
    areaSize: "20x32m",
    rules: [
      "Reds aim to play through the defensive block of 2-2-2 to score in either of the two far goals",
      "Blue defenders are locked to their lines but can shuffle left and right",
      "Only when the ball enters the middle third can the defenders press and cover",
      "Blues need to provide support underneath and runs beyond to score",
      "Offside rule applies",
      "Game always restarts from the GK",
      "Rotate 4 outfield players at the end of each block"
    ],
    coachingPoints: [
      "Movement into Pocket",
      "Timing",
      "Type",
      "Sacrificial Movements to Drag Defenders",
      "Support",
      "Runs Beyond/Support Underneath"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 30, y: 25 },
        { x: 70, y: 25 },
        { x: 25, y: 45 },
        { x: 75, y: 45 },
        { x: 30, y: 65 },
        { x: 70, y: 65 },
      ],
      defenders: [
        { x: 35, y: 35 },
        { x: 65, y: 35 },
        { x: 35, y: 52 },
        { x: 65, y: 52 },
        { x: 35, y: 70 },
        { x: 65, y: 70 },
      ],
      neutrals: [
        { x: 50, y: 12, label: "GK" },
      ],
      ball: { x: 50, y: 18 },
      goals: [
        { x: 50, y: 5, width: 12, rotation: 0 },
        { x: 30, y: 95, width: 10, rotation: 180 },
        { x: 70, y: 95, width: 10, rotation: 180 },
      ],
      lines: [
        { startX: 10, startY: 40, endX: 90, endY: 40, style: "dashed", color: "rgba(255,150,100,0.6)" },
        { startX: 10, startY: 58, endX: 90, endY: 58, style: "dashed", color: "rgba(255,150,100,0.6)" },
        { startX: 10, startY: 75, endX: 90, endY: 75, style: "dashed", color: "rgba(255,150,100,0.6)" },
      ],
    }
  },
  {
    id: "between-lines-game-training",
    title: "Between Lines - Game Training",
    description: "Game Training attack vs defence. Blues aim to maintain possession until the opportunity arises to play between the lines and create in central areas. Reds aim to win the ball back and score in either of the three mini goals.",
    category: "game-training",
    topics: ["mid-play"],
    duration: "3x10mins",
    players: "13",
    areaSize: "Half Pitch",
    rules: [
      "BP - 2 - 4, BPO - 4 - 2",
      "Ball always starts with Red CB/6",
      "Reds aim is to maintain possession until the opportunity arises to play between the lines and create in central areas",
      "Blues aim is to win the ball back and score in either of the three mini goals",
      "Blues are instructed not to press and remain in their block",
      "Flat discs will be placed in the starting positions for reset",
      "2 x 10 Minutes + 10 Minutes Open Game (Restarts from Throw In For)",
      "Goal Scored = 1 Point"
    ],
    coachingPoints: [
      "Patience in Possession",
      "Wait for Moment when Pocket is open",
      "Different ways of entering the pocket",
      "Defensive Shape of Reds",
      "6s: Pass into Pocket, Support Underneath",
      "10s: Movement into Pocket - Timing/Type, Always one 10 providing lengthening pitch",
      "7/11s: Coming inside into Pocket, Runs beyond when Open Ball Occurs"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 20, y: 28 },
        { x: 40, y: 28 },
        { x: 60, y: 28 },
        { x: 80, y: 28 },
        { x: 35, y: 42 },
        { x: 65, y: 42 },
      ],
      defenders: [
        { x: 25, y: 35 },
        { x: 45, y: 35 },
        { x: 55, y: 35 },
        { x: 75, y: 35 },
        { x: 40, y: 72 },
        { x: 60, y: 72 },
      ],
      neutrals: [
        { x: 50, y: 10, label: "GK" },
      ],
      ball: { x: 40, y: 66 },
      goals: [
        { x: 50, y: 3, width: 12, rotation: 0 },
        { x: 25, y: 95, width: 8, rotation: 180 },
        { x: 50, y: 95, width: 8, rotation: 180 },
        { x: 75, y: 95, width: 8, rotation: 180 },
      ],
      zones: [
        { x: 30, y: 3, width: 40, height: 18, color: "rgba(100, 200, 100, 0.3)" },
      ],
    }
  },
  {
    id: "mid-to-beyond-gt",
    title: "Mid to Beyond",
    description: "Ball starts with Blue 6. Objective is to score a goal by playing a pass or running with the ball into the no defending zone to score a goal. Reds must stay compact and win ball and play a pass into one of the three blue goals within 10 seconds.",
    category: "game-training",
    topics: ["mid-play"],
    duration: "2x10mins",
    players: "11",
    areaSize: "4m in from each sideline",
    rules: [
      "Ball starts with Blue 6",
      "Objective is to score a goal by playing a pass or running with the ball into the no defending zone",
      "Reds must stay compact and win ball",
      "If Reds win possession, they must play a pass into one of the three blue goals within 10 seconds"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "full-pitch-no-bottom-box",
      zones: [
        // No Defending Zone near goal
        { x: 15, y: 5, width: 70, height: 25, color: "rgba(255,200,200,0.3)" }
      ],
      attackers: [
        // Blue players
        { x: 18, y: 35, label: "11" },
        { x: 65, y: 60, label: "6" },
        { x: 54, y: 42, label: "9" },
        { x: 35, y: 55, label: "10" },
        { x: 75, y: 50, label: "8" },
        { x: 92, y: 35, label: "7" }
      ],
      defenders: [
        // Red GK
        { x: 50, y: 10, label: "1" },
        // Red defenders - centered just below the red dotted line
        { x: 35, y: 35, label: "2" },
        { x: 50, y: 33, label: "3" },
        { x: 55, y: 50, label: "6" },
        { x: 58, y: 33, label: "4" },
        { x: 70, y: 35, label: "5" },
        { x: 45, y: 48, label: "8" }
      ],
      neutrals: [],
      ball: { x: 65, y: 56 },
      cones: [],
      arrows: [],
      goals: [
        // Main goal at top
        { x: 50, y: 3, width: 12, rotation: 0 },
        // Three blue mini goals just below blue 6
        { x: 20, y: 68, width: 8, rotation: 180 },
        { x: 50, y: 68, width: 8, rotation: 180 },
        { x: 80, y: 68, width: 8, rotation: 180 }
      ],
      lines: [
        // No defending zone dashed border
        { startX: 15, startY: 30, endX: 85, endY: 30, style: "dashed", color: "rgba(255,100,100,0.6)" },
        { startX: 15, startY: 5, endX: 15, endY: 30, style: "dashed", color: "rgba(255,100,100,0.6)" },
        { startX: 85, startY: 5, endX: 85, endY: 30, style: "dashed", color: "rgba(255,100,100,0.6)" }
      ]
    }
  },
  {
    id: "ball-out-weak-side-box",
    title: "Ball Out Weak Side Box",
    description: "2v2/3v3 with three bouncers. Blue in possession try to get ball to the 9. If reds win the ball, they must quickly dribble or pass across the weak side.",
    category: "brilliant-basics",
    topics: ["transitions"],
    duration: "3x3mins",
    players: "7-9",
    areaSize: "15x20m",
    rules: [
      "2v2/3v3 with three bouncers",
      "Blue in possession try to get ball to the 9",
      "Bouncers are one touch",
      "9 is restricted to move between the white area",
      "If reds win the ball, they must quickly dribble the ball or pass the ball across the weak side",
      "If reds do this successfully three times, they swap roles with the blues"
    ],
    coachingPoints: [
      "Progression vs Possession",
      "Hit highest point of pass as quickly as possible (1st out and forward)",
      "Press cover",
      "Angle of approach"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 55, y: 15, width: 35, height: 70, color: "rgba(239, 68, 68, 0.2)" },
      ],
      attackers: [
        { x: 65, y: 40 },
        { x: 80, y: 65 },
      ],
      defenders: [
        { x: 75, y: 35 },
        { x: 65, y: 60 },
      ],
      neutrals: [
        { x: 75, y: 18, color: "#F59E0B" },
        { x: 90, y: 50, color: "#F59E0B" },
        { x: 72, y: 85, color: "#F59E0B" },
      ],
      ball: { x: 65, y: 35 },
      arrows: [
        { startX: 65, startY: 40, endX: 75, endY: 18, type: "pass" },
      ],
      goals: [
        { x: 75, y: 12, width: 16, rotation: 0 },
      ],
      cones: [],
    }
  },
  {
    id: "quick-counters",
    title: "Quick Counters",
    description: "4v4 positioning game with wide players creating counter-attacking options. Wide players must stay in opposite half to create outlet for defending team when winning possession.",
    category: "positioning-games",
    topics: ["transitions"],
    duration: "4x4mins",
    players: "10",
    areaSize: "40x30m",
    rules: [
      "The players in the wide positions must not be in the same half as the players playing 4v4, creating a wide option if and when the defending team wins the ball",
      "If the ball is won by the defending team, they must attempt to play to one of the wide players and support the half they are attacking, allowing the wide player to cross, creating a counter-attacking situation",
      "Once the wide players play inside, they must move to the opposite half creating an option for the defending team again",
      "If the defending team recovers well and the attacking team cannot counter attack quickly, the attacking team can keep possession and try to score a goal with combination play"
    ],
    coachingPoints: [
      "Progression vs Possession",
      "Hit highest point of pass as quickly as possible (1st out and forward)",
      "Press cover",
      "Angle of approach"
    ],
    diagram: {
      type: "small-grid",
      zones: [],
      attackers: [
        { x: 50, y: 52 },
        { x: 22, y: 72 },
        { x: 78, y: 72 },
        { x: 50, y: 95 },
      ],
      defenders: [
        { x: 42, y: 65 },
        { x: 58, y: 65 },
        { x: 42, y: 80 },
        { x: 58, y: 80 },
      ],
      neutrals: [
        { x: 10, y: 30, color: "#10B981" },
        { x: 90, y: 30, color: "#10B981" },
      ],
      ball: { x: 50, y: 56 },
      arrows: [],
      goals: [
        { x: 25, y: 5, width: 12, rotation: 0 },
        { x: 75, y: 5, width: 12, rotation: 0 },
        { x: 25, y: 95, width: 12, rotation: 180 },
        { x: 75, y: 95, width: 12, rotation: 180 },
      ],
      cones: [],
      lines: [
        { startX: 0, startY: 50, endX: 100, endY: 50, style: "dashed", color: "#EAB308" },
      ],
    }
  },
  {
    id: "fast-finishing",
    title: "Fast Finishing",
    description: "Red side player plays to any green player creating a 3v2. The passer can then run around the closest mini goal to create a 3v3.",
    category: "positioning-games",
    topics: ["creating-finishing"],
    duration: "4x3mins",
    players: "10-12",
    areaSize: "40x30m",
    rules: [
      "Red side player plays to any blue player creating a 3v2",
      "The passer can then run around the closest mini goal to create a 3v3",
      "Score side netting if needed",
      "Block 1 - 3v2",
      "Block 2 - 4v3",
      "Block 3 - 5v4"
    ],
    coachingPoints: [
      "Attacking principles recap: Cutback, touch deliver, FP, Pen, BP, active def",
      "Reach furthest forward player if on, drive if not or secure possession if not",
      "Support under over"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 30, y: 5, width: 40, height: 18, color: "rgba(255,255,255,0.1)" },
        { x: 10, y: 20, width: 80, height: 45, color: "rgba(253, 224, 71, 0.3)" },
      ],
      attackers: [
        { x: 15, y: 70 },
        { x: 38, y: 70 },
        { x: 62, y: 70 },
        { x: 15, y: 80 },
        { x: 38, y: 80 },
        { x: 62, y: 80 },
      ],
      defenders: [
        { x: 25, y: 8 },
        { x: 40, y: 30 },
        { x: 60, y: 30 },
        { x: 75, y: 8 },
        { x: 92, y: 45 },
      ],
      neutrals: [
        { x: 50, y: 12, color: "#10B981" },
      ],
      ball: { x: 88, y: 50 },
      arrows: [
        { startX: 88, startY: 50, endX: 60, endY: 70, type: "pass", curved: true, curveDirection: 1 },
        { startX: 88, startY: 50, endX: 85, endY: 65, type: "movement", curved: true, curveDirection: -1 },
      ],
      goals: [
        { x: 50, y: 5, width: 16, rotation: 0 },
        { x: 27, y: 65, width: 10, rotation: 0 },
        { x: 50, y: 65, width: 10, rotation: 0 },
        { x: 74, y: 65, width: 10, rotation: 0 },
      ],
      cones: [],
      lines: [
        { startX: 5, startY: 65, endX: 95, endY: 65, style: "dotted", color: "white" },
      ],
    }
  },
  {
    id: "positioning-pass-forward-run-forward",
    title: "Pass Forward Run Forward",
    description: "6v6 positioning game with zonal restrictions that releases attackers on forward passes to create overloads.",
    category: "positioning-games",
    topics: ["building"],
    duration: "2x8mins",
    players: "12",
    areaSize: "40x40m",
    rules: [
      "6v6 game - teams work to score in opponent's goals",
      "Players are stuck in zones with the exception of a forward pass",
      "An attacker may join the attack when a forward pass is made",
      "Block 1 - 2x8mins"
    ],
    coachingPoints: [
      "Double movements to lose defender",
      "Identifying space/scan",
      "Deception/Disguise - Reverse Pass",
      "Receive level or beyond your marker",
      "Touch - Front foot / Back foot",
      "Playing to your teammate's advantage",
      "No two players on same line",
      "Invert into space when available",
      "6 Drop and attract / Other high (diff lines)",
      "Support - Underneath / Beyond",
      "Drag them ball side then exploit (diag)",
      "Up, Back, Through"
    ],
    diagram: {
      type: "custom",
      zones: [
        { x: 25, y: 5, width: 50, height: 30, color: "rgba(255, 230, 150, 0.5)" },
        { x: 25, y: 65, width: 50, height: 30, color: "rgba(255, 230, 150, 0.5)" },
      ],
      attackers: [
        // Top zone - 2 reds
        { x: 50, y: 25 },
        { x: 70, y: 35 },
        // Bottom zone - 3 reds
        { x: 30, y: 60 },
        { x: 70, y: 75 },
        { x: 50, y: 75 },
      ],
      defenders: [
        // Top zone - 3 blues
        { x: 30, y: 15 },
        { x: 70, y: 15 },
        { x: 50, y: 40 },
        // Bottom zone - 2 blues
        { x: 40, y: 70 },
        { x: 60, y: 60 },
      ],
      neutrals: [
        // Yellow GKs
        { x: 50, y: 8, color: "#FBBF24" },
        { x: 50, y: 92, color: "#FBBF24" },
      ],
      ball: { x: 72, y: 22 },
      arrows: [],
      goals: [
        { x: 50, y: 0, width: 12, rotation: 0 },
        { x: 50, y: 100, width: 12, rotation: 0 },
      ],
      cones: [],
      lines: [
        { startX: 5, startY: 50, endX: 95, endY: 50, style: "dotted", color: "white" },
      ],
    }
  },
  {
    id: "build-up-ssg",
    title: "Build Up SSG",
    description: "Small-sided game focusing on building out from the back with zonal progression. 4v3 in starting zone, 2v2 in central zone.",
    category: "positioning-games",
    topics: ["building"],
    duration: "2x8mins",
    players: "12",
    areaSize: "40x30m",
    rules: [
      "The set up of the practice is split into two zones",
      "Attacking team begin 4v3 in the starting zone and are 2v2 in the central zone",
      "The objective for the attacking team is to play into, around or over the defending team",
      "Only one player from the attacking team can enter the central zone to support progress of the ball",
      "Emphasis on movement to receive, support runs, timing of run and decision making to play forward",
      "Block 1 (2x8mins): +1 attacker on broken line",
      "Block 2: Freedom for all to cross zones after line is broken"
    ],
    coachingPoints: [
      "Double Movements to Lose Defender",
      "Identifying Space/Scan",
      "Deception/Disguise - Reverse Pass",
      "Receive level or beyond your marker",
      "Touch - Front foot / Back foot",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line",
      "Invert into space when available",
      "6 Drop and attract / Other high (diff lines)",
      "Support - Underneath / Beyond",
      "Drag them ball side then exploit (diag)",
      "Up, Back, Through"
    ],
    diagram: {
      type: "custom",
      zones: [
        { x: 10, y: 5, width: 80, height: 45, color: "rgba(255, 230, 150, 0.4)" },
        { x: 10, y: 50, width: 80, height: 45, color: "rgba(255, 230, 150, 0.4)" }
      ],
      attackers: [
        { x: 30, y: 80, label: "" },
        { x: 70, y: 80, label: "" },
        { x: 50, y: 70, label: "" },
        { x: 50, y: 35, label: "" },
        { x: 75, y: 35, label: "" }
      ],
      defenders: [
        { x: 35, y: 65, label: "" },
        { x: 65, y: 65, label: "" },
        { x: 50, y: 58, label: "" },
        { x: 40, y: 30, label: "" },
        { x: 60, y: 30, label: "" }
      ],
      neutrals: [
        { x: 50, y: 10, label: "GK" },
        { x: 50, y: 90, label: "GK" }
      ],
      goals: [
        { x: 50, y: 5, width: 12, rotation: 0 },
        { x: 50, y: 95, width: 12, rotation: 0 }
      ],
      ball: { x: 50, y: 85 },
      lines: [
        { startX: 10, startY: 50, endX: 90, endY: 50, style: "dashed", color: "black" },
        { startX: 10, startY: 25, endX: 90, endY: 25, style: "dashed", color: "black" }
      ],
      arrows: [],
      cones: []
    }
  },
  {
    id: "drive-to-commit-box",
    title: "Drive to Commit Box",
    description: "3v3 zonal progression game focusing on driving with the ball to commit defenders and unlock the next zone.",
    category: "brilliant-basics",
    topics: ["building"],
    duration: "4x3mins",
    players: "6",
    areaSize: "10x18m",
    rules: [
      "Players must move ball from one end line to the other",
      "Players must RWTB over each line",
      "Players in BP can move into the next zone",
      "BPO players are locked into their zones",
      "Must RWTB over end line to score",
      "Players swap roles after every 3mins"
    ],
    coachingPoints: [
      "Can we circulate ball in back half until a CB can inject into the next third?",
      "When RWB can ball carrier keep the ball on furthest foot from opponent?",
      "As CB is injecting into next half, can the CM move away from the ball to create space?",
      "Can CB use disguise when attracting opponent and passing to midfielder?",
      "Can CM stay away from CB during buildup?"
    ],
    diagram: {
      type: "custom",
      zones: [
        { x: 10, y: 5, width: 80, height: 30, color: "rgba(0, 128, 0, 0.3)" },
        { x: 10, y: 35, width: 80, height: 30, color: "rgba(0, 128, 0, 0.3)" },
        { x: 10, y: 65, width: 80, height: 30, color: "rgba(0, 128, 0, 0.3)" }
      ],
      attackers: [
        { x: 30, y: 25, label: "" },
        { x: 75, y: 20, label: "" },
        { x: 35, y: 55, label: "" },
        { x: 55, y: 75, label: "" }
      ],
      defenders: [
        { x: 65, y: 20, label: "" },
        { x: 60, y: 50, label: "" },
        { x: 70, y: 80, label: "" },
        { x: 30, y: 80, label: "" }
      ],
      neutrals: [],
      goals: [],
      ball: { x: 30, y: 75 },
      lines: [
        { startX: 10, startY: 35, endX: 90, endY: 35, style: "dashed", color: "red" },
        { startX: 10, startY: 65, endX: 90, endY: 65, style: "dashed", color: "red" }
      ],
      arrows: [],
      cones: []
    }
  },
  {
    id: "driving-to-overload",
    title: "Driving to Overload",
    description: "3v3+2 positioning game focusing on driving with the ball to create overloads and numerical advantages.",
    category: "positioning-games",
    topics: ["building"],
    duration: "3x3mins",
    players: "9",
    areaSize: "20x13m",
    rules: [
      "3v3+2",
      "Bouncer cannot play to bouncer",
      "Game is directional, always starts with 3 or 4",
      "Score a point by playing into the big goal",
      "BPO team must hit one of the 3 or 4 to regain possession"
    ],
    coachingPoints: [
      "Level or beyond your marker (stepping and dropping to receive)",
      "Scanning before receiving touch",
      "Short to long (attract + penetrate)",
      "Types of pass pattern, backward to diff forward line",
      "Invert to create +1",
      "Hit free player",
      "Drive and commit to create +1"
    ],
    diagram: {
      type: "custom",
      zones: [
        { x: 10, y: 5, width: 80, height: 90, color: "rgba(0, 128, 0, 0.3)" }
      ],
      attackers: [
        { x: 25, y: 35, label: "10" },
        { x: 75, y: 25, label: "8" },
        { x: 65, y: 55, label: "6" }
      ],
      defenders: [
        { x: 30, y: 85, label: "4" },
        { x: 70, y: 85, label: "3" }
      ],
      neutrals: [
        { x: 75, y: 15, label: "10" },
        { x: 50, y: 40, label: "8" },
        { x: 50, y: 10, label: "GK", color: "yellow" },
        { x: 25, y: 42, label: "6" }
      ],
      goals: [
        { x: 50, y: 5, width: 10, rotation: 0 }
      ],
      ball: { x: 35, y: 82 },
      lines: [
        { startX: 10, startY: 5, endX: 10, endY: 95, style: "solid", color: "blue" },
        { startX: 90, startY: 5, endX: 90, endY: 95, style: "solid", color: "blue" },
        { startX: 10, startY: 5, endX: 90, endY: 5, style: "solid", color: "blue" },
        { startX: 10, startY: 95, endX: 90, endY: 95, style: "solid", color: "blue" },
        { startX: 10, startY: 75, endX: 90, endY: 75, style: "dotted", color: "black" }
      ],
      arrows: [],
      cones: []
    }
  },
  {
    id: "building-ssg",
    title: "Building SSG",
    description: "4v4+1 small-sided game focusing on building out from the back with directional scoring options.",
    category: "positioning-games",
    topics: ["building"],
    duration: "2x10mins",
    players: "10",
    areaSize: "40x30m",
    rules: [
      "4v4+1",
      "Ball restarts with the GK",
      "Area is tighter to make it difficult for the BP team to play out, adjust accordingly",
      "BP team score by playing a one-two with the 9 or scoring directly into one of the smaller goals",
      "The BPO team score by shooting into the normal goalkeeper"
    ],
    coachingPoints: [
      "Level or beyond your marker (stepping and dropping to receive)",
      "Scanning before receiving touch",
      "Short to long (attract + penetrate)",
      "Types of pass pattern, backward to diff forward line",
      "Invert to create +1",
      "Hit free player",
      "Drive and commit to create +1"
    ],
    diagram: {
      type: "custom",
      zones: [
        { x: 5, y: 5, width: 90, height: 90, color: "rgba(0, 128, 0, 0.3)" },
        { x: 25, y: 75, width: 50, height: 20, color: "rgba(255, 255, 255, 0.3)" }
      ],
      attackers: [
        { x: 15, y: 30, label: "6" },
        { x: 85, y: 45, label: "10" },
        { x: 25, y: 78, label: "3" },
        { x: 75, y: 78, label: "4" }
      ],
      defenders: [
        { x: 40, y: 35, label: "8" },
        { x: 60, y: 35, label: "6" },
        { x: 40, y: 55, label: "9" },
        { x: 60, y: 55, label: "10" }
      ],
      neutrals: [
        { x: 50, y: 20, label: "9", color: "yellow" },
        { x: 50, y: 85, label: "GK", color: "yellow" }
      ],
      goals: [
        { x: 50, y: 95, width: 14, rotation: 0 },
        { x: 15, y: 5, width: 8, rotation: 0 },
        { x: 85, y: 5, width: 8, rotation: 0 }
      ],
      ball: { x: 50, y: 82 },
      lines: [],
      arrows: [],
      cones: []
    }
  },
  {
    id: "unopposed-2v2-finishing",
    title: "Unopposed to 2v2 Finishing",
    description: "Attacking third pattern focusing on combination play and finishing with progression from unopposed to 2v2.",
    category: "patterns",
    topics: ["creating-finishing"],
    duration: "3x3mins",
    players: "11",
    areaSize: "ATK third",
    rules: [
      "Up, back, through",
      "Third man run",
      "Cross 6 yard line",
      "Placement finishes",
      "Progression: 2v1 into box",
      "Progression: 2v2 into box"
    ],
    coachingPoints: [
      "Cross into 6 yard box line",
      "Plan forward run to 6 yard line (timing)",
      "Laces, instep",
      "Sacrificial runs",
      "Key scoring areas FP, BP, 6yrd, Pen",
      "No straight lines",
      "Timing of approach",
      "First time finishes"
    ],
    diagram: {
      type: "full-pitch",
      zones: [],
      attackers: [
        { x: 10, y: 65, label: "7" },
        { x: 30, y: 50, label: "6" },
        { x: 35, y: 65, label: "10" },
        { x: 45, y: 62, label: "8" },
        { x: 55, y: 62, label: "9" }
      ],
      defenders: [
        { x: 45, y: 68, label: "9" },
        { x: 55, y: 68, label: "8" },
        { x: 90, y: 65, label: "7" },
        { x: 70, y: 50, label: "6" },
        { x: 65, y: 65, label: "10" }
      ],
      neutrals: [
        { x: 50, y: 90, label: "GK", color: "yellow" }
      ],
      mannequins: [
        { x: 35, y: 85 },
        { x: 65, y: 85 }
      ],
      goals: [
        { x: 50, y: 95, width: 14, rotation: 0 }
      ],
      balls: [{ x: 35, y: 70 }, { x: 65, y: 70 }],
      lines: [],
      arrows: [],
      cones: []
    }
  },
  {
    id: "breaking-lines",
    title: "Breaking Lines",
    description: "6v6 positioning game focusing on breaking defensive lines through passing and movement in a 2-3-1 shape.",
    category: "positioning-games",
    topics: ["building", "mid-play"],
    duration: "2x7mins",
    players: "12",
    areaSize: "40x20",
    rules: [
      "6v6 BP/BPO 2-3-1 shape",
      "Don't concede ball side, force team out",
      "Can we switch behind, beside or beyond to advance play diagonally after attracting a team ball side?"
    ],
    coachingPoints: [
      "Ball speed",
      "2nd movement to create a line of pass",
      "No two players on the same line",
      "Play to your teammates advantage",
      "Suck them in, to create space to RWTB",
      "Body positioning open to break a line",
      "Explosive touch to go beyond",
      "Body-Body-Ball",
      "Top pointed down, head up, large touches"
    ],
    diagram: {
      type: "custom",
      zones: [
        { x: 5, y: 5, width: 45, height: 90, color: "rgba(200, 200, 200, 0.2)" },
        { x: 50, y: 5, width: 45, height: 90, color: "rgba(200, 200, 200, 0.2)" }
      ],
      attackers: [
        { x: 30, y: 85, label: "" },
        { x: 5, y: 45, label: "" },
        { x: 45, y: 45, label: "" },
        { x: 70, y: 85, label: "" },
        { x: 55, y: 35, label: "" },
        { x: 95, y: 35, label: "" }
      ],
      defenders: [
        { x: 40, y: 30, label: "" },
        { x: 55, y: 30, label: "" },
        { x: 70, y: 45, label: "" },
        { x: 35, y: 55, label: "" },
        { x: 55, y: 60, label: "" },
        { x: 50, y: 50, label: "" }
      ],
      neutrals: [],
      goals: [
        { x: 25, y: 5, width: 8, rotation: 0 },
        { x: 75, y: 5, width: 8, rotation: 0 },
        { x: 25, y: 95, width: 8, rotation: 0 },
        { x: 75, y: 95, width: 8, rotation: 0 }
      ],
      balls: [{ x: 70, y: 80 }],
      lines: [
        { startX: 50, startY: 5, endX: 50, endY: 95, style: "solid", color: "black" },
        { startX: 5, startY: 50, endX: 95, endY: 50, style: "solid", color: "black" }
      ],
      arrows: [],
      cones: []
    }
  },
  {
    id: "cross-the-river",
    title: "Cross the River",
    category: "positioning-games",
    topics: ["building", "mid-play"],
    areaSize: "25x60m (25x10 middle zone)",
    duration: "3x4mins",
    players: "10-12",
    description: "A possession game with three zones where teams must play the ball below knee height to cross into the next zone. Using central players earns points, with double points for running over to play the pass.",
    rules: [
      "In order for the ball to be played into the next zone, it must be played below knee height across",
      "If they use the central players, you get a point, double points if the ball is run over to play the pass",
      "Defenders try to win the ball and score - each goal is a point",
      "Attackers can also score a point by getting 15 passes"
    ],
    coachingPoints: [
      "Ball speed",
      "2nd movement to create a line of pass",
      "No two players on the same line",
      "Play to your teammates advantage",
      "Suck them in, to create space to RWTB",
      "Body positioning open to break a line",
      "Explosive touch to go beyond",
      "Body-Body-Ball",
      "Top pointed down, head up, large touches"
    ],
    diagram: {
      type: "custom",
      zones: [
        { x: 5, y: 5, width: 90, height: 25, color: "rgba(200, 200, 200, 0.15)" },
        { x: 5, y: 38, width: 90, height: 24, color: "rgba(255, 100, 100, 0.25)" },
        { x: 5, y: 70, width: 90, height: 25, color: "rgba(200, 200, 200, 0.15)" }
      ],
      attackers: [
        { x: 30, y: 15, label: "" },
        { x: 70, y: 15, label: "" },
        { x: 30, y: 85, label: "" },
        { x: 70, y: 85, label: "" }
      ],
      defenders: [
        { x: 50, y: 40, label: "" },
        { x: 30, y: 50, label: "" },
        { x: 70, y: 50, label: "" },
        { x: 50, y: 60, label: "" }
      ],
      neutrals: [
        { x: 50, y: 5, label: "GK" },
        { x: 50, y: 95, label: "GK" },
        { x: 45, y: 50, label: "" },
        { x: 55, y: 50, label: "" }
      ],
      goals: [
        { x: 50, y: 2, width: 12, rotation: 0 },
        { x: 50, y: 98, width: 12, rotation: 180 }
      ],
      balls: [{ x: 50, y: 92 }],
      lines: [
        { startX: 5, startY: 30, endX: 95, endY: 30, style: "solid", color: "white" },
        { startX: 5, startY: 70, endX: 95, endY: 70, style: "solid", color: "white" }
      ],
      arrows: [],
      cones: []
    }
  },
  {
    id: "exploit-wide",
    title: "Exploit Wide",
    category: "game-training",
    topics: ["creating-finishing"],
    areaSize: "Half Pitch",
    duration: "3x6mins",
    players: "13",
    description: "9v7 game with wide unopposed areas. Two touch in unopposed area. Encourage wide players after breaking the link to cut inside (RWTB across defender).",
    rules: [
      "6v6+GK with wide unopposed area",
      "Two touch in unopposed area",
      "Encourage wide players after break the link to cut inside (RWTB across defender)"
    ],
    coachingPoints: [
      "Ball speed",
      "2nd movement to create a line of pass",
      "No two players on the same line",
      "Play to your teammates advantage",
      "Suck them in, to create space to RWTB",
      "Body positioning open to break a line",
      "Explosive touch to go beyond",
      "Body-Body-Ball",
      "Top pointed down, head up, large touches"
    ],
    diagram: {
      type: "full-pitch",
      zones: [
        { x: 0, y: 5, width: 12, height: 90, color: "rgba(100, 150, 255, 0.15)" },
        { x: 88, y: 5, width: 12, height: 90, color: "rgba(100, 150, 255, 0.15)" }
      ],
      attackers: [
        { x: 5, y: 75, label: "" },
        { x: 95, y: 75, label: "" },
        { x: 50, y: 75, label: "" },
        { x: 25, y: 65, label: "" },
        { x: 75, y: 65, label: "" },
        { x: 50, y: 50, label: "" }
      ],
      defenders: [
        { x: 25, y: 75, label: "" },
        { x: 42, y: 75, label: "" },
        { x: 58, y: 75, label: "" },
        { x: 75, y: 75, label: "" },
        { x: 42, y: 65, label: "" },
        { x: 58, y: 65, label: "" }
      ],
      neutrals: [
        { x: 50, y: 92, label: "GK" }
      ],
      goals: [
        { x: 50, y: 95, width: 16, rotation: 180 },
        { x: 25, y: 50, width: 8, rotation: 180 },
        { x: 75, y: 50, width: 8, rotation: 180 }
      ],
      balls: [{ x: 50, y: 55 }],
      lines: [
        { startX: 12, startY: 5, endX: 12, endY: 95, style: "dashed", color: "rgba(0, 0, 150, 0.5)" },
        { startX: 88, startY: 5, endX: 88, endY: 95, style: "dashed", color: "rgba(0, 0, 150, 0.5)" }
      ],
      arrows: [],
      cones: []
    }
  },
  {
    id: "mid-combos",
    title: "Mid Combos",
    description: "Combination play to work on principles of play through midfield with focus on passing combinations and rotations to disrupt the opponent. High repetition of passing and combinations with optional 2-touch limit for added challenge.",
    category: "brilliant-basics",
    topics: ["mid-play"],
    duration: "2x4mins",
    players: "10",
    areaSize: "24x28m (8m b/w cones)",
    rules: [
      "Play begins from the goals, the first pass is to be directed short to either side",
      "Combination play to play forwards in the direction of our goal using wide players",
      "Players are free to move and combine in any set of passing with team-mates on the field, finishing the action and sequence with a shot on goal",
      "Players take up an alternative starting position when a new phase is begun",
      "To work on principles of play through midfield we focus on a simple set of passing combinations which will also focus on rotations to disrupt the opponent",
      "Key focus here is we want players to get high repetition of passing and combinations",
      "To make it more challenging we will add into this a touch limit of 2 touches"
    ],
    coachingPoints: [
      "Line of pass = pass can you find a pass to your next teammate",
      "Timing to receive",
      "Speed of pass",
      "Combination play",
      "Coaches to lead pre-activation and set of dynamics prescribed"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 23, y: 35, label: "" },
        { x: 63, y: 35, label: "" },
        { x: 23, y: 65, label: "" },
        { x: 63, y: 65, label: "" }
      ],
      defenders: [
        { x: 37, y: 35, label: "" },
        { x: 77, y: 35, label: "" },
        { x: 37, y: 65, label: "" },
        { x: 77, y: 65, label: "" }
      ],
      neutrals: [
        { x: 50, y: 10, label: "GK" },
        { x: 50, y: 90, label: "GK" }
      ],
      goals: [
        { x: 50, y: 5, width: 16, rotation: 0 },
        { x: 50, y: 95, width: 16, rotation: 180 }
      ],
      balls: [{ x: 50, y: 15 }, { x: 50, y: 85 }],
      arrows: [],
      cones: [
        { x: 30, y: 35, color: "red" },
        { x: 70, y: 35, color: "red" },
        { x: 30, y: 65, color: "red" },
        { x: 70, y: 65, color: "red" }
      ]
    }
  },
  {
    id: "find-free-player-mid",
    title: "Find Free Player",
    description: "A brilliant basics drill focusing on recognizing and finding the free player during mid-field play.",
    category: "brilliant-basics",
    topics: ["mid-play"],
    duration: "3x4mins",
    players: "10",
    areaSize: "30x30m",
    rules: [
      "Players must scan before receiving",
      "Find the free player with the first or second touch",
      "Free player must move to create passing lanes",
      "Communication is key - call for the ball"
    ],
    coachingPoints: [
      "Head up, scan the area before receiving",
      "Body position open to see the whole area",
      "Weight and accuracy of pass to the free player",
      "Movement off the ball to create space"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 50, y: 30 },
        { x: 25, y: 60 },
        { x: 50, y: 65 },
        { x: 75, y: 70 }
      ],
      defenders: [
        { x: 70, y: 35 },
        { x: 35, y: 55 },
        { x: 60, y: 60 },
        { x: 45, y: 72 }
      ],
      neutrals: [
        { x: 50, y: 7 },
        { x: 50, y: 92 }
      ],
      zones: [
        { x: 0, y: 0, width: 100, height: 15 },
        { x: 0, y: 15, width: 100, height: 35 },
        { x: 0, y: 50, width: 100, height: 35 },
        { x: 0, y: 85, width: 100, height: 15 }
      ],
      ball: { x: 50, y: 20 },
      arrows: [],
      cones: [
        { x: 10, y: 10, color: "orange" },
        { x: 90, y: 10, color: "orange" },
        { x: 10, y: 90, color: "orange" },
        { x: 90, y: 90, color: "orange" }
      ]
    }
  },
  {
    id: "side-to-side",
    title: "Side to Side",
    description: "A positioning game focusing on combining with wide players to create overloads and finishing opportunities.",
    category: "positioning-games",
    topics: ["mid-play", "creating-finishing"],
    duration: "9x1.5mins",
    players: "7",
    areaSize: "24x28m",
    rules: [
      "Blue team must combine to make a minimum of 4 passes before they can shoot and score",
      "Players will be encouraged to combine with wide neutral to create mini-game (2v1) in wide area to allow time and space to shoot and score",
      "If the defending team regains the ball they can score immediately in the two mini-goals at the end of the grid"
    ],
    coachingPoints: [
      "Can you utilize outside supporting players to create and overload and an assist to score",
      "Decision making on whether to shoot or combine",
      "Variety of finishing - can you finish with in-step, laces, outside of foot",
      "Variety of forward runs, 2v1 in central and wide areas"
    ],
    diagram: {
      type: "full-pitch-top-only",
      attackers: [
        { x: 40, y: 18 },
        { x: 60, y: 28 },
        { x: 35, y: 38 }
      ],
      defenders: [
        { x: 55, y: 22 },
        { x: 50, y: 35 },
        { x: 48, y: 17 }
      ],
      neutrals: [
        { x: 10, y: 28 },
        { x: 90, y: 32 },
        { x: 50, y: 48 },
        { x: 50, y: 4, label: "GK" }
      ],
      goals: [
        { x: 25, y: 50, width: 8, rotation: 180 },
        { x: 75, y: 50, width: 8, rotation: 180 }
      ],
      ball: { x: 40, y: 15 },
      arrows: [],
      cones: [
        { x: 15, y: 12, color: "red" },
        { x: 85, y: 12, color: "red" },
        { x: 15, y: 48, color: "red" },
        { x: 85, y: 48, color: "red" }
      ]
    }
  },
  {
    id: "side-to-side-gt",
    title: "Side to Side GT",
    description: "A game training drill focusing on playing side to side until penetrating in wide areas.",
    category: "game-training",
    topics: ["mid-play"],
    duration: "2x10mins",
    players: "13",
    areaSize: "Half pitch (step in 4m each side)",
    rules: [
      "Attack vs Defence game",
      "Can we play side to side until we can penetrate in wide areas?",
      "Red combine with #6 to score",
      "Blues look to score in the big goals"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 10, y: 18, label: "11" },
        { x: 50, y: 28, label: "9" },
        { x: 90, y: 18, label: "7" },
        { x: 25, y: 38, label: "10" },
        { x: 75, y: 35, label: "8" }
      ],
      defenders: [
        { x: 55, y: 18, label: "3" },
        { x: 35, y: 22, label: "2" },
        { x: 45, y: 18, label: "4" },
        { x: 65, y: 22, label: "5" },
        { x: 42, y: 38, label: "6" },
        { x: 58, y: 36, label: "8" }
      ],
      neutrals: [
        { x: 50, y: 6, label: "GK" },
        { x: 50, y: 50, label: "6" }
      ],
      lines: [
        { startX: 10, startY: 55, endX: 90, endY: 55, style: "dashed" }
      ],
      ball: { x: 50, y: 46 },
      arrows: [],
      cones: []
    }
  },
  {
    id: "breaking-lines-rondo",
    title: "Breaking Lines Rondo",
    description: "A rondo focused on breaking lines by connecting through bouncers, developing body positioning and movement to receive between lines.",
    category: "brilliant-basics",
    topics: ["mid-play"],
    duration: "4x2.5mins",
    players: "9",
    areaSize: "14x22m",
    rules: [
      "Can go direct into opposite grid, but must connect underneath",
      "Bouncer to bouncer, but 2nd bouncer receiving is one-touch",
      "Reds score by dribbling out of the grid"
    ],
    coachingPoints: [
      "Body positioning open to break a line",
      "Movement to lose marker",
      "2nd movement to receive ball between lines",
      "Move the ball to move the opponent"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 15, y: 15, width: 70, height: 35, color: "rgba(0, 0, 0, 0.1)" },
        { x: 15, y: 50, width: 70, height: 35, color: "rgba(0, 0, 0, 0.1)" }
      ],
      attackers: [
        { x: 50, y: 5, label: "" },
        { x: 50, y: 95, label: "" },
        { x: 8, y: 65, label: "" },
        { x: 92, y: 65, label: "" }
      ],
      defenders: [
        { x: 35, y: 60, label: "" },
        { x: 50, y: 70, label: "" },
        { x: 65, y: 60, label: "" }
      ],
      neutrals: [
        { x: 40, y: 75, label: "N" },
        { x: 60, y: 75, label: "N" }
      ],
      lines: [
        { startX: 15, startY: 50, endX: 85, endY: 50, style: "dashed" }
      ],
      ball: { x: 50, y: 88 },
      cones: [],
      arrows: []
    }
  },
  {
    id: "up-back-through-box",
    title: "Up, Back, Through Box",
    description: "Combination play exercise focusing on up-back-through patterns to promote forward play towards goal.",
    category: "brilliant-basics",
    topics: ["mid-play"],
    duration: "4x2mins",
    players: "8",
    areaSize: "14x14m",
    rules: [
      "Play always starts from marked area with balls towards our goals",
      "Blue and red team have a ball and pass up-back and through in a set pattern with no opposition pressure",
      "We want to promote combination play to play forwards in the direction of our goal",
      "Players will be then only allowed one ball – if team in possession is red then they will keep ball and play end to end against 2 balls (i.e. 2v4)",
      "When the blues win the ball only two red's inside the square can put pressure onto the team in possession",
      "Players on outside must rotate to ensure they are exposed to same conditions"
    ],
    coachingPoints: [
      "Quality of first touch - open body to receive",
      "Weight and timing of passes",
      "Communication between players",
      "Movement off the ball to create passing lanes"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 50, y: 5, label: "" },
        { x: 50, y: 95, label: "" },
        { x: 35, y: 40, label: "" },
        { x: 65, y: 60, label: "" }
      ],
      defenders: [
        { x: 5, y: 50, label: "" },
        { x: 95, y: 50, label: "" },
        { x: 40, y: 60, label: "" },
        { x: 60, y: 40, label: "" }
      ],
      neutrals: [],
      lines: [],
      balls: [
        { x: 50, y: 12 },
        { x: 12, y: 50 }
      ],
      cones: [],
      arrows: []
    }
  },
  {
    id: "combine-with-9",
    title: "Combine with #9",
    description: "Directional positioning game focusing on combination play with end players to score.",
    category: "brilliant-basics",
    topics: ["mid-play"],
    duration: "6x1.5mins",
    players: "6",
    areaSize: "8x12m",
    rules: [
      "2v2+2 - directional positioning game",
      "Ball must be played into end player to score",
      "Can only score with a first time finish",
      "Rep 1-3: All-in",
      "Rep 4-6: Max two-touch",
      "Play around with bouncer rules as progression"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 30, y: 35, label: "" },
        { x: 70, y: 65, label: "" }
      ],
      defenders: [
        { x: 35, y: 65, label: "" },
        { x: 65, y: 35, label: "" }
      ],
      neutrals: [
        { x: 50, y: 5, label: "N" },
        { x: 50, y: 95, label: "N" }
      ],
      lines: [],
      ball: { x: 35, y: 58 },
      cones: [],
      arrows: [
        { startX: 35, startY: 65, endX: 50, endY: 5, type: "pass" },
        { startX: 50, startY: 5, endX: 65, endY: 35, type: "pass" },
        { startX: 65, startY: 35, endX: 65, endY: 5, type: "movement" }
      ],
      goals: [
        { x: 35, y: 5, width: 6, rotation: 0 },
        { x: 65, y: 5, width: 6, rotation: 0 },
        { x: 35, y: 95, width: 6, rotation: 180 },
        { x: 65, y: 95, width: 6, rotation: 180 }
      ]
    }
  },
  {
    id: "up-back-through-pg",
    title: "Up, Back, Through PG",
    description: "Positioning game focusing on up-back-through patterns with end bouncers and third man combinations.",
    category: "positioning-games",
    topics: ["mid-play"],
    duration: "4x3mins",
    players: "13",
    areaSize: "24x48m",
    rules: [
      "Start with normal 4v4+5 game. Introduce rules as per below",
      "Rep 1 - Normal 4v4+5",
      "Rep 2+3 - Introduce halfway line. Must have one BP player in attacking half. BPO team can have one player defending in that half",
      "Cannot play to the end bouncer in the attacking half, must be played through central players",
      "Point scored by connecting with the end player. Third man receive off the end bouncer OR player who played the ball to the bouncer receives it",
      "Rep 4 - As above but with maximum two-touch"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 30, y: 60, label: "" },
        { x: 70, y: 60, label: "" },
        { x: 30, y: 80, label: "" },
        { x: 70, y: 80, label: "" }
      ],
      defenders: [
        { x: 50, y: 25, label: "" },
        { x: 40, y: 65, label: "" },
        { x: 60, y: 65, label: "" },
        { x: 50, y: 75, label: "" }
      ],
      neutrals: [
        { x: 50, y: 5, label: "N" },
        { x: 50, y: 30, label: "N" },
        { x: 50, y: 95, label: "N" },
        { x: 5, y: 65, label: "N" },
        { x: 95, y: 65, label: "N" }
      ],
      lines: [
        { startX: 5, startY: 50, endX: 95, endY: 50, style: "dashed" }
      ],
      ball: { x: 50, y: 88 },
      cones: [],
      arrows: []
    }
  },
  {
    id: "atk-v-def",
    title: "ATK v DEF",
    description: "Attack vs Defence game focusing on building out from the back and scoring in one of three goals.",
    category: "game-training",
    topics: ["building"],
    duration: "2x8mins",
    players: "14",
    areaSize: "40x60m",
    rules: [
      "Attack vs Defence game",
      "Build-up team have to dribble over halfway to score in one of three goals",
      "BPO team win ball and score",
      "If they can retain possession for 10 passes it is also a score"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level out",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 35, y: 30, label: "" },
        { x: 65, y: 30, label: "" },
        { x: 5, y: 50, label: "" },
        { x: 95, y: 50, label: "" },
        { x: 50, y: 70, label: "" },
        { x: 35, y: 85, label: "" },
        { x: 65, y: 85, label: "" }
      ],
      defenders: [
        { x: 50, y: 25, label: "" },
        { x: 40, y: 40, label: "" },
        { x: 60, y: 40, label: "" },
        { x: 25, y: 55, label: "" },
        { x: 75, y: 55, label: "" },
        { x: 50, y: 65, label: "" }
      ],
      neutrals: [
        { x: 50, y: 92, label: "GK" }
      ],
      lines: [
        { startX: 5, startY: 30, endX: 95, endY: 30, style: "dashed" }
      ],
      zones: [
        { x: 28, y: 81, width: 44, height: 14, color: "transparent" }
      ],
      ball: { x: 50, y: 85 },
      cones: [],
      arrows: [],
      goals: [
        { x: 30, y: 5, width: 10, rotation: 0 },
        { x: 50, y: 5, width: 10, rotation: 0 },
        { x: 70, y: 5, width: 10, rotation: 0 },
        { x: 50, y: 95, width: 14, rotation: 180 }
      ]
    }
  },
  {
    id: "attract-to-penetrate-gt",
    title: "Attract to Penetrate GT",
    description: "7v7 game training in a 2-3-2 formation. Regular game with restarts at CB, focusing on attracting defenders and penetrating through the central zone.",
    category: "game-training",
    topics: ["building"],
    duration: "3x8mins",
    players: "16",
    areaSize: "60x40m",
    rules: [
      "7v7 game",
      "2-3-2 formation",
      "Set 3 - Regular game",
      "Restarts at CB"
    ],
    coachingPoints: [
      "Double movements to lose defender",
      "Identifying space/scan",
      "Deception/Disguise - Reverse pass",
      "Receive level or beyond your marker",
      "Touch - Front foot / Back foot",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line",
      "2/5 forward runs for through",
      "Invert into space when available",
      "6 Drop and attract",
      "Support - Underneath and beyond"
    ],
    diagram: {
      type: "full-pitch",
      zones: [
        // Central zone
        { x: 25, y: 25, width: 50, height: 50, color: "rgba(255,255,255,0.1)" },
      ],
      attackers: [
        // Blue team 2-3-2 formation (attacking right)
        // Back 2
        { x: 25, y: 35, label: "4" },
        { x: 25, y: 65, label: "3" },
        // Middle 3
        { x: 40, y: 25, label: "5" },
        { x: 45, y: 50, label: "6" },
        { x: 40, y: 75, label: "2" },
        // Front 2
        { x: 60, y: 35, label: "9" },
        { x: 60, y: 65, label: "8" },
      ],
      defenders: [
        // Red team defending
        // Back line
        { x: 75, y: 35, label: "4" },
        { x: 75, y: 65, label: "3" },
        // Middle
        { x: 60, y: 25, label: "2" },
        { x: 55, y: 45, label: "10" },
        { x: 55, y: 55, label: "8" },
        { x: 60, y: 75, label: "5" },
        // Deep
        { x: 70, y: 50, label: "6" },
      ],
      neutrals: [
        // GKs in goals
        { x: 8, y: 50, label: "GK" },
        { x: 92, y: 50, label: "GK" },
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 5, y: 50, width: 12, rotation: 90 },
        { x: 95, y: 50, width: 12, rotation: 270 },
      ],
      lines: [
        // Central zone outline
        { startX: 25, startY: 25, endX: 75, endY: 25, style: "solid", color: "#1e3a5f" },
        { startX: 75, startY: 25, endX: 75, endY: 75, style: "solid", color: "#1e3a5f" },
        { startX: 75, startY: 75, endX: 25, endY: 75, style: "solid", color: "#1e3a5f" },
        { startX: 25, startY: 75, endX: 25, endY: 25, style: "solid", color: "#1e3a5f" },
      ],
    }
  },
  {
    id: "building-combos-patterns",
    title: "Building Combos",
    description: "Ball played in any combination using the cone to the right or the player in the middle. Players always move one cone to the right/left.",
    category: "patterns",
    topics: ["building"],
    duration: "4x2mins",
    players: "8",
    areaSize: "8-10m b/w poles",
    rules: [
      "Ball played in any combination using the cone to the right or the player in the middle",
      "Players always move one cone to the right/left",
      "Progression 1: To the left",
      "Progression 2: Introduce By-Pass pass",
      "Progression 3: Rotation with Central Player (Driving in)",
      "Progression 4: Free"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 50, y: 10, label: "4" },
        { x: 25, y: 30, label: "5" },
        { x: 75, y: 30, label: "3" },
        { x: 25, y: 60, label: "6" },
        { x: 75, y: 60, label: "2" },
        { x: 45, y: 88, label: "1" },
        { x: 55, y: 88, label: "7" }
      ],
      defenders: [],
      neutrals: [
        { x: 50, y: 50, label: "8" }
      ],
      cones: [
        { x: 50, y: 5, color: "orange" },
        { x: 20, y: 30, color: "red" },
        { x: 80, y: 30, color: "red" },
        { x: 20, y: 60, color: "red" },
        { x: 80, y: 60, color: "red" },
        { x: 50, y: 88, color: "red" }
      ],
      balls: [
        { x: 45, y: 92 }
      ],
      arrows: [],
      zones: []
    }
  },
  {
    id: "4v4-plus-2-positioning",
    title: "4v4+2",
    description: "4v4+2 game. If ball is played bouncer to bouncer, bouncer receiving only has one touch. Bouncer is always 2 touch.",
    category: "positioning-games",
    topics: ["mid-play"],
    duration: "4x4mins",
    players: "10",
    areaSize: "24x12m",
    rules: [
      "4v4+2 game. If ball is played bouncer to bouncer, bouncer receiving only has one touch",
      "Bouncer is always 2 touch",
      "Progression 1: 2-Touch",
      "Progression 2: 1-Touch (Except Bouncer)"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 30, y: 35, label: "1" },
        { x: 70, y: 35, label: "2" },
        { x: 30, y: 65, label: "3" },
        { x: 70, y: 65, label: "4" }
      ],
      defenders: [
        { x: 45, y: 40, label: "1" },
        { x: 55, y: 40, label: "2" },
        { x: 45, y: 60, label: "3" },
        { x: 55, y: 60, label: "4" }
      ],
      neutrals: [
        { x: 50, y: 5, label: "N" },
        { x: 50, y: 95, label: "N" }
      ],
      cones: [],
      balls: [
        { x: 50, y: 90 }
      ],
      arrows: [],
      zones: []
    }
  },
  {
    id: "9-specific-brilliant-basics",
    title: "#9 Specific",
    description: "The serving player in the yellow zone will attempt to pass into their teammate in order to advance on goal. All players are restricted to their zones with the exception of the defending player who is permitted to chase back as a recovery run when the number 9 receives the ball.",
    category: "brilliant-basics",
    topics: ["creating-finishing"],
    duration: "4x2.5mins",
    players: "3+GK",
    areaSize: "12x25m",
    rules: [
      "The serving player in the bottom zone will attempt to pass into their teammate in order to advance on goal",
      "All players are restricted to their zones",
      "Exception: defending player is permitted to chase back as a recovery run when the number 9 receives the ball"
    ],
    coachingPoints: [
      "Body positioning open to break a line",
      "Movement to lose marker",
      "2nd movement to receive ball between lines",
      "Move the ball to move the opponent",
      "Touch Deliver",
      "Runs in Box",
      "Sacrificial Run"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 50, y: 35, label: "9" },
        { x: 55, y: 75, label: "6" }
      ],
      defenders: [
        { x: 50, y: 55, label: "5" }
      ],
      neutrals: [
        { x: 50, y: 10, label: "GK" }
      ],
      cones: [],
      balls: [
        { x: 55, y: 70 }
      ],
      arrows: [],
      zones: [
        { x: 10, y: 45, width: 80, height: 20, color: "rgba(255,0,0,0.3)" }
      ],
      goals: [
        { x: 50, y: 5, width: 16 }
      ]
    }
  },
  {
    id: "overlap-underlap-brilliant-basics",
    title: "Overlap, Underlap",
    description: "Players compete in 1v1, 1v2 and 2v2 situations incorporating overlapping, underlapping and takeover movements.",
    category: "brilliant-basics",
    topics: ["creating-finishing"],
    duration: "3x3mins",
    players: "4+",
    areaSize: "18x14m",
    rules: [
      "Players compete in 1v1, 1v2 and 2v2 situations",
      "Players should incorporate different overlapping, underlapping and takeover movements",
      "Once players attack then become the defender",
      "Players rotate to a different group to ensure they are coming up against different body types",
      "Players maintain individual score",
      "Block 1 - 1v1 - 3 Minutes",
      "Block 2 - 1v2 - 3 Minutes",
      "Block 3 - 2v2 - 3 Minutes",
      "1 Point = 1 Goal Scored",
      "Must be over halfway to score"
    ],
    coachingPoints: [
      "Drive to Open Space",
      "Draw to Release",
      "Timing of Overlap/Underlap/Takeover",
      "Combination Play - Speed, Weight, Detail"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 15, y: 55, label: "" },
        { x: 55, y: 45, label: "" }
      ],
      defenders: [
        { x: 90, y: 5, label: "" },
        { x: 60, y: 30, label: "" }
      ],
      neutrals: [],
      cones: [
        { x: 35, y: 55, color: "red" },
        { x: 50, y: 30, color: "red" },
        { x: 70, y: 30, color: "red" }
      ],
      balls: [
        { x: 15, y: 50 }
      ],
      arrows: [
        { startX: 20, startY: 55, endX: 50, endY: 40, type: "dribble" }
      ],
      zones: [
        { x: 35, y: 5, width: 60, height: 55, color: "rgba(0,200,150,0.2)" }
      ],
      goals: [
        { x: 80, y: 5, width: 12 }
      ]
    }
  },
  {
    id: "overlap-underlap-pg",
    title: "Overlap, Underlap PG",
    description: "Team in possession must combine in their half to find a player in a wide area against 3 defenders.",
    category: "positioning-games",
    topics: ["creating-finishing"],
    duration: "2x6mins",
    players: "10",
    areaSize: "35x30m",
    rules: [
      "Team in possession must combine in their half to find a player in a wide area against 3 defenders",
      "Once in the wide area that player can drive across the line",
      "In this moment one of the remaining defenders can enter and defend the wide area",
      "If the out of possession team win the ball they play back and again build up against a 5v3",
      "Block 1: 1v1 in Wide Area - 6 Minutes",
      "Block 2: 2v1 in Wide Area - 6 Minutes",
      "Normal Goal = 1 Point",
      "Progression: Number of Pressing Defenders",
      "Progression: Touch Limit in Build Up"
    ],
    coachingPoints: [
      "Speed of Switch to Find Free Player Quickly",
      "Drive to Open Space",
      "Draw to Release",
      "Timing of Overlap/Underlap/Takeover",
      "Combination Play - Speed, Weight, Detail"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 15, y: 40, label: "" },
        { x: 25, y: 25, label: "" },
        { x: 50, y: 20, label: "" },
        { x: 85, y: 25, label: "" },
        { x: 95, y: 40, label: "" }
      ],
      defenders: [
        { x: 40, y: 40, label: "" },
        { x: 55, y: 40, label: "" },
        { x: 70, y: 40, label: "" },
        { x: 35, y: 65, label: "" },
        { x: 65, y: 65, label: "" }
      ],
      neutrals: [],
      cones: [],
      balls: [
        { x: 95, y: 50 }
      ],
      arrows: [
        { startX: 20, startY: 25, endX: 45, endY: 20, type: "pass" },
        { startX: 55, startY: 20, endX: 80, endY: 25, type: "pass" },
        { startX: 85, startY: 30, endX: 95, endY: 50, type: "movement" }
      ],
      zones: [
        { x: 20, y: 15, width: 60, height: 70, color: "rgba(0,200,150,0.2)" },
        { x: 0, y: 49, width: 100, height: 2, color: "rgba(255,255,255,0.8)" }
      ],
      goals: [
        { x: 10, y: 5, width: 8 },
        { x: 90, y: 5, width: 8 },
        { x: 10, y: 90, width: 8 },
        { x: 90, y: 90, width: 8 }
      ]
    }
  },
  {
    id: "overlap-underlap-gt",
    title: "Overlap, Underlap GT",
    description: "Blue team must combine to make a minimum of 3 passes before they can progress past the penetration line (1vGK).",
    category: "game-training",
    topics: ["creating-finishing"],
    duration: "3x8mins",
    players: "12+2GK",
    areaSize: "65x45m",
    rules: [
      "Red team must combine to make a minimum of 3 passes before they can progress past the penetration line (1vGK)",
      "Players will be encouraged to shoot first time once breaking the penetration line",
      "If the defending team wins the ball they can combine to play 3 passes and progress past the penetration line",
      "Progression: 2 attackers can go against 1 defender",
      "Progression: Players must play a 3rd man run into the space beyond the penetration line to score and create 2v1 moment"
    ],
    coachingPoints: [
      "Speed of Switch to Find Free Player Quickly",
      "Drive to Open Space",
      "Draw to Release",
      "Timing of Overlap/Underlap/Takeover",
      "Combination Play - Speed, Weight, Detail"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 25, y: 28, label: "" },
        { x: 40, y: 40, label: "" },
        { x: 45, y: 22, label: "" },
        { x: 55, y: 22, label: "" },
        { x: 60, y: 40, label: "" },
        { x: 75, y: 28, label: "" }
      ],
      defenders: [
        { x: 20, y: 40, label: "" },
        { x: 35, y: 28, label: "" },
        { x: 50, y: 42, label: "" },
        { x: 60, y: 30, label: "" },
        { x: 75, y: 40, label: "" },
        { x: 88, y: 35, label: "" }
      ],
      neutrals: [
        { x: 50, y: 5, label: "GK" },
        { x: 50, y: 65, label: "GK" }
      ],
      cones: [],
      balls: [
        { x: 50, y: 55 }
      ],
      arrows: [],
      zones: [
        { x: 0, y: 22, width: 100, height: 28, color: "rgba(200,200,200,0.3)" }
      ],
      goals: [
        { x: 50, y: 70, width: 16 }
      ]
    }
  },
  {
    id: "line-of-pass-brilliant-basics",
    title: "Line of Pass",
    description: "2v1 in a 15x15m box. BPO player swaps when they can win the ball and drive outside the box.",
    category: "brilliant-basics",
    topics: ["mid-play"],
    duration: "6x1.5mins",
    players: "3+",
    areaSize: "15x15m",
    rules: [
      "2v1 in a 15x15m box",
      "BPO player swaps when they can win the ball and drive outside the box"
    ],
    coachingPoints: [
      "Level + beyond",
      "Attract to penetrate",
      "Double movements",
      "Supporting positions"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 8, y: 10, label: "" },
        { x: 25, y: 10, label: "" },
        { x: 42, y: 10, label: "" },
        { x: 58, y: 25, label: "" },
        { x: 75, y: 10, label: "" },
        { x: 92, y: 10, label: "" },
        { x: 8, y: 42, label: "" },
        { x: 25, y: 58, label: "" },
        { x: 42, y: 42, label: "" },
        { x: 58, y: 58, label: "" },
        { x: 75, y: 42, label: "" },
        { x: 92, y: 58, label: "" },
        { x: 8, y: 75, label: "" },
        { x: 25, y: 75, label: "" },
        { x: 42, y: 92, label: "" },
        { x: 58, y: 75, label: "" },
        { x: 75, y: 92, label: "" },
        { x: 92, y: 75, label: "" }
      ],
      defenders: [
        { x: 16, y: 25, label: "" },
        { x: 50, y: 10, label: "" },
        { x: 83, y: 25, label: "" },
        { x: 16, y: 50, label: "" },
        { x: 50, y: 50, label: "" },
        { x: 83, y: 50, label: "" },
        { x: 16, y: 92, label: "" },
        { x: 50, y: 83, label: "" },
        { x: 83, y: 83, label: "" }
      ],
      neutrals: [],
      cones: [],
      balls: [
        { x: 15, y: 10 },
        { x: 58, y: 28 },
        { x: 85, y: 10 },
        { x: 15, y: 43 },
        { x: 50, y: 43 },
        { x: 85, y: 43 },
        { x: 15, y: 73 },
        { x: 50, y: 73 },
        { x: 85, y: 73 }
      ],
      arrows: [],
      zones: [
        { x: 33, y: 0, width: 2, height: 100, color: "rgba(255,100,100,0.5)" },
        { x: 66, y: 0, width: 2, height: 100, color: "rgba(255,100,100,0.5)" },
        { x: 0, y: 33, width: 100, height: 2, color: "rgba(255,100,100,0.5)" },
        { x: 0, y: 66, width: 100, height: 2, color: "rgba(255,100,100,0.5)" }
      ],
      goals: []
    }
  },
  {
    id: "play-through-thirds-patterns",
    title: "Play Through Thirds",
    description: "Combinations to find 9/10/7/11 in pocket. Up-Back-Through's to finish.",
    category: "patterns",
    topics: ["building", "mid-play", "creating-finishing"],
    duration: "4x4mins",
    players: "11",
    areaSize: "40mx70m",
    rules: [
      "Combinations to find 9/10/7/11 in pocket",
      "Up-Back-Through's to finish",
      "Cross to 10/winger and 9 waiting to finish",
      "One group working through the RHS, other group working through the LHS",
      "Rep 1 - 3 up 10 back 6 to 2 through 10",
      "Rep 2 - 3 up 6 back 2 pocket 10 beyond 7",
      "Rep 3 - 3 up 7 inside 9 through 10",
      "Rep 4 - 3 one 2 two 3 up 10 back 2 through 7"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 60, y: 88, label: "3" },
        { x: 95, y: 70, label: "2" },
        { x: 95, y: 50, label: "7" },
        { x: 65, y: 65, label: "6" },
        { x: 78, y: 55, label: "10" },
        { x: 55, y: 50, label: "9" }
      ],
      defenders: [
        { x: 40, y: 88, label: "3" },
        { x: 5, y: 70, label: "2" },
        { x: 5, y: 50, label: "7" },
        { x: 35, y: 65, label: "6" },
        { x: 22, y: 55, label: "10" },
        { x: 45, y: 50, label: "9" }
      ],
      neutrals: [
        { x: 50, y: 20, label: "GK" }
      ],
      mannequins: [
        { x: 20, y: 50 },
        { x: 40, y: 50 },
        { x: 60, y: 50 },
        { x: 80, y: 50 },
        { x: 40, y: 60 },
        { x: 60, y: 60 },
        { x: 20, y: 70 },
        { x: 40, y: 70 },
        { x: 60, y: 70 },
        { x: 80, y: 70 }
      ],
      cones: [],
      balls: [
        { x: 40, y: 83 },
        { x: 60, y: 83 }
      ],
      arrows: [],
      zones: [],
      goals: [
        { x: 50, y: 18, width: 16 }
      ]
    }
  },
  {
    id: "exploit-2v1",
    title: "Exploit 2v1",
    description: "Open game focusing on creating and capitalizing on 2v1 situations in wide areas to score.",
    category: "game-training",
    topics: ["creating-finishing"],
    duration: "2x10mins",
    players: "13",
    areaSize: "40x50m",
    rules: [
      "Open Game 6v6+GK",
      "Technical Point awarded at the end of the match to the team that creates and capitalises on 2v1 in wide areas",
      "Offside line still applies"
    ],
    coachingPoints: [
      "Speed of Switch to Find Free Player Quickly",
      "Drive to Open Space",
      "Draw to Release",
      "Timing of Overlap/Underlap/Takeover",
      "Combination Play",
      "- Speed",
      "- Weight",
      "- Detail"
    ],
    diagram: {
      type: "full-pitch-top-only",
      zones: [
        // Wide channel zones (dashed lines in image)
        { x: 0, y: 25, width: 20, height: 50, color: "rgba(255,100,100,0.1)" },
        { x: 80, y: 25, width: 20, height: 50, color: "rgba(255,100,100,0.1)" }
      ],
      lines: [
        // Horizontal dashed zone lines
        { startX: 0, startY: 25, endX: 100, endY: 25, style: "dashed", color: "rgba(200,100,100,0.6)" },
        { startX: 0, startY: 50, endX: 100, endY: 50, style: "dashed", color: "rgba(200,100,100,0.6)" },
        { startX: 0, startY: 75, endX: 100, endY: 75, style: "dashed", color: "rgba(200,100,100,0.6)" }
      ],
      attackers: [
        { x: 50, y: 8, label: "1" },
        { x: 30, y: 25, label: "3" },
        { x: 70, y: 25, label: "4" },
        { x: 15, y: 55, label: "2" },
        { x: 50, y: 50, label: "6" },
        { x: 85, y: 55, label: "5" },
        { x: 42, y: 70, label: "10" }
      ],
      defenders: [
        { x: 30, y: 45, label: "11" },
        { x: 55, y: 45, label: "10" },
        { x: 30, y: 70, label: "6" },
        { x: 55, y: 70, label: "6" },
        { x: 70, y: 35, label: "9" },
        { x: 90, y: 45, label: "7" }
      ],
      neutrals: [],
      balls: [
        { x: 30, y: 30 }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 16, rotation: 0 },
        { x: 25, y: 75, width: 8, rotation: 0 },
        { x: 75, y: 75, width: 8, rotation: 0 }
      ]
    }
  },
  {
    id: "wide-combination-pattern",
    title: "Wide Combination Pattern",
    description: "Combination play pattern focusing on wide play with FB, MF and FW. Ball cannot be played square - promotes forward and diagonal passing.",
    category: "patterns",
    topics: ["creating-finishing"],
    duration: "3x3mins",
    players: "8-10",
    areaSize: "8x10m",
    rules: [
      "Ball starts at the FB and can be played to the MF or FW",
      "Ball cannot be played square",
      "FW or FB can receive ball last and dribble ball to the other side",
      "Progression: FW or FB finishes after taking the ball through",
      "Add a defender"
    ],
    coachingPoints: [
      "Technical:",
      "Drive to Open Space",
      "Draw to Release",
      "Timing of Overlap/Underlap/Takeover",
      "Combination Play",
      "- Speed",
      "- Weight",
      "- Detail"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Main combination zone (dashed red box)
        { x: 30, y: 25, width: 40, height: 50, color: "rgba(255,100,100,0.15)" }
      ],
      attackers: [
        // Blue players - single file on top right side of grid
        { x: 62, y: 10 },
        { x: 62, y: 25 },
        // Blue on right side line
        { x: 70, y: 50 }
      ],
      defenders: [
        // Red players - two in single file, more central
        { x: 38, y: 75 },
        { x: 38, y: 90 },
        { x: 30, y: 50 }
      ],
      neutrals: [
        // Yellow players - central positions
        { x: 50, y: 40, color: "#FACC15" },
        { x: 50, y: 55, color: "#FACC15" }
      ],
      mannequins: [
        // Black mannequins
        { x: 50, y: 30 },
        { x: 50, y: 70 }
      ],
      balls: [
        { x: 38, y: 70 },
        { x: 62, y: 30 }
      ],
      cones: [],
      arrows: [],
      goals: []
    }
  },
  {
    id: "wide-combos",
    title: "Wide Combos",
    description: "Positioning game focusing on combination play through wide areas. Ball travels from target to target with defenders restricted to their halves.",
    category: "positioning-games",
    topics: ["creating-finishing"],
    duration: "3x4mins",
    players: "7",
    areaSize: "8x10m",
    rules: [
      "Ball must be played from one target to the other",
      "Encourage ball to travel through wide areas",
      "Red defenders can only defend in their halves",
      "Ball must be dribbled over end line to be played to target",
      "Area is very tight, width is 8m and depth is 10m (total)",
      "Normal Goal = 1 Point",
      "Progression: Add defenders",
      "Progression: Touch limit in build up"
    ],
    coachingPoints: [
      "Drive to Open Space",
      "Draw to Release",
      "Timing of Overlap/Underlap/Takeover",
      "Combination Play",
      "Technical:",
      "- Speed",
      "- Weight",
      "- Detail"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Top half zone (dashed red box)
        { x: 30, y: 25, width: 40, height: 25, color: "rgba(255,100,100,0.15)" },
        // Bottom half zone (dashed red box)
        { x: 30, y: 50, width: 40, height: 25, color: "rgba(255,100,100,0.15)" }
      ],
      attackers: [
        // Blue players - 3 attackers
        { x: 62, y: 35, label: "7" },
        { x: 38, y: 50, label: "8" },
        { x: 62, y: 65, label: "2" }
      ],
      defenders: [
        // Red players - 2 defenders, one in each half
        { x: 38, y: 35 },
        { x: 62, y: 50 }
      ],
      neutrals: [
        // Yellow target players at top and bottom
        { x: 50, y: 10, color: "#FACC15" },
        { x: 50, y: 90, color: "#FACC15" }
      ],
      balls: [],
      cones: [],
      arrows: [],
      goals: [],
      lines: [
        { startX: 55, startY: 25, endX: 55, endY: 75, style: "dotted", color: "rgba(255,100,100,0.8)" }
      ]
    }
  },
  // ============ BRILLIANT BASICS - MID PLAY ============
  {
    id: "support-under-over-box",
    title: "Support Under & Over Box",
    description: "Multi directional possession game with finish. Yellow acts as support underneath or beyond and are limited to two touches.",
    category: "brilliant-basics",
    topics: ["mid-play"],
    duration: "4x3mins",
    players: "8",
    areaSize: "30x15m",
    rules: [
      "Multi directional possession game with finish",
      "Yellow acts as support underneath or beyond - limited to two touches",
      "Score and retain possession",
      "Rotate Yellows each block",
      "Cannot play from bouncer to bouncer"
    ],
    coachingPoints: [
      "Movement into Pocket - Timing, Type",
      "Sacrificial Movements to Drag Defenders",
      "Support",
      "Runs Beyond / Support Underneath"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Top zone (lighter green)
        { x: 25, y: 10, width: 50, height: 25, color: "rgba(200,255,200,0.4)" },
        // Middle zone (darker green)
        { x: 25, y: 35, width: 50, height: 30, color: "rgba(100,200,150,0.4)" },
        // Bottom zone (lighter green)
        { x: 25, y: 65, width: 50, height: 25, color: "rgba(200,255,200,0.4)" }
      ],
      attackers: [
        // Blue players - 3 attackers in middle zone
        { x: 40, y: 42 },
        { x: 55, y: 50 },
        { x: 40, y: 58 }
      ],
      defenders: [
        // Red players - 3 defenders in middle zone
        { x: 60, y: 42 },
        { x: 45, y: 50 },
        { x: 60, y: 58 }
      ],
      neutrals: [
        // Yellow support players at top and bottom only
        { x: 50, y: 18, color: "#FACC15" },
        { x: 50, y: 82, color: "#FACC15" }
      ],
      balls: [
        { x: 53, y: 18 }
      ],
      cones: [],
      arrows: [],
      goals: [
        // Top goal
        { x: 50, y: 5, width: 8, rotation: 0 },
        // Bottom goal
        { x: 50, y: 95, width: 8, rotation: 180 }
      ],
      lines: [
        // Zone divider lines
        { startX: 25, startY: 35, endX: 75, endY: 35, style: "dashed", color: "rgba(100,150,100,0.5)" },
        { startX: 25, startY: 65, endX: 75, endY: 65, style: "dashed", color: "rgba(100,150,100,0.5)" }
      ]
    }
  },
  {
    id: "inject-to-create-plus-one",
    title: "Inject to Create +1",
    description: "Game of 2v2+4. Objective is to get the ball from one end to the other. Players on end receive and drive the ball into zone, rotating with a central player. BPO player on end cannot tackle the end player on their side.",
    category: "brilliant-basics",
    topics: ["building"],
    duration: "3x4mins",
    players: "8",
    areaSize: "15x20m",
    rules: [
      "Game of 2v2+4",
      "Objective is to get the ball from one end to the other",
      "Players on end receive and drive the ball into zone, rotating with a central player",
      "BPO player on end cannot tackle the end player on their side"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Main playing area
        { x: 15, y: 20, width: 70, height: 60, color: "rgba(100,150,100,0.2)" }
      ],
      attackers: [
        // Blue players - 2 on corners + 2 in middle
        { x: 85, y: 35, label: "7" },
        { x: 15, y: 65, label: "2" },
        { x: 30, y: 50, label: "11" },
        { x: 55, y: 45, label: "8" }
      ],
      defenders: [
        // Red players - 2 in middle + 2 on corners
        { x: 50, y: 55, label: "6" },
        { x: 70, y: 50, label: "8" },
        { x: 15, y: 30, label: "7" },
        { x: 85, y: 70, label: "2" }
      ],
      neutrals: [],
      balls: [
        { x: 58, y: 45 }
      ],
      cones: [
        // Corner cones
        { x: 15, y: 20, color: "#3B82F6" },
        { x: 85, y: 20, color: "#3B82F6" },
        { x: 15, y: 80, color: "#3B82F6" },
        { x: 85, y: 80, color: "#3B82F6" },
        // Middle cones between 2s and 7s
        { x: 15, y: 50, color: "#3B82F6" },
        { x: 85, y: 50, color: "#3B82F6" }
      ],
      arrows: [],
      goals: [],
      lines: [
        // Top line
        { startX: 15, startY: 20, endX: 85, endY: 20, style: "solid", color: "rgba(0,50,150,0.8)" },
        // Bottom line
        { startX: 15, startY: 80, endX: 85, endY: 80, style: "solid", color: "rgba(0,50,150,0.8)" },
        // Left line
        { startX: 15, startY: 20, endX: 15, endY: 80, style: "solid", color: "rgba(0,50,150,0.8)" },
        // Right line
        { startX: 85, startY: 20, endX: 85, endY: 80, style: "solid", color: "rgba(0,50,150,0.8)" }
      ]
    }
  },
  {
    id: "pocket-and-beyond",
    title: "Pocket and Beyond",
    description: "Blues aim to play through defensive block of 2-2 to score in either of the two far goals. Red defenders locked to lines but can shuffle. When ball enters middle third, defenders can press and cover. Blues provide support underneath and runs beyond to score.",
    category: "positioning-games",
    topics: ["creating-finishing"],
    duration: "4x4mins",
    players: "9",
    areaSize: "20x32m",
    rules: [
      "Blues aim to play through the defensive block of 2-2 to score in either of the two far goals",
      "Red defenders are locked to their lines but can shuffle left and right",
      "Only when ball enters the middle third can defenders press and cover",
      "Blues need to provide support underneath and runs beyond to score",
      "Offside rule applies",
      "Game always restarts from the GK",
      "If Reds win possession they can use the GK to score in the opposite goal",
      "Rotate 4 outfield players at the end of each block"
    ],
    coachingPoints: [
      "Movement into Pocket",
      "Timing of runs",
      "Type of movement",
      "Sacrificial Movements to Drag Defenders",
      "Support underneath",
      "Runs Beyond/Support Underneath"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        // Blue players - GK and 2 blues in top zone, 2 blues in middle zone
        { x: 50, y: 12, label: "GK" },
        { x: 25, y: 22 },
        { x: 75, y: 25 },
        { x: 25, y: 60 },
        { x: 75, y: 65 },
      ],
      defenders: [
        // Red defenders - 2 on top dotted line (y:33), 2 on bottom dotted line (y:66)
        { x: 40, y: 33 },
        { x: 60, y: 33 },
        { x: 40, y: 66 },
        { x: 60, y: 66 },
      ],
      neutrals: [],
      ball: { x: 55, y: 16 },
      cones: [],
      arrows: [],
      goals: [
        // Top goal - single
        { x: 50, y: 5, width: 10, rotation: 0 },
        // Bottom goals - two mini goals
        { x: 35, y: 95, width: 8, rotation: 180 },
        { x: 65, y: 95, width: 8, rotation: 180 },
      ],
      lines: [
        // Horizontal dashed lines dividing into thirds
        { startX: 10, startY: 33, endX: 90, endY: 33, style: "dashed", color: "rgba(218, 165, 32, 0.6)" },
        { startX: 10, startY: 66, endX: 90, endY: 66, style: "dashed", color: "rgba(218, 165, 32, 0.6)" },
      ]
    }
  },
  {
    id: "penetrate-space-behind",
    title: "Penetrate Space Behind",
    description: "5v4 to play out across the half way line. Once team has played out, they must drive and finish against the GK. Shooting zone is 10m in front of goal (pink line).",
    category: "positioning-games",
    topics: ["building"],
    duration: "4x4mins",
    players: "12",
    areaSize: "24x12m",
    rules: [
      "Shooting zone is 10m in front of goal (Pink line)",
      "5 vs 4 to play out across the half way line",
      "Once team has played out, they must drive and finish against the GK"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        // Blue players - back line and midfield
        { x: 50, y: 95, label: "1" },
        { x: 30, y: 82, label: "4" },
        { x: 70, y: 85, label: "3" },
        { x: 18, y: 65, label: "5" },
        { x: 50, y: 68, label: "6" },
        { x: 92, y: 68, label: "2" },
      ],
      defenders: [
        // Red players - pressing line
        { x: 50, y: 8, label: "1" },
        { x: 50, y: 36, label: "4" },
        { x: 85, y: 68, label: "11" },
        { x: 50, y: 62, label: "10" },
        { x: 34, y: 63, label: "7" },
        { x: 50, y: 83, label: "9" },
      ],
      neutrals: [],
      ball: { x: 62, y: 82 },
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 14, rotation: 0 },
        { x: 50, y: 98, width: 14, rotation: 180 },
      ],
      lines: [
        // Pink shooting zone line
        { startX: 15, startY: 28, endX: 85, endY: 28, style: "solid", color: "rgba(255, 0, 255, 0.8)" },
        // Blue half way line
        { startX: 15, startY: 45, endX: 85, endY: 45, style: "solid", color: "rgba(0, 100, 255, 0.8)" },
      ]
    }
  },
  {
    id: "idp-training",
    title: "IDP Training",
    description: "Individual Player Tasks focusing on specific game scenarios. P1: High ball into 2v2. P2: 1v1 to goal in wide area. P3: Switch of play into numbers - open atk v def. P4: Extra 2 finishes.",
    category: "brilliant-basics",
    topics: ["building", "mid-play", "creating-finishing", "bpo-1", "bpo-3+1", "transitions"],
    duration: "3x6mins",
    players: "9",
    areaSize: "Final third",
    rules: [
      "Individual Player Tasks",
      "P1 - High ball into 2v2",
      "P2 - 1v1 to goal - wide area",
      "P3 - Switch of play into numbers - open atk v def",
      "P4 - Extra 2 finishes"
    ],
    coachingPoints: [
      "CB one on one cover",
      "1v1 Def force wide",
      "1v1 atk with speed - space beyond",
      "Play to advantage",
      "1v1/overlap",
      "Underlap assist zone"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        // Blue players
        { x: 30, y: 58, label: "6" },
        { x: 78, y: 58, label: "2" },
        { x: 38, y: 35, label: "9" },
        { x: 62, y: 35, label: "10" },
        { x: 92, y: 28, label: "7" },
      ],
      defenders: [
        // Red GK and defenders
        { x: 50, y: 8, label: "1" },
        { x: 38, y: 28, label: "3" },
        { x: 62, y: 28, label: "4" },
        { x: 82, y: 25, label: "5" },
      ],
      neutrals: [
        // Coach beside goal
        { x: 25, y: 8, color: "#000000", label: "C" },
      ],
      balls: [
        { x: 30, y: 52 },
        { x: 30, y: 8 },
        { x: 70, y: 8 },
        { x: 92, y: 22 },
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 14, rotation: 0 },
        { x: 8, y: 58, width: 8, rotation: 180 },
        { x: 92, y: 58, width: 8, rotation: 180 },
      ],
      lines: []
    }
  },
  {
    id: "ssg-conditioning",
    title: "SSG Conditioning",
    description: "3v3+2 small sided game focusing on transitions. Attack team combine with end and can score. Def team to hunt/win/play free player and score.",
    category: "positioning-games",
    topics: ["transitions"],
    duration: "4x3mins",
    players: "8",
    areaSize: "20x15m",
    rules: [
      "3v3+2",
      "SET 1+2: Atk team combine with end and can score. Def team to hunt / win / play free player and score",
      "SET 3: Game - must combine with the 9",
      "SET 4: Open game with +2"
    ],
    coachingPoints: [
      "HUNT / Win",
      "Protect behind",
      "Action Reaction",
      "Secure FBO"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        // Blue players
        { x: 55, y: 25, label: "9" },
        { x: 62, y: 42, label: "7" },
        { x: 35, y: 62, label: "6" },
      ],
      defenders: [
        // Red players
        { x: 82, y: 38, label: "3" },
        { x: 68, y: 58, label: "10" },
        { x: 68, y: 75, label: "8" },
      ],
      neutrals: [
        // Yellow neutrals on the sides
        { x: 8, y: 50, color: "#F59E0B" },
        { x: 92, y: 50, color: "#F59E0B" },
      ],
      ball: { x: 58, y: 50 },
      cones: [],
      arrows: [],
      goals: [
        { x: 3, y: 25, width: 8, rotation: 90 },
        { x: 3, y: 75, width: 8, rotation: 90 },
        { x: 97, y: 25, width: 8, rotation: -90 },
        { x: 97, y: 75, width: 8, rotation: -90 },
      ],
      lines: []
    }
  },
  {
    id: "progress-with-free-player",
    title: "Progress with Free Player",
    description: "2v1 to connect with the N/9. Free atk to goal. If def win, play N or free player to progress into mini goals.",
    category: "positioning-games",
    topics: ["transitions"],
    duration: "3x4mins",
    players: "8",
    areaSize: "8x12",
    rules: [
      "2v1 to connect with the N / 9",
      "Free atk to goal",
      "If def win, play N or free player to progress into mini goals"
    ],
    coachingPoints: [
      "CB one on one cover",
      "1v1 Def force wide",
      "1v1 atk with speed - space beyond",
      "Play to advantage",
      "1v1/overlap",
      "Underlap assist zone"
    ],
    diagram: {
      type: "full-pitch-top-only",
      attackers: [
        // Blue players
        { x: 40, y: 68, label: "6" },
        { x: 70, y: 68, label: "2" },
        { x: 52, y: 38, label: "9" },
      ],
      defenders: [
        // Red GK and defenders
        { x: 50, y: 15, label: "1" },
        { x: 40, y: 38, label: "3" },
        { x: 60, y: 38, label: "4" },
        { x: 55, y: 58, label: "6" },
      ],
      neutrals: [
        // Yellow neutral and coach
        { x: 60, y: 48, color: "#F59E0B" },
        { x: 25, y: 82, color: "#000000", label: "C" },
      ],
      balls: [
        { x: 20, y: 78 },
        { x: 25, y: 88 },
        { x: 32, y: 85 },
        { x: 28, y: 75 },
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 14, rotation: 0 },
        { x: 40, y: 78, width: 8, rotation: 180 },
        { x: 70, y: 78, width: 8, rotation: 180 },
      ],
      lines: [
        // Zone divider
        { startX: 20, startY: 55, endX: 80, endY: 55, style: "solid", color: "rgba(0, 100, 255, 0.8)" },
      ]
    }
  },
  {
    id: "delay-and-recover",
    title: "Delay & Recover",
    description: "Players stay in their zones. Green can use jokers in next zone. Objective is to play into the final zone where attackers are one touch.",
    category: "positioning-games",
    topics: ["bpo-1", "bpo-3+1"],
    duration: "3x4mins",
    players: "11",
    areaSize: "45x24m",
    rules: [
      "Players stay in their zones",
      "Green can use jokers in next zone",
      "Objective is to play into the final zone where the attackers are one touch",
      "One pass must be made in the final zone and the back 4 must be inside the middle zone to score in the minis",
      "Screening defenders can recover",
      "If red win the ball they can use blue to progress into the final third where they must also play a pass to score"
    ],
    coachingPoints: [
      "Connection + Press + Cover",
      "Block and Press on Triggers",
      "Line Broken = Positioning of furthest defender to be 2nd Defender",
      "If they get beyond DELAY - Numbers behind the ball",
      "Timing of Tackle (triggers)",
      "Front foot pokes (disrupt)",
      "Block with back foot",
      "Wedge yourself b/w them and the ball"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        // Blue players (formerly greens in middle zone, moved closer to right dotted line)
        { x: 48, y: 45 },
        { x: 48, y: 65 },
      ],
      defenders: [
        // Red defenders - 4 in a line in middle zone
        { x: 42, y: 30 },
        { x: 42, y: 45 },
        { x: 42, y: 60 },
        { x: 42, y: 75 },
      ],
      neutrals: [
        // Green player (formerly blue in left zone)
        { x: 18, y: 55, color: "#22C55E" },
        // Green players in right zone
        { x: 85, y: 25, color: "#22C55E" },
        { x: 85, y: 65, color: "#22C55E" },
        { x: 72, y: 82, color: "#22C55E" },
        { x: 92, y: 38, color: "#22C55E" },
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 3, y: 35, width: 8, rotation: 90 },
        { x: 3, y: 65, width: 8, rotation: 90 },
        { x: 97, y: 50, width: 8, rotation: -90 },
      ],
      lines: [
        // Zone dividers (vertical dashed lines)
        { startX: 30, startY: 15, endX: 30, endY: 85, style: "dashed", color: "rgba(218, 165, 32, 0.6)" },
        { startX: 55, startY: 15, endX: 55, endY: 85, style: "dashed", color: "rgba(218, 165, 32, 0.6)" },
      ]
    }
  },
  {
    id: "goal-protecting",
    title: "Goal Protecting",
    description: "Yellow team attempts to play through blue team's block to find the spare two yellows nearest to the mini-goals. Blues work on cover and balance when protecting the goal.",
    category: "positioning-games",
    topics: ["bpo-3+1"],
    duration: "4x3mins",
    players: "11",
    areaSize: "45x24m",
    rules: [
      "Yellow team attempts to play through blue team's block to find the spare two yellows nearest to mini-goals",
      "If a blue player jumps out of the middle zone to apply pressure on a yellow, there must be movement from other blues to reduce the space between them",
      "Management of distances between players in a horizontal space to prevent opposition (yellow) from playing through you",
      "If the blues (def) are broken and the ball is played beyond to the other yellows, then they can recover",
      "Yellows must combine in end zone"
    ],
    coachingPoints: [
      "Can you take smaller steps as you get closer to the ball carrier to press them",
      "Can you position yourself to force a player wide and backwards before winning the ball",
      "If your teammate jumps can you provide cover and balance behind him"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        // Blue defenders in middle zone (4 in a line)
        { x: 50, y: 25 },
        { x: 50, y: 42 },
        { x: 50, y: 58 },
        { x: 50, y: 75 },
      ],
      defenders: [
        // Yellow in middle zone (central)
        { x: 65, y: 50, color: "#EAB308" },
        // Yellow players in right zone (4 total, spaced between mini-goals)
        { x: 88, y: 25, color: "#EAB308" },
        { x: 88, y: 42, color: "#EAB308" },
        { x: 88, y: 58, color: "#EAB308" },
        { x: 88, y: 75, color: "#EAB308" },
        // Yellow players in left zone (in line with mini-goals)
        { x: 20, y: 25, color: "#EAB308" },
        { x: 20, y: 75, color: "#EAB308" },
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        // Mini-goals at top (red)
        { x: 97, y: 25, width: 8, rotation: -90 },
        { x: 97, y: 75, width: 8, rotation: -90 },
        // Mini-goals at bottom (red)
        { x: 3, y: 25, width: 8, rotation: 90 },
        { x: 3, y: 75, width: 8, rotation: 90 },
      ],
      lines: [
        // Zone dividers (vertical dashed lines)
        { startX: 35, startY: 10, endX: 35, endY: 90, style: "dashed", color: "rgba(255, 255, 255, 0.6)" },
        { startX: 75, startY: 10, endX: 75, endY: 90, style: "dashed", color: "rgba(255, 255, 255, 0.6)" },
        // Horizontal border lines
        { startX: 35, startY: 10, endX: 75, endY: 10, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        { startX: 35, startY: 90, endX: 75, endY: 90, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
      ]
    }
  },
  {
    id: "goal-protecting-gt",
    title: "Goal Protecting GT",
    description: "6v6 game training. Yellow team tries to play through central blue defenders and link up to score. Blue team protects the goal and prevents penetration into the box.",
    category: "game-training",
    topics: ["bpo-3+1"],
    duration: "2x10mins",
    players: "12",
    areaSize: "50x60m",
    rules: [
      "Play begins with red team, who are trying to play through the central blue (def) players, and link with the red team",
      "Red team can combine and score anytime they are able to get the ball into the box",
      "Blue team attempts to prevent the reds from playing through and into the penalty box",
      "If they do - then next best action is to drop into the box and protect the goal"
    ],
    coachingPoints: [
      "Reference lines of defending: if the ball is beyond the box our back four are in line with the box. If the ball has travelled below the box our reference line is the ball",
      "When can we jump out of our protecting zones to win the ball back from our opponent",
      "Can you find a first ball out of a congested space after winning the ball back deep in your defensive half"
    ],
    diagram: {
      type: "full-pitch-empty",
      attackers: [
        { x: 50, y: 92, label: "GK" },
        { x: 50, y: 70 },
        { x: 30, y: 78 },
        { x: 43, y: 78 },
        { x: 57, y: 78 },
        { x: 70, y: 78 },
      ],
      defenders: [
        { x: 50, y: 55, color: "#EF4444" },
        { x: 40, y: 70, color: "#EF4444" },
        { x: 60, y: 70, color: "#EF4444" },
        { x: 8, y: 78, color: "#EF4444" },
        { x: 50, y: 78, color: "#EF4444" },
        { x: 92, y: 78, color: "#EF4444" },
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 35, y: 45, width: 8, rotation: 180 },
        { x: 65, y: 45, width: 8, rotation: 180 },
        { x: 50, y: 97, width: 16, rotation: 0 },
      ],
      ball: { x: 50, y: 58 },
      lines: [
        // Bottom 18 yard box
        { startX: 28, startY: 81, endX: 28, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.7)" },
        { startX: 28, startY: 81, endX: 72, endY: 81, style: "solid", color: "rgba(255, 255, 255, 0.7)" },
        { startX: 72, startY: 81, endX: 72, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.7)" },
        // Bottom 6 yard box
        { startX: 38, startY: 89, endX: 38, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.7)" },
        { startX: 38, startY: 89, endX: 62, endY: 89, style: "solid", color: "rgba(255, 255, 255, 0.7)" },
        { startX: 62, startY: 89, endX: 62, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.7)" },
      ]
    }
  },
  {
    id: "technical-shielding",
    title: "Technical Shielding",
    description: "1v1 shielding practice where Player A starts with the ball facing away from the halfway line. Player B defends from behind. Dribble into the opposite half to score. If defender wins the ball, they become the attacker.",
    category: "brilliant-basics",
    topics: ["mid-play"],
    duration: "4x2mins",
    players: "8",
    areaSize: "15x30m",
    rules: [
      "Player A starts with the ball, facing away from the halfway line",
      "Player B (defender) stands behind Player A",
      "Dribble into the opposite half of the working area to score a point",
      "If successful, retain possession for the next repetition",
      "If defender steals the ball, they become the in-possession dribbler",
      "New opponent each round - Knockout tournament style",
      "Winners vs Winners / Losers vs Losers progression"
    ],
    coachingPoints: [
      "Protecting the Ball - Body, Strong Arm as Barrier",
      "Turning, Wriggling, Spinning techniques",
      "Feints, L Turns, Chops, Cuts",
      "Low center of gravity when shielding",
      "Awareness of defender position"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Top half
        { x: 25, y: 5, width: 50, height: 45, color: "rgba(255,255,255,0.05)" },
        // Bottom half
        { x: 25, y: 50, width: 50, height: 45, color: "rgba(255,255,255,0.05)" },
      ],
      attackers: [
        // Blue players - one in each corner
        { x: 35, y: 18 },  // top-left corner
        { x: 65, y: 18 },  // top-right corner
        { x: 35, y: 82 },  // bottom-left corner
        { x: 65, y: 82 },  // bottom-right corner
      ],
      defenders: [
        // Red players - behind each blue
        { x: 35, y: 14 },  // top-left corner
        { x: 65, y: 14 },  // top-right corner
        { x: 35, y: 78 },  // bottom-left corner
        { x: 65, y: 78 },  // bottom-right corner
      ],
      neutrals: [],
      cones: [
        // Corner markers
        { x: 25, y: 5 },
        { x: 75, y: 5 },
        { x: 25, y: 95 },
        { x: 75, y: 95 },
      ],
      arrows: [],
      goals: [],
      balls: [
        // Above reds in top grid
        { x: 35, y: 10 },
        { x: 65, y: 10 },
        // Below blues in bottom grid
        { x: 35, y: 86 },
        { x: 65, y: 86 },
      ],
      lines: [
        // Outer boundary only (no dotted halfway line)
        { startX: 25, startY: 5, endX: 75, endY: 5, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        { startX: 25, startY: 95, endX: 75, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        { startX: 25, startY: 5, endX: 25, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        { startX: 75, startY: 5, endX: 75, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
      ]
    }
  },
  {
    id: "no-line-protect",
    title: "No Line = Protect",
    description: "2v2 positioning game with neutral outside players. 1v1 in each grid restricted to their half. Attacking player must win the duel to combine and score at the opposite end.",
    category: "positioning-games",
    topics: ["mid-play"],
    duration: "2x3mins",
    players: "8",
    areaSize: "15x30m",
    rules: [
      "Two neutral players at each end work with whichever team is in possession",
      "1v1 in each grid - players restricted to their half",
      "Outside players can continue wall passes until attacking player is open",
      "Ball should be played into nearest player from attacking team",
      "Cannot play back to outside player on the same side",
      "Win the 1v1, combine with teammate and play to opposite end to score",
      "If defending team steals, roles reverse - play to outside player first",
      "Players play and keep score in pairs"
    ],
    coachingPoints: [
      "In Possession: Protecting the Ball - Body, Strong Arm as Barrier",
      "Turning, Wriggling, Spinning - Feints, L Turns, Chops, Cuts",
      "Movement - Timing, Double Movements",
      "Combinations - Up, Back, Through",
      "Out of Possession: Man Marking"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        // Top half
        { x: 25, y: 5, width: 50, height: 45, color: "rgba(255,255,255,0.05)" },
        // Bottom half
        { x: 25, y: 50, width: 50, height: 45, color: "rgba(255,255,255,0.05)" },
      ],
      attackers: [
        // Blue players - one in each half
        { x: 50, y: 35 },
        { x: 50, y: 65 },
      ],
      defenders: [
        // Red players - marking blues
        { x: 55, y: 30 },
        { x: 45, y: 70 },
      ],
      neutrals: [
        // Yellow outside players - top corners
        { x: 28, y: 10, color: "#EAB308" },
        { x: 72, y: 10, color: "#EAB308" },
        // Yellow outside players - bottom corners
        { x: 28, y: 90, color: "#EAB308" },
        { x: 72, y: 90, color: "#EAB308" },
      ],
      cones: [
        // Side markers
        { x: 25, y: 50 },
        { x: 75, y: 50 },
      ],
      arrows: [],
      goals: [],
      balls: [
        { x: 28, y: 14 },
      ],
      lines: [
        // Halfway line (dashed)
        { startX: 25, startY: 50, endX: 75, endY: 50, style: "dashed", color: "rgba(255, 255, 255, 0.6)" },
        // Outer boundary
        { startX: 25, startY: 5, endX: 75, endY: 5, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        { startX: 25, startY: 95, endX: 75, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        { startX: 25, startY: 5, endX: 25, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        { startX: 75, startY: 5, endX: 75, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
      ]
    }
  },
  {
    id: "line-of-pass-pg",
    title: "Line of Pass PG",
    description: "Positional game focusing on creating passing lines through the opponent's press. Team builds from GK or center-backs and plays through zones to score.",
    category: "positioning-games",
    topics: ["mid-play"],
    duration: "5x3mins",
    players: "12",
    areaSize: "20x40m",
    rules: [
      "Team with ball begins play from GK or from #3/#4",
      "Team sets up in shape to play through the opponent",
      "Once set, the game begins",
      "If ball is lost, open play until opponent scores or ball leaves area",
      "Remove pressing zones from two sets",
      "Challenge: Can we score in fewer passes than before?"
    ],
    coachingPoints: [
      "From GK, engage #6 to create numerical advantage in the middle",
      "When #3 or #4 has ball, can #6 or #8 create line of pass through first press?",
      "If no direct vertical pass, still make forward pass - don't go backwards",
      "When played out of back, use wider pass to drag press then through ball to #6, #8 or #10"
    ],
    diagram: {
      type: "full-pitch-empty",
      zones: [],
      attackers: [
        // Top zone - GK and 2 blues (wider)
        { x: 50, y: 8, label: "GK" },
        { x: 25, y: 20 },
        { x: 75, y: 20 },
        // Middle zone - 2 blues
        { x: 35, y: 50 },
        { x: 65, y: 50 },
        // Bottom zone - GK and 2 blues (wider)
        { x: 50, y: 92, label: "GK" },
        { x: 25, y: 80 },
        { x: 75, y: 80 },
      ],
      defenders: [
        // Middle zone - 2 reds
        { x: 40, y: 45 },
        { x: 60, y: 55 },
        // Bottom zone - 2 reds
        { x: 30, y: 75 },
        { x: 70, y: 75 },
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      ball: { x: 50, y: 88 },
      goals: [
        { x: 50, y: 3, width: 16, rotation: 180 },
        { x: 50, y: 97, width: 16, rotation: 0 },
      ],
      lines: [
        // 3 zone grid - two horizontal dividing lines
        { startX: 5, startY: 33, endX: 95, endY: 33, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        { startX: 5, startY: 66, endX: 95, endY: 66, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        // Outer boundary
        { startX: 5, startY: 5, endX: 95, endY: 5, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        { startX: 5, startY: 95, endX: 95, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        { startX: 5, startY: 5, endX: 5, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
        { startX: 95, startY: 5, endX: 95, endY: 95, style: "solid", color: "rgba(255, 255, 255, 0.8)" },
      ]
    }
  },
  {
    id: "through-and-around",
    title: "Through and Around",
    description: "Game training focused on building from the back through the opponent's press. Yellows must create 2v1 situations against opposition wingers to penetrate and progress up the pitch.",
    category: "game-training",
    topics: ["building"],
    duration: "2x10min",
    players: "14",
    areaSize: "55x60m",
    rules: [
      "Blues must play through opponent, making a 2v1 against opposition winger to penetrate",
      "Reds on regain and score in opposite end goal at anytime",
      "If the blues play into 2v1 area they must attempt to play forwards (mini goals) and utilise the numerical advantage they have created in this wide zone"
    ],
    coachingPoints: [
      "How can supporting player of fullback create a 2v1 scenario to break the first line of press",
      "If you cannot find a wide 2v1 how can our #2/#5 move into central space to exploit numerical advantage inside and behind the first line of the opponents press",
      "Timing and mobility to turn and play forwards",
      "The dynamic link to step and play forwards"
    ],
    diagram: {
      type: "full-pitch-no-bottom-box",
      zones: [],
      attackers: [
        // GK
        { x: 50, y: 8, label: "GK" },
        // On pitch sidelines (wide)
        { x: 8, y: 20 },
        { x: 92, y: 20 },
        // Back line center
        { x: 42, y: 15 },
        { x: 58, y: 15 },
        // Midfield
        { x: 50, y: 25 },
        { x: 35, y: 38 },
        { x: 65, y: 38 },
      ],
      defenders: [
        // In the triangle of blues in 18 yard box
        { x: 50, y: 18 },
        // In the large triangle of blues (midfield)
        { x: 50, y: 34 },
        { x: 45, y: 45 },
        { x: 55, y: 45 },
        // Wingers pressing wide
        { x: 20, y: 30 },
        { x: 80, y: 30 },
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 3, width: 16, rotation: 180 },
        // Wide mini goals at halfway facing the big goal
        { x: 10, y: 50, width: 6, rotation: 180 },
        { x: 90, y: 50, width: 6, rotation: 180 },
      ],
      lines: []
    }
  },
  {
    id: "runs-before-ball-pattern",
    title: "Runs Before Ball Pattern",
    description: "Combination of passes used to find pass where a team mate can take their first touch over the half-way line. Focus on timing of runs before the ball is played.",
    category: "patterns",
    topics: ["building"],
    duration: "6x1.5mins",
    players: "9-12",
    areaSize: "30m x 40m (8-10m b/w poles)",
    rules: [
      "Combination of passes used to find pass where a team mate can take their first touch over the half-way line",
      "REP 1 Skip pass, REP 2 run comes first, REP 3 one two",
      "REP 4 Add additional CB, FB invert/outside",
      "REP 5 Add 10, high pocket combo",
      "REP 6 player choice"
    ],
    coachingPoints: [
      "Runs before ball",
      "Different lines",
      "Supporting positions",
      "Pass forward run forward",
      "Pass back - level"
    ],
    diagram: {
      type: "full-pitch-empty",
      zones: [
        { x: 25, y: 25, width: 50, height: 50, color: "rgba(59, 130, 246, 0.1)" }
      ],
      attackers: [
        // Inside grid players
        { x: 35, y: 70, label: "2" },
        { x: 90, y: 22, label: "10" },
        { x: 22, y: 58, label: "4" },
        { x: 90, y: 28, label: "5" },
        { x: 75, y: 42, label: "3" },
        { x: 10, y: 75, label: "9" },
        { x: 55, y: 28, label: "2" },
        // GKs in sideline goals
        { x: 5, y: 50, label: "GK" },
        { x: 95, y: 50, label: "GK" }
      ],
      defenders: [],
      neutrals: [
        { x: 46, y: 50 },
        { x: 54, y: 50 }
      ],
      mannequins: [
        { x: 40, y: 42 },
        { x: 60, y: 42 },
        { x: 40, y: 58 },
        { x: 60, y: 58 }
      ],
      cones: [
        // Poles on sides
        { x: 15, y: 25, color: "red" },
        { x: 15, y: 75, color: "red" },
        { x: 85, y: 25, color: "red" },
        { x: 85, y: 75, color: "red" }
      ],
      arrows: [],
      goals: [
        { x: 5, y: 50, width: 14, rotation: 90 },
        { x: 95, y: 50, width: 14, rotation: 270 }
      ],
      balls: [
        { x: 12, y: 50 },
        { x: 88, y: 50 }
      ],
      lines: [
        { startX: 15, startY: 25, endX: 85, endY: 25, style: "dashed", color: "rgba(0,0,0,0.3)" },
        { startX: 15, startY: 75, endX: 85, endY: 75, style: "dashed", color: "rgba(0,0,0,0.3)" },
        { startX: 15, startY: 25, endX: 15, endY: 75, style: "dashed", color: "rgba(0,0,0,0.3)" },
        { startX: 85, startY: 25, endX: 85, endY: 75, style: "dashed", color: "rgba(0,0,0,0.3)" }
      ]
    }
  },
  {
    id: "runs-before-ball-rondo",
    title: "Runs Before Ball Rondo",
    description: "The practice will focus on 3rd man passing and one-two touch. Focus on a simple set of passing combinations which will also focus on rotations to disrupt the opponent.",
    category: "brilliant-basics",
    topics: ["creating-finishing"],
    duration: "4x2mins",
    players: "6",
    areaSize: "10x20m",
    rules: [
      "The practice will focus on 3rd man passing and one-two touch",
      "Focus on a simple set of passing combinations which will also focus on rotations to disrupt the opponent",
      "Key focus is high repetition of switching of play - achieved by having the rondo set up with another area to transition or move into after passes are made"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 5, y: 25, width: 40, height: 50, color: "rgba(144, 238, 144, 0.3)" },
        { x: 55, y: 25, width: 40, height: 50, color: "rgba(144, 238, 144, 0.3)" }
      ],
      attackers: [
        { x: 25, y: 30, label: "9" },
        { x: 12, y: 50, label: "10" },
        { x: 38, y: 50, label: "7" },
        { x: 25, y: 70, label: "8" }
      ],
      defenders: [
        { x: 30, y: 55, label: "8" },
        { x: 25, y: 50, label: "6" }
      ],
      neutrals: [],
      cones: [
        { x: 5, y: 25, color: "red" },
        { x: 45, y: 25, color: "red" },
        { x: 5, y: 75, color: "red" },
        { x: 45, y: 75, color: "red" },
        { x: 55, y: 25, color: "red" },
        { x: 95, y: 25, color: "red" },
        { x: 55, y: 75, color: "red" },
        { x: 95, y: 75, color: "red" }
      ],
      arrows: [
        { startX: 25, startY: 70, endX: 12, endY: 50, type: "pass" },
        { startX: 25, startY: 30, endX: 75, endY: 30, type: "movement" },
        { startX: 25, startY: 70, endX: 75, endY: 70, type: "movement" }
      ],
      goals: [],
      ball: { x: 20, y: 72 },
      lines: [
        { startX: 5, startY: 25, endX: 45, endY: 25, style: "dashed", color: "rgba(255,0,0,0.5)" },
        { startX: 5, startY: 75, endX: 45, endY: 75, style: "dashed", color: "rgba(255,0,0,0.5)" },
        { startX: 5, startY: 25, endX: 5, endY: 75, style: "dashed", color: "rgba(255,0,0,0.5)" },
        { startX: 45, startY: 25, endX: 45, endY: 75, style: "dashed", color: "rgba(255,0,0,0.5)" },
        { startX: 55, startY: 25, endX: 95, endY: 25, style: "dashed", color: "rgba(255,0,0,0.5)" },
        { startX: 55, startY: 75, endX: 95, endY: 75, style: "dashed", color: "rgba(255,0,0,0.5)" },
        { startX: 55, startY: 25, endX: 55, endY: 75, style: "dashed", color: "rgba(255,0,0,0.5)" },
        { startX: 95, startY: 25, endX: 95, endY: 75, style: "dashed", color: "rgba(255,0,0,0.5)" }
      ]
    }
  },
  {
    id: "switch-to-penetrate",
    title: "Switch to Penetrate",
    description: "Attack vs Defence game. 8v7. Key here is if we can play from one side of our back 4 to the other, ultimately to penetrate through and into a deep pocket.",
    category: "game-training",
    topics: ["building"],
    duration: "2x10 mins",
    players: "15",
    areaSize: "24x60m",
    rules: [
      "Attack vs Defence game 8v7",
      "Key here is if we can play from one side of our back 4 to the other, ultimately to penetrate through and into a deep pocket",
      "Immediate attacking transition, by switching the point of attack to penetrate the defensive line of the opponent"
    ],
    coachingPoints: [
      "Level + beyond",
      "Inject to create +1",
      "Double movements",
      "Pass forward move forward / Pass back level up",
      "Pass back to go forward",
      "Supporting positions"
    ],
    diagram: {
      type: "full-pitch",
      zones: [],
      attackers: [
        // GK
        { x: 50, y: 95, label: "GK" },
        // Back 4
        { x: 25, y: 86, label: "4" },
        { x: 5, y: 73, label: "5" },
        { x: 75, y: 86, label: "3" },
        // Midfield
        { x: 40, y: 64, label: "10" },
        { x: 60, y: 61, label: "8" },
        { x: 50, y: 75, label: "6" },
        // Forward
        { x: 92, y: 65, label: "2" }
      ],
      defenders: [
        { x: 37, y: 78, label: "10" },
        { x: 62, y: 78, label: "9" },
        { x: 76, y: 63, label: "11" },
        { x: 22, y: 68, label: "7" },
        { x: 50, y: 66, label: "6" },
        { x: 60, y: 55, label: "8" },
        { x: 45, y: 57, label: "3" }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 5, width: 16, rotation: 180 },
        { x: 50, y: 100, width: 16, rotation: 0 },
        { x: 25, y: 50, width: 6, rotation: 0 },
        { x: 50, y: 50, width: 6, rotation: 0 },
        { x: 75, y: 50, width: 6, rotation: 0 }
      ],
      ball: { x: 50, y: 92 },
      lines: [
        { startX: 0, startY: 75, endX: 100, endY: 75, style: "dashed", color: "rgba(255,255,255,0.5)" }
      ]
    }
  },
  {
    id: "cb-injecting",
    title: "CB Injecting",
    description: "Blues score by playing through each zone and scoring a goal. Reds must win ball back and have 7 seconds to score goal. Two touch once they get to the 9. BP team have one extra player. BPO team are locked in their zones, BP team can move anywhere. Players must inject/RWB into middle third.",
    category: "game-training",
    topics: ["building"],
    duration: "2x10mins",
    players: "16",
    areaSize: "25x10 middle zone - overall 25x60m",
    rules: [
      "Blues score by playing through each zone and scoring a goal",
      "Reds must win ball back and have 7 seconds to score goal",
      "Two touch once they get to the 9",
      "BP team have one extra player",
      "BPO team are locked in their zones, BP team can move anywhere",
      "Players must inject/RWB into middle third",
      "PROG to 2 forwards for the BPO team"
    ],
    coachingPoints: [
      "#3 and #4 position themselves two/three metres in front of the corner of the penalty area",
      "#6, #8 stay high and out of defensive third",
      "#3 or #4 must drop off to force #9 to pick side and keep distance from opposing #7 & #11",
      "Keep ball circulating between #1, #3 & #4 to attract #9 until either #3 or #4 can inject",
      "#6 to drop into defensive third only in case of emergency or if a clear opportunity arises to pick up ball",
      "Attract next opponent by injecting with the ball and play beyond whoever presses to the now free player in the Middle Third"
    ],
    diagram: {
      type: "full-pitch",
      zones: [
        { x: 15, y: 2, width: 70, height: 30, color: "rgba(255,255,255,0.05)" },
        { x: 15, y: 34, width: 70, height: 30, color: "rgba(255,255,255,0.08)" },
        { x: 15, y: 66, width: 70, height: 32, color: "rgba(255,255,255,0.05)" }
      ],
      attackers: [
        // Top zone - blue players
        { x: 55, y: 15, label: "9" },
        // Middle zone - blue players
        { x: 30, y: 48, label: "6" },
        { x: 55, y: 42, label: "10" },
        { x: 70, y: 48, label: "8" },
        // Side players
        { x: 45, y: 62, label: "5" },
        // Bottom zone - blue players (lowered)
        { x: 35, y: 85, label: "4" },
        { x: 65, y: 85, label: "3" }
      ],
      defenders: [
        // Top zone - red players
        { x: 42, y: 12, label: "3" },
        { x: 65, y: 12, label: "4" },
        // Middle zone - red players
        { x: 45, y: 45, label: "6" },
        { x: 60, y: 52, label: "8" },
        { x: 25, y: 52, label: "7" },
        { x: 78, y: 52, label: "11" },
        // Bottom zone - red players (removed red 3)
        { x: 45, y: 72, label: "9" }
      ],
      neutrals: [
        // GKs as yellow neutrals
        { x: 50, y: 3, label: "GK" },
        { x: 50, y: 97, label: "GK" }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 0, width: 16, rotation: 180 },
        { x: 50, y: 100, width: 16, rotation: 0 }
      ],
      balls: [
        { x: 55, y: 95 }
      ],
      lines: [
        { startX: 15, startY: 33, endX: 85, endY: 33, style: "dashed", color: "rgba(255,255,255,0.5)" },
        { startX: 15, startY: 65, endX: 85, endY: 65, style: "dashed", color: "rgba(255,255,255,0.5)" }
      ]
    }
  },
  {
    id: "tight-combos",
    title: "Tight Combos",
    description: "Blue play 3 to play forward. One red presses, another passive pressure and block forward pass. Def swap on ball transfer into the other grid.",
    category: "brilliant-basics",
    topics: ["mid-play"],
    duration: "4x3mins",
    players: "8",
    areaSize: "4x10m",
    rules: [
      "Blue play 3 to play forward",
      "One red presses, another passive pressure and block forward pass",
      "Def swap on ball transfer into the other grid"
    ],
    coachingPoints: [
      "Receive level or beyond your marker",
      "Receive with an open body shape",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line",
      "Supporting positions (time - no time)"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 10, y: 5, width: 80, height: 43, color: "rgba(255,255,255,0.05)" },
        { x: 10, y: 52, width: 80, height: 43, color: "rgba(255,255,255,0.05)" }
      ],
      attackers: [
        // Top box - 3 blues
        { x: 25, y: 20 },
        { x: 50, y: 15 },
        { x: 75, y: 25 },
        // Bottom box - 3 blues
        { x: 25, y: 70 },
        { x: 50, y: 80 },
        { x: 75, y: 75 }
      ],
      defenders: [
        // One red in top box
        { x: 50, y: 30 },
        // One red on dividing line
        { x: 50, y: 50 }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [],
      ball: { x: 50, y: 18 },
      lines: [
        { startX: 10, startY: 50, endX: 90, endY: 50, style: "solid", color: "rgba(255,255,255,0.6)" }
      ]
    }
  },
  {
    id: "block-to-press-gt",
    title: "Block to Press GT",
    description: "7v6 game training focusing on defensive block to press movements, forcing ball inside and winning possession in the final third.",
    category: "game-training",
    topics: ["bpo-1"],
    duration: "2x10 mins",
    players: "13",
    areaSize: "1/3 pitch with narrow sidelines (step in 8m)",
    rules: [
      "BP try to receive or dribble in the zone past halfway",
      "BPO try to win ball in F3 and look to score",
      "BP scoring: 1-2-3-1",
      "BPO scoring: 2-4"
    ],
    coachingPoints: [
      "Action Reaction to Delay",
      "Numbers behind the ball",
      "Force ball inside where we have numbers",
      "No switch through their 6",
      "Def players ready to jump",
      "Timing whilst ball is moving - anticipating based on body language",
      "Adjusting to sit b/w two",
      "Block to press movements"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 50, y: 5, label: "GK" },
        { x: 30, y: 5, label: "3" },
        { x: 70, y: 5, label: "4" },
        { x: 5, y: 25, label: "2" },
        { x: 95, y: 25, label: "5" },
        { x: 50, y: 22, label: "6" },
        { x: 50, y: 50, label: "9" }
      ],
      defenders: [
        { x: 20, y: 36, label: "11" },
        { x: 40, y: 36, label: "6" },
        { x: 60, y: 36, label: "8" },
        { x: 80, y: 36, label: "7" },
        { x: 35, y: 22, label: "10" },
        { x: 65, y: 22, label: "9" }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 0, width: 12, rotation: 90 }
      ],
      lines: [
        { startX: 0, startY: 50, endX: 100, endY: 50, style: "dotted", color: "rgba(255,255,255,0.6)" },
        { startX: 0, startY: 62, endX: 100, endY: 62, style: "dotted", color: "rgba(255,255,255,0.6)" }
      ]
    }
  },
  {
    id: "technical-finishing",
    title: "Technical Finishing",
    description: "Final third finishing pattern focusing on crossing into the 6-yard box with timed forward runs.",
    category: "patterns",
    topics: ["creating-finishing"],
    duration: "3x3 mins",
    players: "4+GK",
    areaSize: "Final Third",
    rules: [
      "One two combo for wave 1",
      "Second wave ball from wide left",
      "Third wave ball from wide right (reset run)"
    ],
    coachingPoints: [
      "Cross into 6 yard box line",
      "Plan forward run to 6 yard line (timing)",
      "Laces, instep",
      "Touch Restriction progression",
      "One Touch Finish progression"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 50, y: 5, label: "GK" },
        { x: 85, y: 15, label: "7" },
        { x: 18, y: 20, label: "11" },
        { x: 50, y: 25, label: "9" },
        { x: 40, y: 42, label: "10" }
      ],
      defenders: [],
      neutrals: [],
      mannequins: [
        { x: 30, y: 20 },
        { x: 70, y: 20 }
      ],
      cones: [
        { x: 50, y: 20, color: "#ef4444" },
        { x: 40, y: 35, color: "#ef4444" },
        { x: 60, y: 35, color: "#ef4444" }
      ],
      arrows: [],
      goals: [
        { x: 50, y: 0, width: 12, rotation: 90 }
      ],
      balls: [
        { x: 80, y: 18 },
        { x: 24, y: 20 },
        { x: 40, y: 38 }
      ],
      lines: []
    }
  },
  {
    id: "inside-combination-patterns",
    title: "Inside Combination Patterns",
    description: "Combination play drill where the 8 and 7 combine to release the 10 or wide player behind the defensive line to finish. Focus on runs before the ball and supporting positions.",
    duration: "6x1.5mins",
    players: "11",
    areaSize: "Atk half",
    category: "patterns",
    topics: ["creating-finishing"],
    rules: [
      "Ball can be played from the 6 into either grid",
      "Objective is for the 8 and 7 to combine to release the 10 or wide player behind the line to finish",
      "8's, 10's and 6's rotate",
      "7's, 9's and 11's rotate",
      "Progression 1: Double up 7/11 to create a passive defender",
      "Progression 2: Wide Active defender",
      "Progression 3: Add Central defender"
    ],
    coachingPoints: [
      "Runs before ball",
      "Different lines",
      "Supporting positions",
      "Winger 'touch and deliver'",
      "Sacrificial runs",
      "Getting into scoring zones",
      "Rest defence"
    ],
    diagram: {
      type: "full-pitch-top-only",
      attackers: [
        { x: 50, y: 5, label: "GK" },
        { x: 85, y: 40, label: "7" },
        { x: 15, y: 40, label: "11" },
        { x: 60, y: 35, label: "9" },
        { x: 50, y: 45, label: "10" },
        { x: 68, y: 45, label: "10" },
        { x: 35, y: 60, label: "8" },
        { x: 65, y: 60, label: "8" },
        { x: 48, y: 85, label: "6" },
        { x: 50, y: 88, label: "6" },
        { x: 52, y: 91, label: "6" }
      ],
      defenders: [],
      cones: [],
      mannequins: [
        { x: 85, y: 65 },
        { x: 15, y: 65 }
      ],
      arrows: [
        { startX: 50, startY: 85, endX: 65, endY: 60, type: "pass" },
        { startX: 65, startY: 60, endX: 85, endY: 40, type: "pass" },
        { startX: 85, startY: 40, endX: 68, endY: 45, type: "pass" },
        { startX: 68, startY: 45, endX: 60, endY: 25, type: "movement" }
      ],
      goals: [
        { x: 50, y: 5, width: 12, rotation: 0 }
      ],
      balls: [
        { x: 50, y: 82 }
      ],
      zones: [
        { x: 10, y: 30, width: 40, height: 35, color: "rgba(239, 68, 68, 0.3)" },
        { x: 50, y: 30, width: 40, height: 35, color: "rgba(239, 68, 68, 0.3)" }
      ],
      lines: []
    }
  },
  {
    id: "endzone-rondo-basics",
    title: "Endzone Rondo",
    description: "Rondo drill where attackers must get the ball to target players in the end zones. Focus on runs before the ball and supporting positions.",
    duration: "4x2.5mins",
    players: "6+",
    areaSize: "12x18m",
    category: "brilliant-basics",
    topics: ["creating-finishing"],
    rules: [
      "Ball starts with 6",
      "Objective is to get the ball to one of the 11, 10, or 9 in the end zone",
      "Player cannot enter end zone before the ball",
      "If Yellow win the ball, they must play the ball to the 6",
      "If they do this 3 times successfully, they can swap positions with the middle players"
    ],
    coachingPoints: [
      "Runs before ball",
      "Different lines",
      "Supporting positions",
      "Winger 'touch and deliver'",
      "Progression: Add a crossing component",
      "Progression: Add a finishing component",
      "Progression: Add a 1v1 to finish"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 20, y: 50, label: "11" },
        { x: 65, y: 40, label: "8" },
        { x: 50, y: 75, label: "6" },
        { x: 40, y: 45, label: "10" }
      ],
      defenders: [
        { x: 55, y: 45, label: "10", color: "#FBBF24" },
        { x: 45, y: 55, label: "6", color: "#FBBF24" }
      ],
      neutrals: [],
      cones: [],
      mannequins: [],
      arrows: [
        { startX: 50, startY: 75, endX: 65, endY: 40, type: "pass" },
        { startX: 65, startY: 40, endX: 70, endY: 15, type: "pass" },
        { startX: 40, startY: 45, endX: 70, endY: 15, type: "movement", curved: true }
      ],
      goals: [],
      balls: [
        { x: 50, y: 72 }
      ],
      zones: [
        { x: 25, y: 5, width: 50, height: 20, color: "rgba(239, 68, 68, 0.2)" },
        { x: 25, y: 75, width: 50, height: 20, color: "rgba(239, 68, 68, 0.2)" }
      ],
      lines: []
    }
  },
  {
    id: "creating-finishing-game",
    title: "Creating and Finishing",
    description: "Attack vs Defence game where blue team aims to score while red team must play a pass into one of three counter-attack goals.",
    duration: "2x10min",
    players: "13",
    areaSize: "Half Pitch",
    category: "game-training",
    topics: ["creating-finishing"],
    rules: [
      "Attack vs Defence game. 6v7",
      "Ball starts with Blue 6 or GK",
      "Objective is to score a goal",
      "Reds must play a pass into one of the three blue goals"
    ],
    coachingPoints: [
      "Runs before ball",
      "Different lines",
      "Supporting positions",
      "Winger 'touch and deliver'",
      "Sacrificial runs",
      "Getting into scoring zones",
      "Rest defence"
    ],
    diagram: {
      type: "full-pitch-top-only",
      attackers: [
        { x: 25, y: 35, label: "11" },
        { x: 45, y: 45, label: "8" },
        { x: 66, y: 38, label: "10" },
        { x: 58, y: 62, label: "6" },
        { x: 55, y: 30, label: "9" },
        { x: 88, y: 30, label: "7" }
      ],
      defenders: [
        { x: 50, y: 8, label: "1", color: "#ef4444" },
        { x: 40, y: 32, label: "2", color: "#ef4444" },
        { x: 50, y: 32, label: "3", color: "#ef4444" },
        { x: 60, y: 28, label: "4", color: "#ef4444" },
        { x: 55, y: 38, label: "6", color: "#ef4444" },
        { x: 78, y: 32, label: "5", color: "#ef4444" },
        { x: 42, y: 48, label: "8", color: "#ef4444" }
      ],
      neutrals: [],
      cones: [],
      mannequins: [],
      arrows: [],
      goals: [
        { x: 50, y: 5, width: 12, rotation: 0 },
        { x: 24, y: 90, width: 6, rotation: 0 },
        { x: 50, y: 90, width: 6, rotation: 0 },
        { x: 76, y: 90, width: 6, rotation: 0 }
      ],
      balls: [
        { x: 58, y: 58 }
      ],
      zones: [],
      lines: []
    }
  },
  {
    id: "9-10-combo-basics",
    title: "9 and 10 Combo",
    description: "2v1 combination drill focusing on forward passes and receiver movement to combine and score in the small goal.",
    duration: "3x3mins",
    players: "3+",
    areaSize: "18x12m",
    category: "brilliant-basics",
    topics: ["creating-finishing"],
    rules: [
      "Play a pass forward from the start line to the receiver who makes a run towards the ball from the goal line",
      "Defender follows to apply passive pressure from behind",
      "In a 2v1 scenario, reds must combine and score in the small goal",
      "Receiver must play bounce pass",
      "Can only score in attacking half",
      "Blue defender must win ball and either pass to waiting player on end line or RWB outside the grid"
    ],
    coachingPoints: [
      "Forward pass with firm ball speed",
      "Supporting player to make forward run in order to combine",
      "Receiver to play first time pass to teammates advantage",
      "Receiver to make movement towards the ball",
      "Use deception and disguise to unbalance opponent"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 12, y: 45, label: "10", color: "#FBBF24" },
        { x: 50, y: 45, label: "9", color: "#FBBF24" }
      ],
      defenders: [
        { x: 56, y: 45, label: "", color: "#ef4444" }
      ],
      neutrals: [],
      cones: [],
      mannequins: [],
      arrows: [],
      goals: [
        { x: 95, y: 45, width: 8, rotation: 90 }
      ],
      balls: [
        { x: 15, y: 42 }
      ],
      zones: [
        { x: 5, y: 20, width: 45, height: 55, color: "rgba(255, 255, 255, 0.05)" },
        { x: 50, y: 20, width: 45, height: 55, color: "rgba(255, 255, 255, 0.05)" }
      ],
      lines: [
        { startX: 50, startY: 20, endX: 50, endY: 75, style: "dashed", color: "white" }
      ]
    }
  },
  {
    id: "4v4-plus-4-positioning",
    title: "4v4+4",
    description: "BP team play someone through into the attacking half to score. BPO aim to keep opponent in their attacking half. Competition between 3 teams.",
    duration: "3x3mins",
    players: "12",
    areaSize: "20x10m",
    category: "positioning-games",
    topics: ["mid-play"],
    rules: [
      "4v4+4 format",
      "BP team play someone through into the atk half to score and vice versa",
      "BPO aim to keep opp in their atk half",
      "FUN - Competition between 3 teams"
    ],
    coachingPoints: [
      "Inject to trigger movement to lose marker",
      "Receive level or beyond your marker",
      "Receive with an open body shape",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line",
      "CUES - Supporting positions (time - no time)"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 20, y: 30, label: "", color: "#FBBF24" },
        { x: 35, y: 45, label: "", color: "#FBBF24" },
        { x: 25, y: 60, label: "", color: "#FBBF24" },
        { x: 38, y: 75, label: "", color: "#FBBF24" }
      ],
      defenders: [
        { x: 27, y: 10, label: "", color: "#ef4444" },
        { x: 27, y: 90, label: "", color: "#ef4444" },
        { x: 10, y: 50, label: "", color: "#ef4444" },
        { x: 90, y: 50, label: "", color: "#ef4444" }
      ],
      neutrals: [
        { x: 30, y: 35, label: "" },
        { x: 40, y: 50, label: "" },
        { x: 22, y: 70, label: "" },
        { x: 32, y: 80, label: "" }
      ],
      cones: [],
      mannequins: [],
      arrows: [],
      goals: [],
      balls: [],
      zones: [
        { x: 10, y: 10, width: 35, height: 80, color: "rgba(255, 255, 255, 0.1)" },
        { x: 55, y: 10, width: 35, height: 80, color: "rgba(255, 255, 255, 0.1)" }
      ],
      lines: []
    }
  },
  {
    id: "exploit-back-3-game",
    title: "Exploit Back 3",
    description: "7v6+1 game where blue team scores in big goals and red team scores in mini goals on the sides. Focus on double movements and exploiting spaces.",
    duration: "2x8mins",
    players: "13",
    areaSize: "Atk half - 4m each side",
    category: "game-training",
    topics: ["mid-play"],
    rules: [
      "6v6+1 format",
      "Blue score in big goals",
      "Red score in mini goals"
    ],
    coachingPoints: [
      "Double Movements to Lose Defender",
      "Identifying Space/Scan",
      "Deception/Disguise - Body Feint, Reverse Pass",
      "Receive level or beyond your marker",
      "Touch - Front foot / Back foot",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line"
    ],
    diagram: {
      type: "full-pitch-no-bottom-box",
      zones: [],
      attackers: [
        { x: 18, y: 35, label: "11" },
        { x: 65, y: 60, label: "6" },
        { x: 54, y: 42, label: "9" },
        { x: 35, y: 55, label: "10" },
        { x: 75, y: 50, label: "8" },
        { x: 92, y: 35, label: "7" }
      ],
      defenders: [
        { x: 50, y: 10, label: "1" },
        { x: 42, y: 35, label: "2" },
        { x: 50, y: 33, label: "3" },
        { x: 55, y: 50, label: "6" },
        { x: 64, y: 33, label: "4" },
        { x: 70, y: 50, label: "5" },
        { x: 45, y: 48, label: "8" }
      ],
      neutrals: [],
      ball: { x: 65, y: 56 },
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 3, width: 12, rotation: 0 },
        { x: 20, y: 68, width: 8, rotation: 180 },
        { x: 50, y: 68, width: 8, rotation: 180 },
        { x: 80, y: 68, width: 8, rotation: 180 }
      ],
      lines: []
    }
  },
  {
    id: "inside-combinations-pg",
    title: "Inside Combinations PG",
    description: "5v5+4 positioning game focusing on inside combinations. Bouncers cannot play to bouncer. Game is directional, always starts with 3 or 4.",
    duration: "3x3mins",
    players: "14",
    areaSize: "20x30m",
    category: "positioning-games",
    topics: ["mid-play"],
    rules: [
      "5v5+4 format",
      "Bouncer cannot play to bouncer",
      "Game is directional, starts with 3 or 4",
      "Score by playing one-two and/or line dribble with 9",
      "BPO team must hit 3 or 4 to regain possession"
    ],
    coachingPoints: [
      "Diagonal passing lines",
      "Dribble to attract",
      "Face Forward",
      "Tempo"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 15, y: 20, label: "11" },
        { x: 15, y: 78, label: "6" },
        { x: 90, y: 20, label: "7" },
        { x: 60, y: 32, label: "10" },
        { x: 85, y: 78, label: "8" }
      ],
      defenders: [
        { x: 35, y: 20, label: "3" },
        { x: 45, y: 45, label: "8" },
        { x: 60, y: 45, label: "10" },
        { x: 50, y: 65, label: "9" },
        { x: 65, y: 20, label: "6" }
      ],
      neutrals: [
        { x: 50, y: 5, label: "9" },
        { x: 25, y: 45, label: "2" },
        { x: 35, y: 92, label: "3" },
        { x: 75, y: 92, label: "4" }
      ],
      ball: { x: 82, y: 75 },
      cones: [],
      arrows: [],
      goals: [],
      zones: [
        { x: 12, y: 15, width: 80, height: 72, color: "rgba(255, 0, 255, 0.15)" }
      ],
      lines: []
    }
  },
  {
    id: "inside-combinations-gt",
    title: "Inside Combinations GT",
    description: "Game training version of inside combinations. Ball restarts with GK. Area is tighter to challenge build-up play.",
    duration: "2x8min",
    players: "16",
    areaSize: "Half pitch - narrow",
    category: "game-training",
    topics: ["mid-play"],
    rules: [
      "Ball restarts with GK",
      "BP team score by one-two with 9 into smaller goals",
      "BPO team score by shooting into normal goal"
    ],
    coachingPoints: [
      "Diagonal passing lines",
      "Dribble to attract",
      "Face Forward",
      "Tempo"
    ],
    diagram: {
      type: "full-pitch",
      zones: [],
      attackers: [
        { x: 50, y: 95, label: "GK" },
        { x: 25, y: 86, label: "4" },
        { x: 5, y: 73, label: "5" },
        { x: 75, y: 86, label: "3" },
        { x: 40, y: 64, label: "10" },
        { x: 60, y: 61, label: "8" },
        { x: 50, y: 75, label: "6" },
        { x: 92, y: 65, label: "2" },
        { x: 50, y: 50, label: "9" }
      ],
      defenders: [
        { x: 37, y: 78, label: "10" },
        { x: 62, y: 78, label: "9" },
        { x: 76, y: 63, label: "11" },
        { x: 22, y: 68, label: "7" },
        { x: 50, y: 66, label: "6" },
        { x: 60, y: 55, label: "8" },
        { x: 45, y: 57, label: "3" }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 5, width: 16, rotation: 180 },
        { x: 50, y: 100, width: 16, rotation: 0 },
        { x: 25, y: 50, width: 6, rotation: 0 },
        { x: 75, y: 50, width: 6, rotation: 0 }
      ],
      ball: { x: 50, y: 92 },
      lines: []
    }
  },
  {
    id: "short-to-long-pg",
    title: "Short to Long PG",
    description: "8v4 positioning game focusing on short passes to attract defenders before playing long. Complete 5 passes then play to another grid.",
    duration: "3x3min",
    players: "12",
    areaSize: "24x24m",
    category: "positioning-games",
    topics: ["mid-play"],
    rules: [
      "8v4 - 4 attackers per grid",
      "Must complete 5 passes to then play to another grid",
      "Central zone defenders need to wait in the center zone until the ball is played",
      "2 reds will press in the corner box and will swap once the ball is played into another after making their 5 passes"
    ],
    coachingPoints: [
      "Level or beyond your marker (stepping and dropping to receive)",
      "Scanning before receiving touch",
      "Touches around the corner (front foot, outside foot, back-foot)",
      "Short to long (attract + penetrate)",
      "Types of pass pattern, backward to diff forward line"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 35, y: 35, width: 30, height: 30, color: "rgba(0, 0, 139, 0.15)" }
      ],
      attackers: [
        { x: 75, y: 20, label: "6" },
        { x: 35, y: 18, label: "3", color: "yellow" },
        { x: 58, y: 23, label: "2" },
        { x: 25, y: 45, label: "8", color: "yellow" },
        { x: 25, y: 78, label: "8", color: "yellow" },
        { x: 92, y: 18, label: "9" },
        { x: 92, y: 35, label: "10" },
        { x: 75, y: 80, label: "7", color: "yellow" }
      ],
      defenders: [
        { x: 40, y: 55 },
        { x: 55, y: 55 },
        { x: 40, y: 40 },
        { x: 55, y: 40 }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [],
      lines: [
        { startX: 0, startY: 50, endX: 100, endY: 50, style: "solid", color: "white" },
        { startX: 50, startY: 0, endX: 50, endY: 100, style: "solid", color: "white" },
        { startX: 35, startY: 35, endX: 65, endY: 35, style: "solid", color: "rgba(0, 0, 139, 0.8)" },
        { startX: 35, startY: 65, endX: 65, endY: 65, style: "solid", color: "rgba(0, 0, 139, 0.8)" },
        { startX: 35, startY: 35, endX: 35, endY: 65, style: "solid", color: "rgba(0, 0, 139, 0.8)" },
        { startX: 65, startY: 35, endX: 65, endY: 65, style: "solid", color: "rgba(0, 0, 139, 0.8)" }
      ],
      ball: { x: 85, y: 35 }
    }
  },
  {
    id: "4v4-plus-5-pg",
    title: "4v4+5 PG",
    description: "4v4+5 positioning game focusing on double movements, deception, and playing to teammates advantage. Progress through REPs introducing new constraints.",
    duration: "4x3min",
    players: "13",
    areaSize: "24x48m",
    category: "positioning-games",
    topics: ["mid-play"],
    rules: [
      "REP 1: Start with normal 4v4+5",
      "REP 2+3: Introduce halfway line, must have one player defend the 9",
      "Can't play straight to end bouncer",
      "Must combine with the 9 or another player in the atk half to score",
      "REP 4: 2 touch play in atk half"
    ],
    coachingPoints: [
      "Double Movements to Lose Defender",
      "Identifying Space/Scan",
      "Deception/Disguise - Body Feint, Reverse Pass",
      "Receive level or beyond your marker",
      "Through/Around/Over",
      "Playing to your teammates advantage",
      "No two players on same line",
      "Hit free player"
    ],
    diagram: {
      type: "small-grid",
      zones: [],
      attackers: [
        { x: 12, y: 25, label: "8" },
        { x: 38, y: 25, label: "10" },
        { x: 12, y: 75, label: "6" },
        { x: 38, y: 75, label: "9" }
      ],
      defenders: [
        { x: 75, y: 50 },
        { x: 5, y: 50 },
        { x: 25, y: 5 },
        { x: 25, y: 95 },
        { x: 95, y: 50 }
      ],
      neutrals: [
        { x: 30, y: 35, label: "11", color: "yellow" },
        { x: 20, y: 55, label: "7", color: "yellow" },
        { x: 40, y: 55, label: "13", color: "yellow" },
        { x: 88, y: 50, label: "1", color: "yellow" }
      ],
      cones: [],
      arrows: [],
      goals: [],
      lines: [
        { startX: 50, startY: 0, endX: 50, endY: 100, style: "solid", color: "white" }
      ],
      ball: { x: 18, y: 75 }
    }
  },
  {
    id: "play-forward-or-switch",
    title: "Play Forward or Switch",
    description: "Game training exercise focusing on midfield players receiving on their furthest foot and playing forward or switching play to expose the weak side.",
    duration: "2x10min",
    players: "14",
    areaSize: "Third pitch",
    category: "game-training",
    topics: ["mid-play"],
    rules: [
      "Ball is fed in by coaches to the 6, 2, 3 or 5",
      "Objective is for blues to score a goal by dribbling through any three of the white mannequins to score in the big goal",
      "Blue BP 3-1-1-2, BPO 4-3",
      "Red BP 2-3-2, BPO 4-3",
      "If reds win the ball, they look to score in their big goal",
      "Reds must break past out of the zone to shoot"
    ],
    coachingPoints: [
      "3/2/5: If the 6/8/10 receive the ball on their furthest foot coming across, can you provide a line to stretch the pitch horizontally?",
      "6/8/10: If a ball is played beyond you, can you get in a line of pass underneath so we can play the next pass forward?",
      "As the ball is played from one side of the pitch, try to receive it on your furthest foot to expose the opposition's weak side",
      "If we play a pass to team mate in a tight area, can you have quick 2nd movement to provide another line of pass?",
      "7/11: As your team mates combine centrally, can you create width and threaten their last line?",
      "If there is space in the pocket, can you receive and drive, or play quickly out of the pocket?",
      "If your team mate has the ball open, can you make a run beyond to threaten through the channel?",
      "If the ball is being crossed in from the opposite side, can you make sure you're in a position to finish inside the box?"
    ],
    diagram: {
      type: "full-pitch",
      zones: [
        { x: 22, y: 20, width: 28, height: 60, color: "rgba(255, 0, 0, 0.15)" },
        { x: 50, y: 20, width: 28, height: 60, color: "rgba(255, 0, 0, 0.15)" },
        { x: 30, y: 78, width: 8, height: 4, color: "rgba(255, 255, 255, 0.8)" },
        { x: 46, y: 78, width: 8, height: 4, color: "rgba(255, 255, 255, 0.8)" },
        { x: 62, y: 78, width: 8, height: 4, color: "rgba(255, 255, 255, 0.8)" }
      ],
      attackers: [
        { x: 50, y: 20, label: "3" },
        { x: 25, y: 25, label: "2" },
        { x: 75, y: 25, label: "5" },
        { x: 45, y: 38, label: "6" },
        { x: 19, y: 75, label: "7" },
        { x: 81, y: 75, label: "11" },
        { x: 60, y: 58, label: "10" }
      ],
      defenders: [
        { x: 28, y: 35, label: "11" },
        { x: 45, y: 44, label: "8" },
        { x: 56, y: 35, label: "10" },
        { x: 70, y: 35, label: "7" },
        { x: 35, y: 65, label: "5" },
        { x: 50, y: 65, label: "6" },
        { x: 65, y: 65, label: "2" }
      ],
      neutrals: [],
      mannequins: [],
      cones: [],
      arrows: [],
      goals: [],
      lines: [
        { startX: 22, startY: 20, endX: 50, endY: 20, style: "dashed", color: "rgba(200, 0, 0, 0.6)" },
        { startX: 22, startY: 80, endX: 50, endY: 80, style: "dashed", color: "rgba(200, 0, 0, 0.6)" },
        { startX: 22, startY: 20, endX: 22, endY: 80, style: "dashed", color: "rgba(200, 0, 0, 0.6)" },
        { startX: 50, startY: 20, endX: 50, endY: 80, style: "dashed", color: "rgba(200, 0, 0, 0.6)" },
        { startX: 50, startY: 20, endX: 78, endY: 20, style: "dashed", color: "rgba(200, 0, 0, 0.6)" },
        { startX: 50, startY: 80, endX: 78, endY: 80, style: "dashed", color: "rgba(200, 0, 0, 0.6)" },
        { startX: 78, startY: 20, endX: 78, endY: 80, style: "dashed", color: "rgba(200, 0, 0, 0.6)" }
      ]
    }
  },
  {
    id: "define-side-of-play",
    title: "Define Side of Play",
    description: "Ball starts at Yellow 1. Objective is to get the ball to one of the red players over the end line. If blues win the ball, they need to get the ball over the line of where the game started. Game restarts from the yellow 1.",
    category: "positioning-games",
    topics: ["bpo-1"],
    duration: "5x3mins",
    players: "7",
    areaSize: "10x15m",
    rules: [
      "Ball starts at Yellow 1",
      "Objective is to get the ball to one of the red players over the end line",
      "If blues win the ball, they need to get the ball over the line of where the game started",
      "Game restarts from the yellow 1"
    ],
    coachingPoints: [
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "Def your lane",
      "Set up to reduce time + space",
      "Reduce distance whilst ball is moving"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 25, y: 25, width: 50, height: 50, color: "rgba(255, 0, 0, 0.15)" }
      ],
      attackers: [
        { x: 38, y: 50 },
        { x: 55, y: 40 },
        { x: 62, y: 60 }
      ],
      defenders: [
        { x: 38, y: 34 },
        { x: 25, y: 72 },
        { x: 75, y: 50 }
      ],
      neutrals: [
        { x: 75, y: 20, color: "#FACC15" }
      ],
      cones: [],
      arrows: [
        { startX: 75, startY: 20, endX: 45, endY: 35, type: "pass" }
      ],
      goals: [],
      lines: [
        { startX: 25, startY: 25, endX: 75, endY: 25, style: "dashed", color: "rgba(255, 0, 0, 0.8)" },
        { startX: 25, startY: 75, endX: 75, endY: 75, style: "dashed", color: "rgba(255, 0, 0, 0.8)" },
        { startX: 25, startY: 25, endX: 25, endY: 75, style: "dashed", color: "rgba(255, 0, 0, 0.8)" },
        { startX: 75, startY: 25, endX: 75, endY: 75, style: "dashed", color: "rgba(255, 0, 0, 0.8)" }
      ],
      ball: { x: 70, y: 20 }
    }
  },
  {
    id: "force-one-way",
    title: "Force One Way",
    description: "Blue team playing out from the back are restricted to their zones. The red team pressing are free to move between any zone.",
    category: "positioning-games",
    topics: ["bpo-1"],
    duration: "2x12mins",
    players: "10",
    areaSize: "40x30m",
    rules: [
      "Blue team playing out from the back are restricted to their zones",
      "The red team pressing are free to move between any zone",
      "1x12 zones",
      "1x12 free"
    ],
    coachingPoints: [
      "1v1 defending",
      "Def your lane",
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "Adjust as ball is moving",
      "Ball side Goal side"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 15, y: 10, width: 70, height: 40, color: "rgba(255, 255, 255, 0.1)" },
        { x: 15, y: 50, width: 70, height: 40, color: "rgba(255, 255, 255, 0.1)" }
      ],
      attackers: [
        { x: 50, y: 12, label: "1" },
        { x: 8, y: 55, label: "2" },
        { x: 40, y: 25, label: "3" },
        { x: 65, y: 25, label: "4" },
        { x: 88, y: 45, label: "5" },
        { x: 55, y: 65, label: "6" }
      ],
      defenders: [
        { x: 88, y: 55, label: "7" },
        { x: 55, y: 72, label: "8" },
        { x: 55, y: 40, label: "9" },
        { x: 8, y: 65, label: "11" }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 5, width: 12, rotation: 0 },
        { x: 12, y: 95, width: 8, rotation: 180 },
        { x: 50, y: 95, width: 8, rotation: 180 },
        { x: 88, y: 95, width: 8, rotation: 180 }
      ],
      lines: [
        { startX: 15, startY: 10, endX: 85, endY: 10, style: "solid", color: "white" },
        { startX: 15, startY: 90, endX: 85, endY: 90, style: "solid", color: "white" },
        { startX: 15, startY: 10, endX: 15, endY: 90, style: "solid", color: "white" },
        { startX: 85, startY: 10, endX: 85, endY: 90, style: "solid", color: "white" },
        { startX: 15, startY: 50, endX: 85, endY: 50, style: "solid", color: "white" }
      ],
      ball: { x: 50, y: 18 }
    }
  },
  {
    id: "6v6-plus-2",
    title: "6v6+2",
    description: "Open game 6v6 + 2. Three zone game with neutral players who can move between zones.",
    category: "game-training",
    topics: ["mid-play"],
    duration: "2x8min",
    players: "14",
    areaSize: "24x60m",
    rules: [
      "Open game 6v6 + 2"
    ],
    coachingPoints: [
      "Diagonal passing lines",
      "Dribble to attract",
      "Face Forward",
      "Tempo"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 10, y: 15, width: 18, height: 70, color: "rgba(255, 255, 255, 0.1)" },
        { x: 28, y: 15, width: 44, height: 70, color: "rgba(255, 255, 255, 0.1)" },
        { x: 72, y: 15, width: 18, height: 70, color: "rgba(255, 255, 255, 0.1)" }
      ],
      attackers: [
        { x: 5, y: 50, label: "1" },
        { x: 16, y: 35 },
        { x: 16, y: 65 },
        { x: 50, y: 30 },
        { x: 38, y: 60 },
        { x: 62, y: 70 },
        { x: 78, y: 45 }
      ],
      defenders: [
        { x: 95, y: 50, label: "1" },
        { x: 22, y: 50 },
        { x: 56, y: 30 },
        { x: 44, y: 60 },
        { x: 68, y: 70 },
        { x: 78, y: 35 },
        { x: 78, y: 55 }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 3, y: 50, width: 16, rotation: 90 },
        { x: 97, y: 50, width: 16, rotation: -90 }
      ],
      lines: [
        { startX: 10, startY: 15, endX: 90, endY: 15, style: "solid", color: "white" },
        { startX: 10, startY: 85, endX: 90, endY: 85, style: "solid", color: "white" },
        { startX: 10, startY: 15, endX: 10, endY: 85, style: "solid", color: "white" },
        { startX: 28, startY: 15, endX: 28, endY: 85, style: "solid", color: "white" },
        { startX: 72, startY: 15, endX: 72, endY: 85, style: "solid", color: "white" },
        { startX: 90, startY: 15, endX: 90, endY: 85, style: "solid", color: "white" }
      ],
      ball: { x: 12, y: 50 }
    }
  },
  {
    id: "pressing-trap",
    title: "Pressing Trap",
    description: "Pressing game where white defenders aim to win the ball and score in the big goal while blue attackers try to score in mini goals.",
    category: "positioning-games",
    topics: ["bpo-1"],
    duration: "3x4min",
    players: "11",
    areaSize: "30x24m",
    rules: [
      "Red try to score in minis",
      "Blue aim to win ball and score in the big goal"
    ],
    coachingPoints: [
      "Side on defending",
      "Delay",
      "Press cover",
      "Weakside connect",
      "Engagement on CB drive",
      "Define side of play"
    ],
    diagram: {
      type: "full-pitch-empty",
      attackers: [
        { x: 25, y: 45, label: "6" },
        { x: 50, y: 35, label: "10" },
        { x: 60, y: 48, label: "9" },
        { x: 48, y: 62, label: "8" }
      ],
      defenders: [
        { x: 35, y: 25, label: "8" },
        { x: 22, y: 70, label: "10" },
        { x: 55, y: 12, label: "4" },
        { x: 55, y: 82, label: "5" },
        { x: 85, y: 35, label: "2" },
        { x: 85, y: 62, label: "3" },
        { x: 95, y: 50, label: "GK" }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 97, y: 50, width: 16, rotation: -90 },
        { x: 5, y: 35, width: 8, rotation: 90 },
        { x: 5, y: 65, width: 8, rotation: 90 }
      ],
      zones: [
        { x: 82, y: 35, width: 18, height: 30, color: "rgba(255, 255, 255, 0.3)" }
      ],
      lines: [
        { startX: 82, startY: 35, endX: 100, endY: 35, style: "solid", color: "white" },
        { startX: 82, startY: 65, endX: 100, endY: 65, style: "solid", color: "white" },
        { startX: 82, startY: 35, endX: 82, endY: 65, style: "solid", color: "white" }
      ],
      ball: { x: 88, y: 50 }
    }
  },
  {
    id: "overload-with-fbs",
    title: "Overload with FBs",
    description: "Positional game focusing on building from the back with fullback overloads through three zones.",
    category: "positioning-games",
    topics: ["building"],
    duration: "4x4min",
    players: "11",
    areaSize: "15x25m",
    rules: [
      "Players must move ball from one end line to the other",
      "Players must RWB over each third",
      "Players in BP can move into next zone",
      "BPO players are locked in their zones",
      "Must RWB over end line to score",
      "Players swap roles every 4mins"
    ],
    coachingPoints: [
      "Circulate ball in back half until a CB can inject into the next third",
      "When RWB, ball carrier to keep the ball on furthest foot from opponent",
      "As CB is injecting into next half, can the CM move away from the ball to create space",
      "If tightly marked can supporting player move in line with the ball",
      "CB to use disguise when attracting opponent and passing to midfielder",
      "CM to stay away from CB during buildup (level / outside)"
    ],
    diagram: {
      type: "full-pitch-empty",
      zones: [
        { x: 10, y: 15, width: 80, height: 70, color: "rgba(255, 255, 255, 0.1)" }
      ],
      attackers: [
        { x: 25, y: 30 },
        { x: 15, y: 70 },
        { x: 55, y: 45 },
        { x: 37, y: 40 },
        { x: 85, y: 70 }
      ],
      defenders: [
        { x: 50, y: 40 },
        { x: 50, y: 60 },
        { x: 37, y: 48 },
        { x: 70, y: 60 }
      ],
      neutrals: [
        { x: 55, y: 25 },
        { x: 55, y: 75 }
      ],
      cones: [],
      arrows: [],
      goals: [],
      lines: [
        { startX: 10, startY: 15, endX: 90, endY: 15, style: "solid", color: "white" },
        { startX: 10, startY: 85, endX: 90, endY: 85, style: "solid", color: "white" },
        { startX: 10, startY: 15, endX: 10, endY: 85, style: "solid", color: "white" },
        { startX: 90, startY: 15, endX: 90, endY: 85, style: "solid", color: "white" },
        { startX: 37, startY: 15, endX: 37, endY: 85, style: "dashed", color: "white" },
        { startX: 63, startY: 15, endX: 63, endY: 85, style: "dashed", color: "white" }
      ],
      ball: { x: 20, y: 70 }
    }
  },
  {
    id: "break-past-halfway",
    title: "Break Past Halfway",
    description: "Building game with 3v3+2+GK in one half and 1v1+GK in the other, focusing on breaking through the halfway line.",
    category: "positioning-games",
    topics: ["building"],
    duration: "4x4min",
    players: "12",
    areaSize: "15x25m",
    rules: [
      "3v3 + 2 + GK in one half of the pitch",
      "1v1 + GK in opposite half of pitch",
      "Can be played or driven over the halfway line",
      "Play through to 9 and create +1",
      "Play high wingbacks to get through",
      "10 link ups to get beyond"
    ],
    coachingPoints: [
      "Forward passes = forward runs",
      "Runs towards the goals",
      "Avoid the corners",
      "Weakside fb and 6 rest defence",
      "Crosses along the floor"
    ],
    diagram: {
      type: "full-pitch-empty",
      attackers: [
        { x: 5, y: 50, label: "GK" },
        { x: 25, y: 35 },
        { x: 30, y: 55 },
        { x: 35, y: 65 },
        { x: 95, y: 50, label: "GK" },
        { x: 75, y: 50 }
      ],
      defenders: [
        { x: 40, y: 35 },
        { x: 45, y: 55 },
        { x: 50, y: 75 },
        { x: 70, y: 55 }
      ],
      neutrals: [
        { x: 30, y: 18 },
        { x: 30, y: 82 }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 3, y: 50, width: 16, rotation: 90 },
        { x: 97, y: 50, width: 16, rotation: -90 }
      ],
      zones: [
        { x: 10, y: 15, width: 40, height: 70, color: "rgba(255, 255, 255, 0.1)" },
        { x: 50, y: 15, width: 40, height: 70, color: "rgba(255, 255, 255, 0.1)" }
      ],
      lines: [
        { startX: 10, startY: 15, endX: 90, endY: 15, style: "solid", color: "white" },
        { startX: 10, startY: 85, endX: 90, endY: 85, style: "solid", color: "white" },
        { startX: 10, startY: 15, endX: 10, endY: 85, style: "solid", color: "white" },
        { startX: 90, startY: 15, endX: 90, endY: 85, style: "solid", color: "white" },
        { startX: 50, startY: 15, endX: 50, endY: 85, style: "dashed", color: "white" },
        { startX: 10, startY: 30, endX: 20, endY: 30, style: "dashed", color: "white" },
        { startX: 20, startY: 30, endX: 20, endY: 70, style: "dashed", color: "white" },
        { startX: 10, startY: 70, endX: 20, endY: 70, style: "dashed", color: "white" },
        { startX: 80, startY: 30, endX: 90, endY: 30, style: "dashed", color: "white" },
        { startX: 80, startY: 30, endX: 80, endY: 70, style: "dashed", color: "white" },
        { startX: 80, startY: 70, endX: 90, endY: 70, style: "dashed", color: "white" }
      ],
      ball: { x: 22, y: 50 }
    }
  },
  {
    id: "bpo-3-1-9v9-version",
    title: "BPO 3+1 9v9 Version",
    description: "9v9 game training focusing on BPO 3+1 defensive principles with side-on defending, press cover, and weakside connections.",
    category: "game-training",
    topics: ["bpo-3+1"],
    duration: "3x4mins",
    players: "9v9",
    areaSize: "24x35m",
    rules: [
      "5v5+1",
      "Blue into minis",
      "Red into big goals"
    ],
    coachingPoints: [
      "Side on defending",
      "Delay",
      "3+1",
      "Triggers to step + drop",
      "Press cover",
      "Weakside connect",
      "Line with the ball if past the 18yard box"
    ],
    diagram: {
      type: "full-pitch-empty",
      attackers: [
        { x: 38, y: 65, label: "6" },
        { x: 38, y: 45, label: "3" },
        { x: 60, y: 35, label: "10" },
        { x: 60, y: 70, label: "8" },
        { x: 70, y: 45, label: "9" }
      ],
      defenders: [
        { x: 55, y: 20, label: "2" },
        { x: 75, y: 30, label: "4" },
        { x: 55, y: 50, label: "6" },
        { x: 78, y: 55, label: "3" },
        { x: 78, y: 75, label: "5" },
        { x: 95, y: 50, label: "GK" }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 20, y: 25, width: 6, rotation: 90 },
        { x: 20, y: 75, width: 6, rotation: 90 },
        { x: 97, y: 50, width: 16, rotation: -90 }
      ],
      zones: [],
      lines: [
        { startX: 85, startY: 35, endX: 100, endY: 35, style: "solid", color: "white" },
        { startX: 85, startY: 35, endX: 85, endY: 65, style: "solid", color: "white" },
        { startX: 85, startY: 65, endX: 100, endY: 65, style: "solid", color: "white" }
      ],
      ball: { x: 42, y: 48 }
    }
  },
  {
    id: "up-back-and-through",
    title: "Up, Back and Through",
    description: "5v5+2 positioning game focusing on playing into the middle third to progress. FBs can advance up the pitch for both teams.",
    category: "positioning-games",
    topics: ["mid-play"],
    duration: "2x8mins",
    players: "13",
    areaSize: "24x60m",
    rules: [
      "5v5+2",
      "Whites must play into the middle third to get beyond",
      "FBs can progress up the pitch for both teams"
    ],
    coachingPoints: [
      "Up, back, through",
      "Runs before ball",
      "Tempo",
      "Support under and over"
    ],
    diagram: {
      type: "full-pitch-empty",
      attackers: [
        { x: 12, y: 50, label: "4" },
        { x: 42, y: 60, label: "6" },
        { x: 45, y: 35, label: "8" },
        { x: 65, y: 28, label: "9" },
        { x: 65, y: 72, label: "10" }
      ],
      defenders: [
        { x: 95, y: 50, label: "GK" },
        { x: 65, y: 65, label: "2" },
        { x: 65, y: 35, label: "3" },
        { x: 55, y: 60, label: "6" },
        { x: 55, y: 40, label: "8" },
        { x: 38, y: 45, label: "9" }
      ],
      neutrals: [
        { x: 50, y: 15, label: "5" },
        { x: 50, y: 85, label: "2" }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 3, y: 40, width: 6, rotation: 90 },
        { x: 3, y: 60, width: 6, rotation: 90 },
        { x: 97, y: 50, width: 16, rotation: -90 }
      ],
      zones: [],
      lines: [
        { startX: 5, startY: 10, endX: 95, endY: 10, style: "solid", color: "white" },
        { startX: 5, startY: 90, endX: 95, endY: 90, style: "solid", color: "white" },
        { startX: 5, startY: 10, endX: 5, endY: 90, style: "solid", color: "white" },
        { startX: 95, startY: 10, endX: 95, endY: 90, style: "solid", color: "white" },
        { startX: 35, startY: 10, endX: 35, endY: 90, style: "dashed", color: "white" },
        { startX: 65, startY: 10, endX: 65, endY: 90, style: "dashed", color: "white" },
        { startX: 85, startY: 35, endX: 95, endY: 35, style: "solid", color: "white" },
        { startX: 85, startY: 35, endX: 85, endY: 65, style: "solid", color: "white" },
        { startX: 85, startY: 65, endX: 95, endY: 65, style: "solid", color: "white" }
      ],
      ball: { x: 20, y: 50 }
    }
  },
  {
    id: "up-back-and-through-gt",
    title: "Up, Back and Through GT",
    description: "6v6+1 game training focusing on playing up, back, and through with runs before the ball and tempo.",
    category: "game-training",
    topics: ["mid-play"],
    duration: "3x6mins",
    players: "13",
    areaSize: "Half pitch",
    rules: [
      "6v6+1"
    ],
    coachingPoints: [
      "Up, back, through",
      "Runs before ball",
      "Tempo",
      "Support under and over"
    ],
    diagram: {
      type: "full-pitch-empty",
      attackers: [
        { x: 50, y: 8, label: "6" },
        { x: 8, y: 25, label: "2" },
        { x: 70, y: 20, label: "8" },
        { x: 50, y: 55, label: "10" },
        { x: 82, y: 55, label: "9" },
        { x: 92, y: 25, label: "5" }
      ],
      defenders: [
        { x: 50, y: 92, label: "GK" },
        { x: 45, y: 35, label: "6" },
        { x: 70, y: 35, label: "8" },
        { x: 30, y: 55, label: "5" },
        { x: 50, y: 60, label: "4" },
        { x: 70, y: 60, label: "3" },
        { x: 90, y: 55, label: "2" }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 30, y: 3, width: 6, rotation: 0 },
        { x: 50, y: 3, width: 6, rotation: 0 },
        { x: 70, y: 3, width: 6, rotation: 0 }
      ],
      zones: [
        { x: 28, y: 81, width: 44, height: 14, color: "transparent" }
      ],
      lines: [
        { startX: 28, startY: 81, endX: 72, endY: 81, style: "solid", color: "white" },
        { startX: 28, startY: 81, endX: 28, endY: 95, style: "solid", color: "white" },
        { startX: 72, startY: 81, endX: 72, endY: 95, style: "solid", color: "white" },
        { startX: 38, startY: 89, endX: 62, endY: 89, style: "solid", color: "white" },
        { startX: 38, startY: 89, endX: 38, endY: 95, style: "solid", color: "white" },
        { startX: 62, startY: 89, endX: 62, endY: 95, style: "solid", color: "white" }
      ],
      ball: { x: 50, y: 14 }
    }
  },
  {
    id: "3v3-3-press-box",
    title: "3v3+3 Press Box",
    description: "Small-sided transition game focusing on counterpress and defensive principles. Two teams verse one - the team that loses possession becomes the defending team and must win the ball back immediately.",
    category: "positioning-games",
    topics: ["transitions"],
    duration: "5x3 min",
    players: "9",
    areaSize: "15x15m",
    rules: [
      "15×15m area",
      "Two teams versing one",
      "Team that loses possession will be the defending team and win the ball back immediately",
      "1v1 Defending focus",
      "Press and cover principles"
    ],
    coachingPoints: [
      "Be aggressive when pressing",
      "Closest player presses ball carrier",
      "Pressor to arc run to block passing lane and force into an area the defender wants",
      "Supporting defenders make pitch as small as possible",
      "Supporting defenders to block closest passing lane to prevent exit pass",
      "If ball carrier turns back, apply maximum pressure to win ball back"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 20, y: 35, label: "4" },
        { x: 25, y: 70, label: "2" },
        { x: 75, y: 55, label: "3" }
      ],
      defenders: [
        { x: 35, y: 20, label: "4" },
        { x: 65, y: 55, label: "2" },
        { x: 35, y: 85, label: "3" }
      ],
      neutrals: [
        { x: 20, y: 55, label: "4" },
        { x: 55, y: 35, label: "2" },
        { x: 50, y: 75, label: "3" }
      ],
      ball: { x: 68, y: 55 }
    }
  },
  {
    id: "rapid-1v1-battles",
    title: "Rapid 1v1 Battles",
    description: "Continuous 1v1 drill focusing on pressing triggers, forcing errors, and quick transitions. Green players look to play across to teammates while red players stay inside to force errors or win the ball.",
    category: "brilliant-basics",
    topics: ["bpo-1", "bpo-3+1"],
    duration: "3x3 min",
    players: "10+",
    areaSize: "20x20m",
    rules: [
      "Continuous 1v1s",
      "Green look to get an opportunity to play to their teammate across",
      "Red will stay inside until they force an error or win the ball and play a teammate",
      "This will cause a swift rotation where a red player will aim to complete the same task",
      "The Green that misplaces the pass needs to Action Reaction and defend immediately"
    ],
    coachingPoints: [
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "Speed, Slowdown, Showdown",
      "FBO/Shield",
      "Action Reaction"
    ],
    diagram: {
      type: "small-grid",
      attackers: [],
      defenders: [
        { x: 42, y: 10, label: "6" },
        { x: 58, y: 10, label: "2" },
        { x: 42, y: 90, label: "3" },
        { x: 58, y: 90, label: "4" },
        { x: 55, y: 42, label: "5" }
      ],
      neutrals: [
        { x: 10, y: 42, label: "6" },
        { x: 10, y: 58, label: "2" },
        { x: 90, y: 42, label: "3" },
        { x: 90, y: 58, label: "4" },
        { x: 26, y: 50, label: "5" }
      ],
      mannequins: [
        { x: 50, y: 20 },
        { x: 25, y: 50 },
        { x: 75, y: 50 },
        { x: 50, y: 80 }
      ],
      arrows: [
        { startX: 35, startY: 50, endX: 55, endY: 50, type: "pass" }
      ],
      ball: { x: 33, y: 50 }
    }
  },
  {
    id: "delay-and-recover-gt",
    title: "Delay and Recover",
    description: "Game training drill focusing on defensive transition. Coach plays Green to score 6v4, then immediately plays to Red team. Green must delay, recover and look to possess or progress back to the mini goals.",
    category: "game-training",
    topics: ["bpo-3+1", "transitions"],
    duration: "2x8 min",
    players: "13",
    areaSize: "40x40m",
    rules: [
      "Ball starts with the coach",
      "Coach plays blue to score into the minis 6v6",
      "Coach then plays second phase to Red team ASAP",
      "Blue must delay, recover and look to possess/progress back to the minis"
    ],
    coachingPoints: [
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "3+1 positioning",
      "Delay, Recover",
      "Control which way they play",
      "Restrain forward momentum"
    ],
    diagram: {
      type: "full-pitch-top-only",
      zones: [],
      attackers: [
        { x: 50, y: 12, label: "GK" },
        { x: 28, y: 38 },
        { x: 45, y: 35 },
        { x: 55, y: 35 },
        { x: 72, y: 38 },
        { x: 42, y: 55 },
        { x: 58, y: 55 },
      ],
      defenders: [
        { x: 10, y: 45 },
        { x: 40, y: 42 },
        { x: 55, y: 42 },
        { x: 92, y: 45 },
        { x: 35, y: 70 },
        { x: 65, y: 70 },
      ],
      neutrals: [
        { x: 50, y: 92, label: "C" }
      ],
      ball: { x: 50, y: 88 },
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 20, rotation: 0 },
        { x: 38, y: 85, width: 8, rotation: 180 },
        { x: 62, y: 85, width: 8, rotation: 180 },
      ],
      cones: [],
    }
  },
  {
    id: "kick-off-pattern",
    title: "Kick Off",
    description: "Kick off routine that opens to a game. Red need to win and receive at halfway zone while Greens look to score in the big goal.",
    category: "patterns",
    topics: ["building"],
    duration: "3x6 min",
    players: "14",
    areaSize: "Half pitch",
    rules: [
      "Kick off routine",
      "Opens to a game",
      "Red need to win and receive at halfway zone",
      "Greens look to score big goal",
      "Play back to MF, Play beyond to wide runners for early deliveries"
    ],
    coachingPoints: [
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate",
      "3+1"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [],
      defenders: [
        { x: 50, y: 8, label: "GK" },
        { x: 50, y: 40, label: "9" },
        { x: 25, y: 22, label: "2" },
        { x: 40, y: 22, label: "3" },
        { x: 60, y: 22, label: "4" },
        { x: 75, y: 22, label: "5" },
        { x: 55, y: 32, label: "6" }
      ],
      neutrals: [
        { x: 50, y: 55, label: "9", color: "#22C55E" },
        { x: 50, y: 68, label: "6", color: "#22C55E" },
        { x: 95, y: 50, label: "7", color: "#22C55E" },
        { x: 85, y: 50, label: "10", color: "#22C55E" },
        { x: 75, y: 50, label: "8", color: "#22C55E" },
        { x: 85, y: 60, label: "2", color: "#22C55E" },
        { x: 25, y: 50, label: "11", color: "#22C55E" }
      ],
      mannequins: [],
      goals: [
        { x: 50, y: 2, width: 16, rotation: 0 },
      ],
      ball: { x: 50, y: 50 },
      arrows: [
        { startX: 85, startY: 50, endX: 82, endY: 24, type: "movement" },
        { startX: 95, startY: 50, endX: 92, endY: 24, type: "movement" },
        { startX: 75, startY: 50, endX: 72, endY: 24, type: "movement" },
        { startX: 85, startY: 60, endX: 82, endY: 45, type: "movement" }
      ],
      cones: [],
    }
  },
  {
    id: "8v7-press",
    title: "8v7 Press",
    description: "8v7+GK on a narrowed half pitch. Focus on Block to Press principles with green team in possession (2-4-1) and red team defending (2-4-2).",
    category: "game-training",
    topics: ["bpo-1"],
    duration: "2x8 min",
    players: "16",
    areaSize: "Half pitch, 4 steps in",
    rules: [
      "8v7+GK - narrowed pitch",
      "15 balls before change over or 8 mins",
      "BP (Green) 2-4-1",
      "BPO 2-4-2",
      "Focus on Block to Press",
      "Restarts at kick off",
      "Rewarded if score",
      "Green to break out and score in any of the goals",
      "1pt wide / 2pt central",
      "Reds to win ball and score ASAP"
    ],
    coachingPoints: [
      "Defend your lane (Scan as you press)",
      "Reduce distance as ball moves",
      "Jump when ball moves",
      "Press cover",
      "Don't over commit"
    ],
    diagram: {
      type: "full-pitch-bottom-only",
      attackers: [],
      defenders: [
        { x: 38, y: 25, label: "3" },
        { x: 62, y: 25, label: "4" },
        { x: 20, y: 52, label: "7" },
        { x: 40, y: 45, label: "6" },
        { x: 60, y: 45, label: "8" },
        { x: 80, y: 52, label: "11" },
        { x: 35, y: 77, label: "9" },
        { x: 65, y: 77, label: "10" },
      ],
      neutrals: [
        { x: 50, y: 92, label: "GK", color: "#22C55E" },
        { x: 28, y: 92, color: "#22C55E" },
        { x: 72, y: 92, color: "#22C55E" },
        { x: 5, y: 65, color: "#22C55E" },
        { x: 40, y: 55, color: "#22C55E" },
        { x: 60, y: 55, color: "#22C55E" },
        { x: 95, y: 65, color: "#22C55E" },
        { x: 50, y: 25, color: "#22C55E" },
      ],
      mannequins: [],
      goals: [
        { x: 25, y: 5, width: 8, rotation: 0 },
        { x: 50, y: 5, width: 8, rotation: 0 },
        { x: 75, y: 5, width: 8, rotation: 0 },
      ],
      ball: { x: 50, y: 88 },
      arrows: [],
      cones: [],
    }
  },
  {
    id: "fbo-forward-gt",
    title: "FBO and Forward GT",
    description: "3v3+1 break out game focusing on first ball out and creating forward overloads during transitions.",
    category: "game-training",
    topics: ["transitions"],
    duration: "3x8 mins",
    players: "16",
    areaSize: "Half pitch",
    rules: [
      "3v3 break out game",
      "Players outside the box passing a ball b/w two until break (REACTION)",
      "Players outside the box passing b/w 2 until break (REACTION)",
      "Blue break out (1st out - 2nd forward)",
      "Coach feed to free 6 if red score or rally takes too long",
      "Block player swaps (swap sides)"
    ],
    coachingPoints: [
      "First ball out",
      "Second forward",
      "Create overloads forward",
      "ACTION REACTION",
      "Fill 3 lines off forward pass",
      "Attacking principles recap: Cutback, touch deliver, FP, Pen, BP, active def"
    ],
    diagram: {
      type: "full-pitch-no-bottom-box",
      attackers: [
        { x: 15, y: 65, label: "5" },
        { x: 85, y: 65, label: "2" },
        { x: 50, y: 65, label: "6" },
        { x: 65, y: 52, label: "8" },
        { x: 15, y: 35, label: "11" },
        { x: 85, y: 45, label: "7" },
        { x: 40, y: 30, label: "9" },
        { x: 60, y: 30, label: "10" },
      ],
      defenders: [
        { x: 25, y: 25, label: "2" },
        { x: 40, y: 25, label: "3" },
        { x: 60, y: 25, label: "4" },
        { x: 75, y: 35, label: "5" },
        { x: 45, y: 40, label: "6" },
        { x: 65, y: 40, label: "8" },
        { x: 75, y: 55, label: "11" },
      ],
      neutrals: [
        { x: 50, y: 8, label: "1", color: "#FBBF24" },
      ],
      mannequins: [],
      ball: { x: 75, y: 40 },
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 16, rotation: 0 },
        { x: 25, y: 65, width: 6, rotation: 0 },
        { x: 75, y: 65, width: 6, rotation: 0 },
      ],
      cones: [],
      zones: [
        { x: 60, y: 35, width: 36, height: 30, color: "rgba(59, 130, 246, 0.2)" },
      ],
    }
  },
  {
    id: "action-reaction-gt",
    title: "Action Reaction GT",
    description: "6v5 game training focusing on quick transitions, first ball out and creating forward overloads.",
    category: "game-training",
    topics: ["transitions"],
    duration: "2x8 mins",
    players: "14",
    areaSize: "Half Pitch length, 60x40m",
    rules: [
      "15 balls before change over or 10 mins",
      "Restarts at kick off",
      "Reds to win ball and score ASAP",
      "If ball goes out the opposing team can restart with any side line ball immediately"
    ],
    coachingPoints: [
      "First ball out",
      "Second forward",
      "Create overloads forward",
      "Action reaction",
      "Delay and recover"
    ],
    diagram: {
      type: "full-pitch-top-only",
      attackers: [
        { x: 95, y: 25, label: "7" },
        { x: 5, y: 25, label: "11" },
        { x: 55, y: 40, label: "10" },
        { x: 45, y: 25, label: "9" },
        { x: 35, y: 68, label: "6" },
        { x: 65, y: 68, label: "8" },
      ],
      defenders: [
        { x: 25, y: 25, label: "2" },
        { x: 40, y: 25, label: "3" },
        { x: 60, y: 25, label: "4" },
        { x: 75, y: 25, label: "5" },
        { x: 35, y: 40, label: "6" },
        { x: 65, y: 40, label: "8" },
        { x: 50, y: 55, label: "10" },
        { x: 50, y: 8, label: "GK" },
      ],
      neutrals: [],
      mannequins: [],
      goals: [
        { x: 50, y: 2, width: 16, rotation: 0 },
        { x: 25, y: 97, width: 6, rotation: 180 },
        { x: 75, y: 97, width: 6, rotation: 180 },
      ],
      zones: [],
      ball: { x: 35, y: 63 },
      balls: [
        { x: 5, y: 40 },
        { x: 5, y: 55 },
        { x: 5, y: 70 },
        { x: 95, y: 40 },
        { x: 95, y: 55 },
        { x: 95, y: 70 },
      ],
      arrows: [],
      cones: [],
    }
  },
  {
    id: "atk-transition-gt",
    title: "ATK transition GT",
    description: "Transition game training focusing on attacking overloads, quick forward play and counter-attacking principles.",
    category: "game-training",
    topics: ["transitions"],
    duration: "3x4 mins",
    players: "12",
    areaSize: "40x55m",
    rules: [
      "4v2 in central box",
      "Blue 2 win ball > Progress or Possess",
      "After breakout if blues score reset",
      "If blues miss GK will start a counter towards minis",
      "If reds win the ball back they counter towards minis"
    ],
    coachingPoints: [
      "First ball out, Second forward",
      "Create overloads forward",
      "Attacking principles recap Cutback, touch deliver, FP, Pen, BP, active def",
      "FWD's Pin",
      "Reach furthest forward player if on, drive if not or secure possession if not",
      "Support under over"
    ],
    diagram: {
      type: "full-pitch-no-bottom-box",
      attackers: [
        { x: 50, y: 65, label: "6" },
        { x: 45, y: 50, label: "8" },
        { x: 15, y: 45, label: "11" },
        { x: 85, y: 45, label: "7" },
        { x: 40, y: 30, label: "9" },
        { x: 55, y: 50, label: "10" },
      ],
      defenders: [
        { x: 32, y: 50, label: "2" },
        { x: 40, y: 25, label: "3" },
        { x: 60, y: 25, label: "4" },
        { x: 45, y: 40, label: "6" },
        { x: 65, y: 55, label: "8" },
        { x: 50, y: 65, label: "9" },
      ],
      neutrals: [
        { x: 50, y: 8, label: "1", color: "#FBBF24" },
      ],
      mannequins: [],
      ball: { x: 50, y: 60 },
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 16, rotation: 0 },
        { x: 25, y: 65, width: 6, rotation: 0 },
        { x: 75, y: 65, width: 6, rotation: 0 },
      ],
      cones: [
        { x: 90, y: 45 },
        { x: 10, y: 45 },
      ],
      zones: [
        { x: 32, y: 35, width: 36, height: 30, color: "rgba(59, 130, 246, 0.2)" },
      ],
    }
  },
  {
    id: "fast-transitions",
    title: "Fast Transitions",
    description: "4v4 positioning game with wide players focusing on quick transitions, counter-attacking and combination play.",
    category: "positioning-games",
    topics: ["transitions"],
    duration: "3x6 mins",
    players: "10",
    areaSize: "40x20m",
    rules: [
      "The players in the wide positions must not be in the same half as the players playing 4v4, creating a wide option if and when the defending team wins the ball",
      "If the ball is won by the defending team, they must attempt to play to one of the wide players and support the half they are attacking, allowing the wide player to cross, creating a counter-attacking situation",
      "Once the wide players play inside, they must move to the opposite half creating an option for the defending team again",
      "If the defending team recovers well and the attacking team cannot counter attack quickly, the attacking team can keep possession and try to score a goal with combination play",
      "Block 3 - 5v5"
    ],
    coachingPoints: [
      "Progression vs Possession",
      "Hit highest point of pass as quickly as possible (1st out and forward)",
      "Secure FBO"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 35, y: 60, label: "8" },
        { x: 55, y: 60, label: "2" },
        { x: 45, y: 70, label: "3" },
        { x: 55, y: 80, label: "4" },
      ],
      defenders: [
        { x: 40, y: 90, label: "8" },
        { x: 45, y: 65, label: "2" },
        { x: 65, y: 70, label: "3" },
        { x: 45, y: 80, label: "4" },
      ],
      neutrals: [
        { x: 5, y: 45, label: "11", color: "#FBBF24" },
        { x: 95, y: 45, label: "7", color: "#FBBF24" },
      ],
      mannequins: [],
      goals: [
        { x: 25, y: 2, width: 6, rotation: 0 },
        { x: 75, y: 2, width: 6, rotation: 0 },
        { x: 25, y: 98, width: 6, rotation: 180 },
        { x: 75, y: 98, width: 6, rotation: 180 },
      ],
      zones: [],
      ball: { x: 40, y: 80 },
      balls: [],
      arrows: [],
      cones: [],
      lines: [
        { startX: 0, startY: 50, endX: 100, endY: 50, style: "dashed", color: "yellow" },
      ],
    }
  },
  {
    id: "cover-on-jump",
    title: "Cover on CB jump",
    description: "Game training focusing on pressing quality, anticipation, and compact cover when teammates jump to press.",
    category: "game-training",
    topics: ["bpo-1"],
    duration: "2x8 mins",
    players: "14",
    areaSize: "Box to Box",
    rules: [
      "Blues to play out and dribble or receive into zone at halfway to score",
      "Reds look to press, win, score in the big goals",
      "BP 1-2-3-1",
      "BPO 3-2-2"
    ],
    coachingPoints: [
      "Press quality the ball quickly and give no time to the attacker",
      "Anticipation ready to step towards the attacker as the ball is played",
      "Close down space and prevent attacker from facing forward",
      "Once attacker is facing backward, keep your arm outstretched to prevent them from turning",
      "As you get close, find a moment to win the ball with your front foot",
      "Progress or posses through FBO",
      "Compact/Cover when a teammate jumps"
    ],
    diagram: {
      type: "full-pitch",
      attackers: [
        { x: 50, y: 5, label: "GK" },
        { x: 30, y: 5, label: "3" },
        { x: 70, y: 5, label: "4" },
        { x: 42, y: 22, label: "6" },
        { x: 60, y: 28, label: "8" },
        { x: 40, y: 38, label: "10" },
        { x: 50, y: 50, label: "9" }
      ],
      defenders: [
        { x: 48, y: 30, label: "6" },
        { x: 60, y: 36, label: "8" },
        { x: 35, y: 22, label: "10" },
        { x: 65, y: 22, label: "9" },
        { x: 35, y: 55, label: "4" },
        { x: 50, y: 55, label: "3" },
        { x: 65, y: 55, label: "2" }
      ],
      neutrals: [],
      zones: [
        { x: 25, y: 0, width: 50, height: 62, color: "rgba(134, 239, 172, 0.3)" },
      ],
      cones: [],
      ball: { x: 50, y: 10 },
      arrows: [],
      goals: [
        { x: 50, y: 0, width: 12, rotation: 90 }
      ],
      lines: [
        { startX: 0, startY: 50, endX: 100, endY: 50, style: "dotted", color: "rgba(255,255,255,0.6)" },
        { startX: 0, startY: 62, endX: 100, endY: 62, style: "dotted", color: "rgba(255,255,255,0.6)" }
      ]
    }
  },
  {
    id: "atk-v-def-bpo3",
    title: "ATK v DEF GT",
    description: "7v8 game training focusing on attacking vs defensive principles, pressing angles and emergency defending.",
    category: "game-training",
    topics: ["bpo-3+1"],
    duration: "2x8 mins",
    players: "15",
    areaSize: "8m wider than width of box and 5m in from Halfway",
    rules: [
      "Attack vs Defence. 7v8. GKs are two touch",
      "Start play with direct ball into CBs to clear and play live",
      "In Possession: 2-2-3",
      "Out of Possession: 3-2-1"
    ],
    coachingPoints: [
      "Angle of Approach",
      "Side On - Force Wide",
      "Timing of Tackle",
      "Connection + Press + Cover",
      "Block and Press on Triggers",
      "Line Broken = Positioning of furthest defender to be 2nd Defender",
      "Pressing cues (open ball)",
      "3+1 emergency defending"
    ],
    diagram: {
      type: "full-pitch-empty",
      attackers: [
        { x: 20, y: 50, label: "6" },
        { x: 60, y: 35, label: "10" },
        { x: 60, y: 70, label: "8" },
        { x: 70, y: 45, label: "9" },
        { x: 55, y: 5, label: "11" },
        { x: 78, y: 90, label: "7" },
        { x: 32, y: 75, label: "2" }
      ],
      defenders: [
        { x: 55, y: 20, label: "2" },
        { x: 75, y: 30, label: "4" },
        { x: 55, y: 42, label: "6" },
        { x: 38, y: 55, label: "10" },
        { x: 60, y: 62, label: "8" },
        { x: 78, y: 55, label: "3" },
        { x: 78, y: 75, label: "5" },
        { x: 95, y: 50, label: "GK" }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 20, y: 25, width: 6, rotation: 90 },
        { x: 20, y: 75, width: 6, rotation: 90 },
        { x: 97, y: 50, width: 16, rotation: -90 }
      ],
      zones: [],
      lines: [
        { startX: 85, startY: 35, endX: 100, endY: 35, style: "solid", color: "white" },
        { startX: 85, startY: 35, endX: 85, endY: 65, style: "solid", color: "white" },
        { startX: 85, startY: 65, endX: 100, endY: 65, style: "solid", color: "white" }
      ],
      ball: { x: 28, y: 50 }
    }
  },
  {
    id: "press-and-cover-ssg",
    title: "Press and Cover SSG",
    description: "Small sided game focusing on 1v1 defending, press cover, and blocking forward passes.",
    category: "positioning-games",
    topics: ["bpo-1"],
    duration: "2x6 mins",
    players: "9",
    areaSize: "35x22m",
    rules: [
      "Attacking team attempt to work the ball into the defenders playing area before scoring in one of two target goals",
      "Defenders attempt to win possession and score on goal",
      "Defending players may not re enter their own half when they cross into the opposite playing zone"
    ],
    coachingPoints: [
      "1v1 defending",
      "Def your lane",
      "Press Cover",
      "Block forward pass",
      "Pressing Triggers",
      "Force one way and isolate"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 30, y: 15, label: "3" },
        { x: 50, y: 10, label: "GK" },
        { x: 90, y: 15, label: "4" },
        { x: 15, y: 40, label: "2" },
        { x: 92, y: 40, label: "5" }
      ],
      defenders: [
        { x: 42, y: 35, label: "9" },
        { x: 55, y: 48, label: "10" },
        { x: 38, y: 65, label: "6" },
        { x: 60, y: 68, label: "8" }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 16, rotation: 0 },
        { x: 15, y: 98, width: 6, rotation: 180 },
        { x: 85, y: 98, width: 6, rotation: 180 }
      ],
      zones: [],
      lines: [
        { startX: 0, startY: 50, endX: 100, endY: 50, style: "dashed", color: "yellow" }
      ],
      ball: { x: 32, y: 18 }
    }
  },
  {
    id: "fb-advantage",
    title: "FB advantage",
    description: "Game training exercise focusing on creating and finishing with fullback overloads.",
    category: "game-training",
    topics: ["creating-finishing"],
    duration: "2x8 mins",
    players: "16",
    areaSize: "Half pitch",
    rules: [
      "Blues look to score in the big goal",
      "Reds will look to win the ball and play into the minis"
    ],
    coachingPoints: [
      "2/5: If the 7/11 inject centrally, can you provide an option beyond the 7/11 or support them underneath?",
      "2/5: If the 6/8/10 receive the ball on their furthest foot coming across, can you provide a line to stretch the pitch horizontally?",
      "6: If a ball is played beyond you, can you get in a line of pass underneath so we can play the next pass forward?",
      "6: As the ball is played from one side of the pitch, try to receive it on your furthest foot to expose the oppositions weak side",
      "6: If we play a pass to team mate in a tight area, can you have quick 2nd movement to provide another line of pass?",
      "7/11: As your team mates combine centrally, can you create width and threaten their last line?",
      "7/11: If there is space in the pocket, can you receive and drive, or play quickly out of the pocket?",
      "7/11: If your team mate has the ball open, can you make a run beyond to threaten through the channel?",
      "7/11: If your 2/5 overlaps you, can you support them by providing a line to retain or an option to penetrate beyond if the ball is dead?"
    ],
    diagram: {
      type: "full-pitch-top-only",
      attackers: [
        { x: 8, y: 35, label: "11" },
        { x: 25, y: 55, label: "10" },
        { x: 50, y: 40, label: "9" },
        { x: 75, y: 55, label: "8" },
        { x: 92, y: 35, label: "7" },
        { x: 8, y: 80, label: "5" },
        { x: 50, y: 85, label: "6" },
        { x: 92, y: 80, label: "2" }
      ],
      defenders: [
        { x: 25, y: 38, label: "" },
        { x: 45, y: 32, label: "" },
        { x: 55, y: 32, label: "" },
        { x: 75, y: 38, label: "" },
        { x: 40, y: 55, label: "" },
        { x: 60, y: 55, label: "" },
        { x: 50, y: 68, label: "" }
      ],
      neutrals: [
        { x: 50, y: 15, label: "GK" }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 16, rotation: 0 },
        { x: 15, y: 98, width: 6, rotation: 180 },
        { x: 50, y: 98, width: 6, rotation: 180 },
        { x: 85, y: 98, width: 6, rotation: 180 }
      ],
      zones: [],
      lines: [],
      ball: { x: 50, y: 78 }
    }
  },
  {
    id: "penetrate-beyond",
    title: "Penetrate Beyond",
    description: "7v6 game training focusing on getting beyond the defensive line to score from touch deliver, cutback zone, or first time finish.",
    category: "game-training",
    topics: ["creating-finishing"],
    duration: "2x10 mins",
    players: "14",
    areaSize: "35x30m",
    rules: [
      "7v6 - BP team setup 3-1-3, BPO team setup 4-2",
      "BP team look to play to get beyond and score from touch deliver or cutback zone or first time finish into side netting",
      "BPO team looks to keep a high line until the offside line is broken (discs can be placed for starting positions)",
      "BPO look to play forward into minis to score",
      "If BP team score before all BPO team members get into their defensive zone its worth double points (2)",
      "Progression: Touch Restriction, One Touch Finish"
    ],
    coachingPoints: [
      "Creativity in 1v1 Situations - Drive to Open Space",
      "Draw to Release",
      "Timing of Overlap/Underlap/Takeover",
      "Speed of Switch to Find Free Player",
      "Surf the line forward runs",
      "Creating wide overloads"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 35, y: 10, label: "7" },
        { x: 35, y: 50, label: "9" },
        { x: 35, y: 90, label: "11" },
        { x: 50, y: 50, label: "8" },
        { x: 75, y: 20, label: "2" },
        { x: 75, y: 50, label: "6" },
        { x: 75, y: 80, label: "5" }
      ],
      defenders: [
        { x: 35, y: 20, label: "5" },
        { x: 35, y: 40, label: "4" },
        { x: 35, y: 60, label: "3" },
        { x: 35, y: 80, label: "2" },
        { x: 50, y: 40, label: "8" },
        { x: 50, y: 60, label: "6" }
      ],
      neutrals: [
        { x: 8, y: 50, label: "GK" }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 2, y: 50, width: 12, rotation: 90 },
        { x: 98, y: 20, width: 6, rotation: 270 },
        { x: 98, y: 50, width: 6, rotation: 270 },
        { x: 98, y: 80, width: 6, rotation: 270 }
      ],
      zones: [
        { x: 10, y: 5, width: 80, height: 90, color: "rgba(218, 165, 32, 0.3)" }
      ],
      lines: [
        { startX: 35, startY: 5, endX: 35, endY: 95, style: "dashed", color: "white" },
        { startX: 75, startY: 5, endX: 75, endY: 95, style: "dashed", color: "white" }
      ],
      balls: [],
      ball: { x: 68, y: 50 }
    }
  },
  {
    id: "2v2-plus-2",
    title: "2v2+2",
    description: "Possession game with neutral players focusing on building play and retaining the ball under pressure.",
    category: "brilliant-basics",
    topics: ["building"],
    duration: "3x4 mins",
    players: "6",
    areaSize: "20x15m",
    rules: [
      "Players in possession attempt to retain the ball",
      "Can adapt to 3v3+3 with mini goals",
      "Defenders attempt to win the ball and score in one of the goals for points",
      "Defenders can press with 1 and def can inject"
    ],
    coachingPoints: [
      "Inject to trigger movement to lose marker",
      "Receive level or beyond your marker",
      "Receive with an open body shape",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 30, y: 50, label: "" },
        { x: 70, y: 50, label: "" }
      ],
      defenders: [
        { x: 35, y: 75, label: "" },
        { x: 65, y: 75, label: "" }
      ],
      neutrals: [
        { x: 35, y: 25, label: "" },
        { x: 65, y: 25, label: "" }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 10, rotation: 0 },
        { x: 50, y: 98, width: 10, rotation: 180 }
      ],
      zones: [
        { x: 5, y: 40, width: 90, height: 20, color: "rgba(218, 165, 32, 0.3)" }
      ],
      lines: [],
      balls: [],
      ball: { x: 35, y: 32 }
    }
  },
  {
    id: "high-reps-building",
    title: "High Reps Building",
    description: "5v4 + GK positioning game focused on building play with high repetitions and quick restarts.",
    category: "positioning-games",
    topics: ["building"],
    duration: "4x4 mins",
    players: "10",
    areaSize: "40x30m",
    rules: [
      "5v4 + GK",
      "Restarts at kick off",
      "Rewarded if score"
    ],
    coachingPoints: [
      "Inject to trigger movement to lose marker",
      "Receive level or beyond your marker",
      "Receive with an open body shape",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 8, y: 45, label: "" },
        { x: 30, y: 70, label: "" },
        { x: 50, y: 88, label: "GK" },
        { x: 55, y: 45, label: "" },
        { x: 92, y: 50, label: "" },
        { x: 75, y: 70, label: "" }
      ],
      defenders: [
        { x: 35, y: 45, label: "" },
        { x: 55, y: 25, label: "" },
        { x: 60, y: 55, label: "" },
        { x: 75, y: 45, label: "" }
      ],
      neutrals: [],
      cones: [],
      arrows: [],
      goals: [
        { x: 30, y: 2, width: 8, rotation: 0 },
        { x: 70, y: 2, width: 8, rotation: 0 },
        { x: 50, y: 98, width: 14, rotation: 180 }
      ],
      zones: [
        { x: 5, y: 5, width: 90, height: 90, color: "rgba(218, 165, 32, 0.3)" },
        { x: 35, y: 82, width: 30, height: 16, color: "rgba(255, 255, 255, 0.3)" }
      ],
      lines: [],
      balls: [],
      ball: { x: 55, y: 88 }
    }
  },
  {
    id: "secret-scorer-fun",
    title: "Secret Scorer Fun",
    description: "6v6+1 positioning game with 4 goals where teams compete with secret scorers for bonus points.",
    category: "positioning-games",
    topics: ["creating-finishing"],
    duration: "2x8 mins",
    players: "13",
    areaSize: "Attacking third - 36x40m",
    rules: [
      "Players compete in 6v6+1",
      "4 goal game",
      "Teams are to select who their 'secret scorer' is and tell the coach",
      "Teams will get 3pts any time their 'secret scorer' scores and 1pt for any other goal",
      "Scores will not be announced until the end of each half",
      "'Secret scorers' will be changed at 'half time'"
    ],
    coachingPoints: [
      "Creativity in 1v1 Situations",
      "Drive to Open Space",
      "Draw to Release",
      "Timing of Overlap/Underlap/Takeover",
      "Combinations to set teammates up for the shot",
      "Movement to create space for others to take the shot"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 25, y: 70, label: "" },
        { x: 50, y: 70, label: "" },
        { x: 75, y: 70, label: "" },
        { x: 25, y: 85, label: "" },
        { x: 50, y: 85, label: "" },
        { x: 75, y: 85, label: "" }
      ],
      defenders: [
        { x: 25, y: 15, label: "" },
        { x: 50, y: 15, label: "" },
        { x: 75, y: 15, label: "" },
        { x: 25, y: 30, label: "" },
        { x: 50, y: 30, label: "" },
        { x: 75, y: 30, label: "" }
      ],
      neutrals: [
        { x: 50, y: 50, label: "" }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 15, y: 2, width: 8, rotation: 0 },
        { x: 85, y: 2, width: 8, rotation: 0 },
        { x: 15, y: 98, width: 8, rotation: 180 },
        { x: 85, y: 98, width: 8, rotation: 180 }
      ],
      zones: [],
      lines: [],
      balls: [
        { x: 45, y: 95 },
        { x: 50, y: 95 },
        { x: 55, y: 95 }
      ]
    }
  },
  {
    id: "inside-combos",
    title: "Inside Combos",
    description: "Positioning game focusing on combinations to penetrate the box and create scoring opportunities.",
    category: "positioning-games",
    topics: ["creating-finishing"],
    duration: "2x8 mins",
    players: "15",
    areaSize: "36x40m",
    rules: [
      "Red 4v2 to get into the edge of the box",
      "Reds are able to make it 4v4 in the box",
      "2 pressers can't recover",
      "Score retain, miss other team gets it",
      "Can shoot from anywhere",
      "Progression: Allow front 2 to recover"
    ],
    coachingPoints: [
      "Creativity in 1v1 Situations",
      "Drive to Open Space",
      "Draw to Release",
      "Timing of Overlap/Underlap/Takeover",
      "Combinations to set teammates up for the shot",
      "Movement to create space for others to take the shot"
    ],
    diagram: {
      type: "full-pitch-empty",
      attackers: [
        { x: 75, y: 35, label: "" },
        { x: 80, y: 45, label: "" },
        { x: 80, y: 55, label: "" },
        { x: 75, y: 65, label: "" },
        { x: 35, y: 45, label: "" },
        { x: 35, y: 55, label: "" }
      ],
      defenders: [
        { x: 25, y: 35, label: "" },
        { x: 20, y: 45, label: "" },
        { x: 20, y: 55, label: "" },
        { x: 25, y: 65, label: "" },
        { x: 65, y: 45, label: "" },
        { x: 65, y: 55, label: "" }
      ],
      neutrals: [
        { x: 50, y: 50, label: "N" },
        { x: 8, y: 50, label: "GK" },
        { x: 92, y: 50, label: "GK" }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 2, y: 50, width: 14, rotation: 90 },
        { x: 98, y: 50, width: 14, rotation: 270 }
      ],
      zones: [
        { x: 2, y: 30, width: 48, height: 40, color: "rgba(255, 255, 255, 0.5)" },
        { x: 50, y: 30, width: 48, height: 40, color: "rgba(255, 255, 255, 0.5)" }
      ],
      lines: [],
      balls: []
    }
  },
  {
    id: "wide-pattern-play",
    title: "Wide Pattern Play",
    description: "Pattern play focusing on wide combinations against two banks of four with emphasis on winger involvement.",
    category: "patterns",
    topics: ["creating-finishing"],
    duration: "3x2.5 mins",
    players: "15",
    areaSize: "Half pitch",
    rules: [
      "Pattern play vs two banks of four",
      "Ball must touch both wingers to score",
      "Competition between teams to score goals",
      "Make game multi-directional with 2nd goal to see if they can score with passive interference"
    ],
    coachingPoints: [
      "Winger 'touch and deliver'",
      "Sacrificial runs",
      "Getting into scoring zones",
      "Rest defence"
    ],
    diagram: {
      type: "full-pitch-top-only",
      attackers: [
        { x: 8, y: 35, label: "" },
        { x: 40, y: 35, label: "" },
        { x: 65, y: 50, label: "" },
        { x: 92, y: 35, label: "" },
        { x: 15, y: 75, label: "" },
        { x: 45, y: 70, label: "" },
        { x: 85, y: 75, label: "" }
      ],
      defenders: [
        { x: 92, y: 45, label: "" },
        { x: 85, y: 82, label: "" },
        { x: 15, y: 82, label: "" },
        { x: 8, y: 42, label: "" },
        { x: 55, y: 70, label: "" },
        { x: 35, y: 50, label: "" },
        { x: 60, y: 35, label: "" }
      ],
      neutrals: [
        { x: 50, y: 12, label: "GK" }
      ],
      mannequins: [
        { x: 30, y: 35 },
        { x: 45, y: 35 },
        { x: 55, y: 35 },
        { x: 70, y: 35 },
        { x: 30, y: 50 },
        { x: 45, y: 50 },
        { x: 55, y: 50 },
        { x: 70, y: 50 }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 2, width: 14, rotation: 0 },
        { x: 50, y: 98, width: 10, rotation: 180 }
      ],
      zones: [],
      lines: [],
      balls: [
        { x: 35, y: 90 },
        { x: 40, y: 90 },
        { x: 60, y: 90 },
        { x: 65, y: 90 }
      ]
    }
  },
  {
    id: "4v4-plus-2-box",
    title: "4v4+2 Box",
    description: "Possession game with inside and outside players working with joker players to keep the ball.",
    category: "positioning-games",
    topics: ["building"],
    duration: "4x3 mins",
    players: "10",
    areaSize: "16x8m box",
    rules: [
      "Players in possession work with two joker players to keep the ball as 6v4",
      "Team in possession are all outside players",
      "Team chasing possession are all inside players",
      "Outside players can invert and play along the line as well",
      "Teams swap if the inside team win ball and get it to one of the two neutrals"
    ],
    coachingPoints: [
      "Inject to trigger movement to lose marker",
      "Receive level or beyond your marker",
      "Receive with an open body shape",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 25, y: 10, label: "11" },
        { x: 75, y: 10, label: "8" },
        { x: 20, y: 75, label: "6" },
        { x: 75, y: 75, label: "10" }
      ],
      defenders: [
        { x: 35, y: 35, label: "4" },
        { x: 65, y: 35, label: "8" },
        { x: 40, y: 55, label: "6" },
        { x: 60, y: 55, label: "10" }
      ],
      neutrals: [
        { x: 92, y: 45, label: "16" },
        { x: 8, y: 45, label: "14" }
      ],
      cones: [],
      arrows: [],
      goals: [],
      zones: [
        { x: 15, y: 25, width: 70, height: 45, color: "rgba(144, 238, 144, 0.4)" }
      ],
      lines: [],
      balls: [
        { x: 45, y: 90 },
        { x: 50, y: 90 },
        { x: 55, y: 90 }
      ]
    }
  },
  {
    id: "6v4-building",
    title: "6v4 Building",
    description: "Possession game where red team works to transfer the ball through zones while blue defenders press and look to win possession.",
    category: "positioning-games",
    topics: ["building"],
    duration: "3x4 mins",
    players: "10",
    areaSize: "12x36m",
    rules: [
      "6v4 - Team in red to keep possession and transfer ball from one end to the other",
      "2 blue defenders can go and press, other 2 must remain in the middle block",
      "If blue win the ball they can immediately shoot in one of the small goals",
      "Top 2 red have one touch to play",
      "1 press Block 1+2, 2 press Block 3+4"
    ],
    coachingPoints: [
      "Inject to trigger movement to lose marker",
      "Receive level or beyond your marker",
      "Receive with an open body shape",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line"
    ],
    diagram: {
      type: "small-grid",
      attackers: [],
      defenders: [
        { x: 50, y: 8, label: "" },
        { x: 25, y: 80, label: "" },
        { x: 50, y: 36, label: "" },
        { x: 75, y: 80, label: "" },
        { x: 50, y: 65, label: "" },
        { x: 50, y: 92, label: "" }
      ],
      neutrals: [
        { x: 35, y: 50, label: "" },
        { x: 65, y: 50, label: "" },
        { x: 35, y: 65, label: "" },
        { x: 65, y: 65, label: "" }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 25, y: 5, width: 8, rotation: 180 },
        { x: 75, y: 5, width: 8, rotation: 180 },
        { x: 25, y: 95, width: 8, rotation: 0 },
        { x: 75, y: 95, width: 8, rotation: 0 }
      ],
      zones: [
        { x: 15, y: 5, width: 70, height: 28, color: "rgba(0, 0, 0, 0.1)" },
        { x: 15, y: 36, width: 70, height: 28, color: "rgba(0, 0, 0, 0.1)" },
        { x: 15, y: 67, width: 70, height: 28, color: "rgba(0, 0, 0, 0.1)" }
      ],
      lines: [],
      balls: [
        { x: 50, y: 85 }
      ]
    }
  },
  {
    id: "the-new-cage",
    title: "The New Cage",
    description: "5v5+2 possession and finishing game with diamond zone where teams build up and attack centrally or wide to create chances.",
    category: "positioning-games",
    topics: ["creating-finishing"],
    duration: "2x6 mins",
    players: "14",
    areaSize: "35x30m",
    rules: [
      "5v5+2 - Must build up into the top 2",
      "From there teams can attack centrally or use wide areas",
      "Look for creativity off the dribble or turn or combo (up, back, through) / (one, twos) to create chances",
      "Outside diamond is a no contact zone",
      "Neutrals have two touches to deliver from anywhere outside the diamond (cutback or cross)"
    ],
    coachingPoints: [
      "One twos",
      "Set and Spin",
      "Pin and Spin",
      "Goal Scoring positions",
      "Winger 'touch and deliver'",
      "Cutbacks"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 45, y: 48, label: "" },
        { x: 65, y: 48, label: "" },
        { x: 55, y: 75, label: "" },
        { x: 68, y: 75, label: "" },
        { x: 42, y: 75, label: "" },
        { x: 50, y: 88, label: "GK" }
      ],
      defenders: [
        { x: 50, y: 18, label: "GK" },
        { x: 35, y: 40, label: "" },
        { x: 50, y: 35, label: "" },
        { x: 65, y: 38, label: "" },
        { x: 48, y: 58, label: "" },
        { x: 58, y: 62, label: "" }
      ],
      neutrals: [
        { x: 8, y: 55, label: "" },
        { x: 92, y: 55, label: "" }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 5, width: 16, rotation: 0 },
        { x: 50, y: 95, width: 16, rotation: 180 }
      ],
      zones: [],
      lines: [
        { startX: 58, startY: 90, endX: 92, endY: 55, style: "dashed", color: "rgba(0,0,0,0.5)" },
        { startX: 92, startY: 55, endX: 58, endY: 10, style: "dashed", color: "rgba(0,0,0,0.5)" },
        { startX: 42, startY: 10, endX: 8, endY: 55, style: "dashed", color: "rgba(0,0,0,0.5)" },
        { startX: 8, startY: 55, endX: 42, endY: 90, style: "dashed", color: "rgba(0,0,0,0.5)" },
        { startX: 8, startY: 55, endX: 92, endY: 55, style: "dashed", color: "rgba(0,0,0,0.5)" }
      ],
      balls: []
    }
  },
  {
    id: "5v2-transfer-boxes",
    title: "5v2 Transfer Boxes",
    description: "Possession game where attackers keep the ball and score end to end while defenders look to win and drive over the line.",
    category: "brilliant-basics",
    topics: ["building"],
    duration: "3x3 mins",
    players: "8",
    areaSize: "20x10m",
    rules: [
      "Def look to win and drive over the line",
      "Atk look to keep possession and score end to end",
      "Bouncer one touch"
    ],
    coachingPoints: [
      "Inject to trigger movement to lose marker",
      "Receive level or beyond your marker",
      "Receive with an open body shape",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line"
    ],
    diagram: {
      type: "small-grid",
      zones: [
        { x: 20, y: 8, width: 60, height: 38, color: "rgba(255,255,255,0.05)" },
        { x: 20, y: 54, width: 60, height: 38, color: "rgba(255,255,255,0.05)" }
      ],
      attackers: [
        { x: 50, y: 10, label: "", color: "#3B82F6" },
        { x: 22, y: 73, label: "", color: "#3B82F6" },
        { x: 78, y: 73, label: "", color: "#3B82F6" },
        { x: 50, y: 88, label: "", color: "#3B82F6" },
        { x: 50, y: 50, label: "", color: "#3B82F6" }
      ],
      defenders: [
        { x: 45, y: 65, label: "" },
        { x: 55, y: 70, label: "" }
      ],
      neutrals: [],
      balls: [
        { x: 50, y: 82 }
      ],
      arrows: [],
      goals: [],
      cones: [],
      lines: [
        { startX: 20, startY: 46, endX: 80, endY: 46, style: "dashed", color: "rgba(255,255,255,0.8)" }
      ]
    }
  },
  {
    id: "through-around-over-gt",
    title: "Through, Around, Over GT",
    description: "Building game training where the attacking team plays into, around or over the defending team using double movements and timing of runs.",
    category: "game-training",
    topics: ["building"],
    duration: "2x8 mins",
    players: "14",
    areaSize: "50x60m",
    rules: [
      "The set up of the practice is split into two zones",
      "The attacking team begin 6v4 in the starting zone and are 2v2 in the central zone",
      "The objective for the attacking team is to play into, around or over the defending team",
      "Only one player from the attacking team can enter the central zone to support progress of the ball",
      "Block 1 - +1 attacker on broken line",
      "Block 2 - Freedom for all to cross zones after line is broken"
    ],
    coachingPoints: [
      "Double Movements to Lose Defender",
      "Identifying Space/Scan",
      "Deception/Disguise - Reverse Pass",
      "Receive level or beyond your marker",
      "Touch - Front foot / Back foot",
      "Speed of action",
      "Playing to your teammates advantage",
      "No two players on same line",
      "6 Drop and attract / Other high (diff lines)",
      "THROUGH, AROUND, OVER"
    ],
    diagram: {
      type: "full-pitch",
      zones: [
        { x: 15, y: 2, width: 70, height: 30, color: "rgba(255,255,255,0.05)" },
        { x: 15, y: 34, width: 70, height: 30, color: "rgba(255,255,255,0.08)" },
        { x: 15, y: 66, width: 70, height: 32, color: "rgba(255,255,255,0.05)" }
      ],
      attackers: [
        { x: 40, y: 40, label: "" },
        { x: 60, y: 40, label: "" },
        { x: 30, y: 90, label: "" },
        { x: 70, y: 90, label: "" },
        { x: 25, y: 72, label: "" },
        { x: 50, y: 75, label: "" },
        { x: 75, y: 72, label: "" }
      ],
      defenders: [
        { x: 42, y: 36, label: "" },
        { x: 58, y: 36, label: "" },
        { x: 45, y: 80, label: "" },
        { x: 55, y: 80, label: "" },
        { x: 35, y: 70, label: "" },
        { x: 65, y: 70, label: "" }
      ],
      neutrals: [
        { x: 50, y: 3, label: "GK" },
        { x: 50, y: 97, label: "GK" }
      ],
      cones: [],
      arrows: [],
      goals: [
        { x: 50, y: 0, width: 16, rotation: 180 },
        { x: 50, y: 100, width: 6, rotation: 0 }
      ],
      balls: [
        { x: 50, y: 95 }
      ],
      lines: [
        { startX: 15, startY: 33, endX: 85, endY: 33, style: "dashed", color: "rgba(255,255,255,0.5)" },
        { startX: 15, startY: 65, endX: 85, endY: 65, style: "dashed", color: "rgba(255,255,255,0.5)" }
      ]
    }
  },

  // Arrival Buzz Game - Brilliant Basics
  {
    id: "arrival-buzz-game",
    title: "Arrival Buzz Game",
    description: "Dribbling and tagging game where players score points by reaching free cones without being tagged, encouraging bravery and quick decision-making.",
    category: "brilliant-basics",
    topics: ["building"],
    duration: "4x1 mins",
    players: "4",
    areaSize: "5x5m",
    rules: [
      "All players are aiming to keep score in total over 4 rounds",
      "Points for dribbling to a free cone or new cone without getting tagged",
      "Points for tagging as a tagger",
      "Encourage bravery"
    ],
    coachingPoints: [
      "Timing of runs",
      "Communication - non verbal"
    ],
    diagram: {
      type: "small-grid",
      attackers: [
        { x: 50, y: 50, label: "" }
      ],
      defenders: [
        { x: 25, y: 22, label: "" },
        { x: 80, y: 18, label: "" },
        { x: 82, y: 80, label: "" }
      ],
      neutrals: [
      ],
      cones: [
        { x: 25, y: 25, color: "orange" },
        { x: 75, y: 25, color: "orange" },
        { x: 25, y: 75, color: "orange" },
        { x: 75, y: 75, color: "orange" }
      ],
      arrows: [],
      goals: [],
      balls: [
        { x: 30, y: 18 },
        { x: 85, y: 14 },
        { x: 87, y: 76 }
      ]
    }
  },
];